import { placeYahooAfterAmazon } from "../utils/platformPresentationOrder";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import "./Dashboard.css";
import "./SherlocMediaCampaigns.css";
import "./CreativeOptimus.css";
import "./CreativeTrafficking.css";
import "./CreativeFamily.css";
import "./CreativeStudio.css";
import "./CreativeDesktopWidth.css";

import CortexDashboardBrandLink from "../components/CortexDashboardBrandLink";
import {
  AdvertiserIdentityTrigger,
  GovernedAdvertiserSelectorModal,
} from "../components/GovernedAdvertiserSelector";
import { useAuth } from "../contexts/AuthContext";
import useGovernedAdvertiserCatalog from "../hooks/useGovernedAdvertiserCatalog";
import {
  buildApprovedPlanContext,
  platformLabelFromKey,
  resolveCreativeStudioAssetMeta,
} from "../utils/creativeStudioIntelligence";
import { extractCreativeAssetMetadata } from "../utils/creativeAssetMetadata";
import {
  shouldReuseExistingPreview,
  isImageAlreadyLoaded,
} from "../utils/creativePreviewState";
import {
  uploadCreativeBatch,
  validateFileAgainstPolicy,
  UPLOAD_PHASES,
} from "../utils/creativeUploadClient";
import {
  buildUploadSummary,
  buildFailureDetails,
} from "../utils/creativeUploadMessages";
import {
  UPLOAD_STAGES,
  describeDropZone,
  describeUploadAction,
  isActiveStage,
  isByteMeasurable,
  recordTransferSample,
  stageForIntakeStatus,
  stageForPhase,
  withIntakeStages,
  withStage,
} from "../utils/creativeUploadPresentation";
import CreativeUploadProgress from "../components/CreativeUploadProgress";
import {
  CREATIVE_UPLOAD_ACCEPT_ATTR,
  CREATIVE_UPLOAD_ACCEPT_HINT,
  partitionCreativeUploadFiles,
  describeRejectedCreativeUploads,
} from "../utils/creativeUpload";
import {
  buildCreativePlatformEligibilityDisplay,
  getCreativePlatformDisplayConfig,
  normalizeCreativePlatformEligibilityResponse,
} from "../utils/creativePlatformEligibilityUi";
import {
  COPY_BEARING_PLATFORMS,
  isCopyBearingPlatform,
  normalizeCopyBearingPlatform,
  resolveDefaultCopyPlatform,
  finalizeCopyPlatformOptions,
  AI_COPY_NO_COPY_PLATFORM_NOTICE,
} from "../utils/creativeCopyPlatforms";
import {
  getCopyPlatformSpec,
  validateCopyField,
  validateCopyEntry,
} from "../utils/creativeCopyPlatformSpecs";
import {
  APPROVED_COPY_DIFF_STATE_LABELS,
  APPROVED_COPY_STATE_LABELS,
  APPROVED_COPY_STATES,
  buildApprovedCopyPreviewRows,
  evaluateApprovedCopyReadiness,
  getApprovedCopyStructure,
} from "../utils/creativeApprovedCopy";
import metaLogo from "../assets/meta.jpg";
import facebookLogo from "../assets/facebook.jpg";
import instagramLogo from "../assets/instagram.jpg";
import tiktokLogo from "../assets/tiktok.jpg";
import xLogo from "../assets/x.jpg";
import youtubeLogo from "../assets/youtube.jpg";
import googleLogo from "../assets/google.jpg";
import redditLogo from "../assets/reddit.jpg";
import amazonLogo from "../assets/amazon.jpg";
import yahooLogo from "../assets/yahoo.png";
import viantLogo from "../assets/viant.png";
import linkedinLogo from "../assets/linkedin.png";
import rumbleLogo from "../assets/rumble-logo.jpg";
import truthSocialLogo from "../assets/truth-social-logo.jpg";
import displayHero from "../assets/display.jpg";
import ctvHero from "../assets/ctv.jpg";
import videoHero from "../assets/video.jpg";
import mobileHero from "../assets/mobile.jpg";
import audioHero from "../assets/audio.jpg";
import doohHero from "../assets/dooh.gif";
import ingameHero from "../assets/ingame.jpg";
import emailHero from "../assets/email.jpg";
import cinemaHero from "../assets/PurpleFilmIcon.jpg";
import { filterClientVisibleCreativeAssets } from "../utils/creativeAssetRole";
import {
  REVIEW_QUEUE_ACTIONS,
  buildReviewQueue,
  reviewQueuePosition,
  reviewQueueNeighborId,
  resolveAdvanceTarget,
  reviewActionLabel,
  resolveReviewKeyAction,
  shouldWarnBeforeLeavingAsset,
  resolveReviewPrefetch,
} from "../utils/creativeReviewQueue";
import {
  hasReviewStatValue,
  hideBackgroundFromAssistiveTech,
  markCreativeReviewOpen,
  resolveFocusWrapTarget,
  restoreFocus,
} from "../utils/creativeReviewSheet";
import {
  REVIEW_MEDIA_STATE,
  resolveReviewMedia,
  resolveReviewMediaState,
  reviewMediaMessage,
} from "../utils/creativeReviewMedia";

const normalizeBase = (base) => {
  if (!base) return "";
  let next = String(base).trim();
  next = next.replace(/\/+$/, "");
  if (next.toLowerCase().endsWith("/api")) next = next.slice(0, -4);
  return next;
};

const API_BASE_URL =
  normalizeBase(process.env.REACT_APP_BACKEND_URL) ||
  normalizeBase(process.env.REACT_APP_BACKEND) ||
  normalizeBase(process.env.REACT_APP_API_URL) ||
  "http://localhost:5000";
const normalizeAdvertiserScopeId = (value) =>
  value == null || !String(value).trim() ? "" : String(value).trim();
const CREATIVE_ASSET_BASE_PATH = "/api/creative-assets";
const CREATIVE_ELIGIBILITY_BATCH_SIZE = 50;
const CREATIVE_PREVIEW_OBJECT_URL_CACHE_LIMIT = 12;
const CREATIVE_PREVIEW_TRACE_PREFIX = "[CreativeStudioPreview]";
const IMAGE_ASPECT_RATIO_OPTIONS = ["1:1", "4:5", "9:16", "16:9"];
const IMAGE_VARIATION_OPTIONS = [1, 2, 4];
const IMAGE_STYLE_MODE_OPTIONS = [
  { value: "product", label: "Product" },
  { value: "lifestyle", label: "Lifestyle" },
  { value: "cinematic", label: "Cinematic" },
  { value: "performance_ad", label: "Performance Ad" },
];

const PLATFORM_HERO_ART = {
  facebook: { src: facebookLogo, alt: "Facebook" },
  instagram: { src: instagramLogo, alt: "Instagram" },
  tiktok: { src: tiktokLogo, alt: "TikTok" },
  x: { src: xLogo, alt: "X" },
  youtube: { src: youtubeLogo, alt: "YouTube" },
  google_search: { src: googleLogo, alt: "Google Search" },
  rumble: { src: rumbleLogo, alt: "Rumble" },
  truth_social: { src: truthSocialLogo, alt: "Truth Social" },
  reddit: { src: redditLogo, alt: "Reddit" },
  amazon: { src: amazonLogo, alt: "Amazon" },
  yahoo: { src: yahooLogo, alt: "Yahoo DSP" },
  yahoo_dsp: { src: yahooLogo, alt: "Yahoo DSP" },
  // viant.png is a full wordmark on a black canvas: fitted, never cropped.
  viant: { src: viantLogo, alt: "Viant", fit: "wordmark" },
  viant_dsp: { src: viantLogo, alt: "Viant", fit: "wordmark" },
  linkedin: { src: linkedinLogo, alt: "LinkedIn" },
  linkedin_ads: { src: linkedinLogo, alt: "LinkedIn" },
  ctv: { src: ctvHero, alt: "CTV" },
  video: { src: videoHero, alt: "Video" },
  display: { src: displayHero, alt: "Display" },
  mobile: { src: mobileHero, alt: "Mobile" },
  audio: { src: audioHero, alt: "Audio" },
  ingame: { src: ingameHero, alt: "In-Game" },
  dooh: { src: doohHero, alt: "DOOH" },
  cinema: { src: cinemaHero, alt: "Cinema" },
  email: { src: emailHero, alt: "Email" },
  meta: { src: metaLogo, alt: "Meta" },
  display_programmatic: { src: displayHero, alt: "Display" },
  programmatic_display: { src: displayHero, alt: "Display" },
};

const safeBase = (base) => String(base || "").replace(/\/+$/, "");

function creativeAssetPath(...segments) {
  const suffix = segments
    .filter((segment) => segment !== undefined && segment !== null && String(segment).trim() !== "")
    .map((segment) => String(segment).replace(/^\/+|\/+$/g, ""))
    .join("/");
  return suffix ? `${CREATIVE_ASSET_BASE_PATH}/${suffix}` : CREATIVE_ASSET_BASE_PATH;
}

function creativeAssetListPath(advertiserId) {
  return `${CREATIVE_ASSET_BASE_PATH}?advertiserId=${encodeURIComponent(advertiserId)}`;
}

function getStatusToneClass(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "is-neutral";
  if (
    ["approved", "approved draft", "prepared", "live", "active", "validated", "validation passed"].includes(
      normalized
    )
  ) {
    return "is-positive";
  }
  if (["exception", "rejected", "archived", "error", "failed"].includes(normalized)) {
    return "is-negative";
  }
  if (
    ["draft", "drafted", "draft review", "pending review", "packaged", "needs validation"].includes(
      normalized
    )
  ) {
    return "is-attention";
  }
  return "is-neutral";
}

function getStatusChipClass(value) {
  const tone = getStatusToneClass(value);
  return tone ? `creativetrafficking-status-chip ${tone}` : "creativetrafficking-status-chip";
}

function formatStudioStatus(label, fallback = "Draft") {
  const raw = String(label || "").trim();
  if (!raw) return fallback;
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const CREATIVE_STATUS_GROUPS = [
  { key: "DRAFT", label: "Draft" },
  { key: "IN_REVIEW", label: "In Review" },
  { key: "CHANGES_REQUESTED", label: "Changes Requested" },
  { key: "APPROVED", label: "Approved" },
  { key: "ARCHIVED", label: "Archived" },
];
const CREATIVE_STUDIO_PLATFORM_FILTER_EXCLUDED_KEYS = new Set(["cinema"]);
const CREATIVE_STUDIO_SCROLL_LOCK_DEPTH_KEY = "creativeStudioScrollLockDepth";
const CREATIVE_STUDIO_PREVIOUS_BODY_OVERFLOW_KEY = "creativeStudioPreviousBodyOverflow";
const CREATIVE_STUDIO_PREVIOUS_HTML_OVERFLOW_KEY = "creativeStudioPreviousHtmlOverflow";

function lockCreativeStudioDocumentScroll() {
  if (typeof document === "undefined") return () => {};

  const { body, documentElement } = document;
  if (!body || !documentElement) return () => {};

  const currentDepth = Number(body.dataset[CREATIVE_STUDIO_SCROLL_LOCK_DEPTH_KEY] || "0") || 0;

  if (currentDepth === 0) {
    body.dataset[CREATIVE_STUDIO_PREVIOUS_BODY_OVERFLOW_KEY] =
      body.style.overflow === "hidden" ? "" : body.style.overflow;
    documentElement.dataset[CREATIVE_STUDIO_PREVIOUS_HTML_OVERFLOW_KEY] =
      documentElement.style.overflow === "hidden" ? "" : documentElement.style.overflow;
  }

  const nextDepth = currentDepth + 1;
  body.dataset[CREATIVE_STUDIO_SCROLL_LOCK_DEPTH_KEY] = String(nextDepth);
  documentElement.dataset[CREATIVE_STUDIO_SCROLL_LOCK_DEPTH_KEY] = String(nextDepth);
  body.style.overflow = "hidden";
  documentElement.style.overflow = "hidden";

  return () => {
    const activeDepth =
      Number(body.dataset[CREATIVE_STUDIO_SCROLL_LOCK_DEPTH_KEY] || String(nextDepth)) || 1;
    const remainingDepth = Math.max(0, activeDepth - 1);

    if (remainingDepth > 0) {
      body.dataset[CREATIVE_STUDIO_SCROLL_LOCK_DEPTH_KEY] = String(remainingDepth);
      documentElement.dataset[CREATIVE_STUDIO_SCROLL_LOCK_DEPTH_KEY] = String(remainingDepth);
      return;
    }

    const previousBodyOverflow = body.dataset[CREATIVE_STUDIO_PREVIOUS_BODY_OVERFLOW_KEY] || "";
    const previousHtmlOverflow =
      documentElement.dataset[CREATIVE_STUDIO_PREVIOUS_HTML_OVERFLOW_KEY] || "";

    delete body.dataset[CREATIVE_STUDIO_SCROLL_LOCK_DEPTH_KEY];
    delete body.dataset[CREATIVE_STUDIO_PREVIOUS_BODY_OVERFLOW_KEY];
    delete documentElement.dataset[CREATIVE_STUDIO_SCROLL_LOCK_DEPTH_KEY];
    delete documentElement.dataset[CREATIVE_STUDIO_PREVIOUS_HTML_OVERFLOW_KEY];

    body.style.overflow = previousBodyOverflow;
    documentElement.style.overflow = previousHtmlOverflow;
  };
}

function normalizeLifecycleBucket(value) {
  const v = String(value || "").trim().toUpperCase();
  if (!v) return "DRAFT";
  if (v.includes("ARCHIVE")) return "ARCHIVED";
  if (v.includes("APPROVED")) return "APPROVED";
  if (v.includes("CHANGE")) return "CHANGES_REQUESTED";
  if (v.includes("REVIEW")) return "IN_REVIEW";
  return "DRAFT";
}

function isStudioApprovedAsset(asset) {
  if (!asset || asset?.archived_at) return false;
  if (normalizeLifecycleBucket(asset?.lifecycle_status || asset?.status) === "APPROVED") return true;
  return Boolean(
    asset?.approvalSummary?.fullyApproved ||
      (asset?.approvals?.clientApproved && asset?.approvals?.superadminApproved) ||
      asset?.approved_version_id ||
      asset?.approvedVersion?.id
  );
}

function getStudioEffectiveStatusLabel(asset, fallback = "Draft") {
  if (isStudioApprovedAsset(asset)) return "Approved";
  return formatStudioStatus(asset?.lifecycle_status || asset?.status, fallback);
}

function getStudioFilterBucket(asset, fallback = "Draft") {
  return normalizeLifecycleBucket(getStudioEffectiveStatusLabel(asset, fallback));
}

function isVideoByNameOrMime({ file_name, mime_type }) {
  const mt = String(mime_type || "").toLowerCase();
  const name = String(file_name || "").toLowerCase();
  if (mt.startsWith("video/")) return true;
  if (mt.startsWith("image/")) return false;
  return [".mp4", ".mov", ".webm", ".m4v", ".avi"].some((ext) => name.endsWith(ext));
}

function isDocumentByNameOrMime({ file_name, mime_type }) {
  const mt = String(mime_type || "").toLowerCase();
  const name = String(file_name || "").toLowerCase();
  if (mt.includes("pdf")) return true;
  return [".pdf"].some((ext) => name.endsWith(ext));
}

function shouldAwaitCreativePreviewReady({ file_name, mime_type }) {
  return !isDocumentByNameOrMime({ file_name, mime_type });
}

function getCreativePreviewNow() {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

function traceCreativePreview(event, details = {}) {
  if (typeof console === "undefined" || typeof console.debug !== "function") return;
  console.debug(CREATIVE_PREVIEW_TRACE_PREFIX, event, details);
}

function getCreativePreviewCacheKey({
  assetId,
  versionId,
  currentVersionId,
  mimeType,
  fileName,
}) {
  return [
    "creative-preview",
    assetId || "asset:none",
    versionId || currentVersionId || "version:none",
    String(mimeType || "").trim().toLowerCase() || "mime:none",
    String(fileName || "").trim().toLowerCase() || "file:none",
  ].join("|");
}

function isCreativePreviewObjectUrlCached(cache, url) {
  if (!url || !cache) return false;
  for (const entry of cache.values()) {
    if (entry?.url === url) return true;
  }
  return false;
}

function releaseCreativePreviewObjectUrl(cache, url) {
  if (!url || isCreativePreviewObjectUrlCached(cache, url)) return;
  if (String(url).startsWith("blob:")) URL.revokeObjectURL(url);
}

function revokeCreativePreviewCache(cache) {
  if (!cache) return;
  for (const entry of cache.values()) {
    if (entry?.url && String(entry.url).startsWith("blob:")) URL.revokeObjectURL(entry.url);
  }
  cache.clear();
}

function getCreativePreviewCachedEntry(cache, cacheKey) {
  if (!cacheKey || !cache?.has(cacheKey)) return null;
  const entry = cache.get(cacheKey);
  if (entry?.url) {
    entry.lastUsedAt = Date.now();
    cache.set(cacheKey, entry);
    return entry;
  }
  cache.delete(cacheKey);
  return null;
}

function setCreativePreviewCachedEntry(cache, cacheKey, entry, currentPreviewUrls = []) {
  if (!cache || !cacheKey || !entry?.url) return entry;
  const existing = cache.get(cacheKey);
  if (existing?.url && existing.url !== entry.url && String(existing.url).startsWith("blob:")) {
    URL.revokeObjectURL(existing.url);
  }
  cache.set(cacheKey, {
    ...entry,
    createdAt: entry.createdAt || Date.now(),
    lastUsedAt: Date.now(),
  });

  if (cache.size > CREATIVE_PREVIEW_OBJECT_URL_CACHE_LIMIT) {
    // Stage and Preview Window can retain different versions. Read their URLs
    // at this synchronous insertion and preserve the incoming URL as well.
    // At most three entries are protected, so the finite cache can still evict.
    const retainedUrls = new Set([...currentPreviewUrls, entry.url]);
    const oldest = Array.from(cache.entries())
      .filter(([, cached]) => !retainedUrls.has(cached?.url))
      .sort(([, left], [, right]) => (left?.lastUsedAt || 0) - (right?.lastUsedAt || 0))[0];
    if (oldest) {
      const [oldestKey, oldestEntry] = oldest;
      if (oldestEntry?.url && String(oldestEntry.url).startsWith("blob:")) {
        URL.revokeObjectURL(oldestEntry.url);
      }
      cache.delete(oldestKey);
    }
  }

  return entry;
}

function pickCreativePreviewPosterUrl(...sources) {
  const candidateFields = [
    "poster_url",
    "posterUrl",
    "thumbnail_url",
    "thumbnailUrl",
    "preview_thumbnail_url",
    "previewThumbnailUrl",
    "preview_image_url",
    "previewImageUrl",
    "cover_image_url",
    "coverImageUrl",
  ];
  const queue = sources.filter(Boolean);
  for (const source of queue) {
    for (const field of candidateFields) {
      const value = String(source?.[field] || "").trim();
      if (value) return value;
    }
    if (source?.currentVersion) queue.push(source.currentVersion);
    if (source?.approvedVersion) queue.push(source.approvedVersion);
  }
  return "";
}

function isCreativePreviewSwipeInteractiveTarget(target) {
  if (!target || typeof target.closest !== "function") return false;
  return Boolean(
    target.closest(
      "video, iframe, button, input, textarea, select, [role='button'], [data-preview-swipe-block='true']"
    )
  );
}

function CreativeStudioPreviewLoader({ overlay = false }) {
  return (
    <div
      className={
        overlay
          ? "creativestudio-preview-loading-overlay"
          : "creativestudio-preview-loading-state"
      }
    >
      <div className="creativetrafficking-preview-loading">
        <span className="creativetrafficking-preview-loading-orb" aria-hidden="true" />
        <span className="creativetrafficking-preview-loading-label">Loading Preview</span>
      </div>
    </div>
  );
}

function isAdvertiserBrandLogoAsset(asset) {
  if (!asset) return false;

  const fileName = String(
    asset?.file_name || asset?.currentVersion?.file_name || asset?.title || ""
  )
    .trim()
    .toLowerCase();
  const title = String(asset?.title || "").trim().toLowerCase();
  const mimeType = String(asset?.mime_type || asset?.currentVersion?.mime_type || "").toLowerCase();
  const channel = String(asset?.channel || "").trim();
  const format = String(asset?.format || "").trim();

  const looksLikeLogo =
    /\blogo\b/.test(fileName) ||
    /\blogo\b/.test(title);

  return Boolean(looksLikeLogo && mimeType.startsWith("image/") && !channel && !format);
}

function getPreferredReviewAssetId(rows = [], preferredId = null) {
  const reviewableRows = (Array.isArray(rows) ? rows : []).filter(
    (row) => !isAdvertiserBrandLogoAsset(row)
  );

  if (preferredId && reviewableRows.some((row) => String(row?.id) === String(preferredId))) {
    return preferredId;
  }

  return reviewableRows[0]?.id || null;
}

function withResolvedStudioMeta(asset, approvedPlatformKeys = [], eligibilityState = null) {
  if (!asset) return null;
  const studioMeta = resolveCreativeStudioAssetMeta(asset, { approvedPlatformKeys });
  const platformEligibilityDisplay = buildCreativePlatformEligibilityDisplay(
    eligibilityState,
    { approvedPlatformKeys }
  );
  return {
    ...asset,
    studioMeta,
    platformEligibilityDisplay,
    studioPlatformKey: studioMeta.platformKey,
    studioPlatform: studioMeta.platformLabel,
    studioPlatformKeys: studioMeta.compatiblePlatformKeys || [],
    studioPlatformLabels: studioMeta.compatiblePlatformLabels || [],
    studioPlatformLabelText: (studioMeta.compatiblePlatformLabels || []).join(" · "),
    studioFormat: studioMeta.formatLabel,
  };
}

function platformHeroImageClassName(visual) {
  return visual?.fit === "wordmark"
    ? "creativestudio-platform-hero-image creativestudio-platform-hero-image--wordmark"
    : "creativestudio-platform-hero-image";
}

function getPlatformHeroVisual(platformKey) {
  const platformDisplay = getCreativePlatformDisplayConfig({ platformKey });
  if (platformDisplay.logo) {
    return { src: platformDisplay.logo, alt: platformDisplay.label };
  }
  // Resolve the icon robustly across backend key casing/spelling so a checked
  // platform (e.g. CTV / "connected_tv" / "CTV") never loses its chip artwork.
  const normalized = String(platformKey || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  const aliased = normalized === "connected_tv" || normalized === "connectedtv" ? "ctv" : normalized;
  return (
    PLATFORM_HERO_ART[platformKey] ||
    PLATFORM_HERO_ART[normalized] ||
    PLATFORM_HERO_ART[aliased] ||
    null
  );
}

// Platform identity primitive: platform key -> canonical artwork + canonical
// label + chip internals. The read-only Platforms column and the interactive
// Approved Platform Copy selector both render THIS, so Creative Studio keeps a
// single platform artwork mapping (PLATFORM_HERO_ART) and a single chip
// geometry. Callers own the outer element, which is what lets the same badge be
// a <span> in a table cell and a <button> in a selector.
function CreativeStudioPlatformHeroBadge({ platformKey, label, compact = false }) {
  const resolvedLabel = label || platformLabelFromKey(platformKey) || platformKey;
  const visual = getPlatformHeroVisual(platformKey);
  const fallbackText = String(resolvedLabel).slice(0, 2).toUpperCase();
  return (
    <>
      <span className={`creativestudio-platform-hero-frame ${compact ? "is-compact" : ""}`} aria-hidden="true">
        <span className="creativestudio-platform-hero-media">
          {visual?.src ? (
            <img
              src={visual.src}
              alt=""
              className={platformHeroImageClassName(visual)}
              draggable="false"
            />
          ) : (
            <span className="creativestudio-platform-hero-fallback">{fallbackText}</span>
          )}
        </span>
      </span>
      <span className="creativestudio-platform-hero-label">{resolvedLabel}</span>
    </>
  );
}

function CreativeStudioPlatformHeroes({
  platformKeys = [],
  platformLabels = [],
  fallback = "—",
  compact = false,
}) {
  const labels = Array.isArray(platformLabels) ? platformLabels : [];
  const seen = new Set();
  const resolved = (Array.isArray(platformKeys) ? platformKeys : [])
    .map((key, index) => ({
      key: String(key || "").trim(),
      labelOverride: labels[index],
    }))
    .filter((entry) => entry.key)
    .filter((entry) => {
      if (seen.has(entry.key)) return false;
      seen.add(entry.key);
      return true;
    })
    .map(({ key, labelOverride }) => ({
      key,
      label: labelOverride || platformLabelFromKey(key) || key,
      visual: getPlatformHeroVisual(key),
    }))
    .filter((entry) => entry.label);

  if (!resolved.length) {
    return <span className="creativestudio-platform-fallback">{fallback}</span>;
  }

  return (
    <div className={`creativestudio-platform-hero-list ${compact ? "is-compact" : ""}`}>
      {resolved.map((platform) => (
        <span
          key={platform.key}
          className={`creativestudio-platform-hero-chip ${compact ? "is-compact" : ""}`}
          title={platform.label}
          aria-label={platform.label}
        >
          <CreativeStudioPlatformHeroBadge
            platformKey={platform.key}
            label={platform.label}
            compact={compact}
          />
        </span>
      ))}
    </div>
  );
}

// Compatibility state ONLY. Platform identity belongs to the Platform column
// and must never be substituted by a verdict, nor a verdict by an identity.
function CreativeStudioCompatibilityCell({ display }) {
  const state = display || buildCreativePlatformEligibilityDisplay(null);
  const label = state.compatibilityLabel || "—";
  const tone = state.compatibilityTone || "neutral";
  return (
    <span
      className={`creativestudio-compatibility-chip creativestudio-compatibility-chip--${tone}`}
      // The state is carried by the text itself; colour is never the only cue.
      title={state.compatibilityTitle || state.compatibilityDetail || label}
      aria-label={state.compatibilityTitle || label}
    >
      {label}
    </span>
  );
}

function CreativeStudioPlatformEligibilityCell({ display, compact = false }) {
  const state = display || buildCreativePlatformEligibilityDisplay(null);
  // Platform identity is driven by RECOGNITION, not compatibility: a creative
  // the Display registry recognizes keeps its Display badge even when it is
  // too heavy, ungoverned, or the wrong format class for the activation
  // surface. Compatibility is reported in its own column.
  // CAMPAIGN-SCOPED identity only. This must never read global recognition or
  // compatibility arrays: a platform the approved campaign did not select may
  // not appear as identity, as a fallback, or in a count.
  const canRenderPlatforms = state.campaignRecognizedPlatformKeys?.length > 0;
  const statusLabel = compact ? state.tableStatusLabel || state.statusLabel : state.statusLabel;
  const detailLabel = compact ? state.tableDetailLabel || state.detailLabel : state.detailLabel;

  return (
    <div className="creativestudio-platform-eligibility-cell">
      {canRenderPlatforms ? (
        // The platform chip itself is the eligibility indicator — no redundant
        // "Eligible: …" caption beneath it (aligns with the Creative Assets
        // table). Full eligibility detail/approval stays in the Review modal's
        // Approval State + Platform Compatibility Checks.
        <CreativeStudioPlatformHeroes
          platformKeys={state.campaignRecognizedPlatformKeys}
          platformLabels={state.campaignRecognizedPlatformLabels}
          fallback={statusLabel}
          compact={compact}
        />
      ) : (
        // Non-compatible states carry their full explanation (state, detected
        // dimensions, file type, governing reason) as an accessible title so a
        // compact row is understandable without opening dev tools. The state is
        // conveyed by the text itself, never by colour alone.
        <span
          className="creativestudio-platform-fallback"
          title={state.statusTitle || detailLabel || statusLabel}
          aria-label={state.statusTitle || statusLabel}
        >
          {state.platformIdentityLabel || statusLabel}
        </span>
      )}
      {/* Detail caption only in the expanded (non-compact) modal view, and only
          when there are no chips to stand in as the indicator. Row/table cells
          (compact) never render it — chips alone, or a short status fallback. */}
      {!compact && !canRenderPlatforms && detailLabel ? (
        <div className="creativestudio-platform-eligibility-lines">
          <span>{detailLabel}</span>
        </div>
      ) : null}
    </div>
  );
}

function CreativePlatformEligibilityDetail({ display }) {
  const state = display || buildCreativePlatformEligibilityDisplay(null);
  // The full list is a deliberate, opt-in diagnostic view. By default the sheet
  // shows only platforms the asset can actually reach activation on, because
  // enumerating every registry platform meant a static banner was reviewed
  // beside CTV and TikTok cards whose only content was a refusal.
  const [showAllPlatforms, setShowAllPlatforms] = useState(false);
  const hiddenPlatformCount = state.hiddenPlatformCount || 0;
  const platformRows = showAllPlatforms
    ? state.platformRows
    : state.positivePathPlatformRows;

  return (
    <div className="creativestudio-review-card">
      <div className="creativetrafficking-detail-section-title">Platform Compatibility Checks</div>
      <div className="creativestudio-platform-eligibility-detail-summary">
        <CreativeStudioPlatformEligibilityCell display={state} />
      </div>
      {/* Derived from the same rows rendered below, so the summary cannot
          contradict them. This was a fixed paragraph asserting eligibility. */}
      {state.platformSummarySentence ? (
        <p className="creativestudio-eligibility-help">{state.platformSummarySentence}</p>
      ) : null}

      {state.metadataRows?.length ? (
        <dl className="creativestudio-review-stats creativestudio-eligibility-metadata">
          {state.metadataRows.map((row) => (
            <div key={row.label}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {platformRows?.length ? (
        <div className="creativestudio-eligibility-platform-list">
          {platformRows.map((row) => (
            <div
              className={`creativestudio-eligibility-platform-row creativestudio-eligibility-platform-row--${row.summaryTone || "neutral"}`}
              key={row.platformKey || row.platformLabel}
            >
              <div className="creativestudio-eligibility-platform-header">
                {(() => {
                  const visual = getPlatformHeroVisual(row.platformKey);
                  const initials = String(row.platformLabel || "").slice(0, 2).toUpperCase();
                  return (
                    <span className="creativestudio-platform-hero-frame is-compact" aria-hidden="true">
                      <span className="creativestudio-platform-hero-media">
                        {visual?.src ? (
                          <img
                            src={visual.src}
                            alt=""
                            className={platformHeroImageClassName(visual)}
                            draggable="false"
                          />
                        ) : (
                          <span className="creativestudio-platform-hero-fallback">{initials}</span>
                        )}
                      </span>
                    </span>
                  );
                })()}
                <div className="creativestudio-eligibility-platform-heading">{row.platformLabel}</div>
              </div>
              <div
                className={`creativestudio-eligibility-platform-summary creativestudio-eligibility-platform-summary--${row.summaryTone || "neutral"}`}
              >
                {row.summaryLabel}
              </div>
              <div className="creativestudio-eligibility-platform-statuses">
                <span>Compatibility: {row.compatibilityLabel}</span>
                <span>Eligibility: {row.eligibilityLabel}</span>
                <span>Approval: {row.approvalLabel}</span>
              </div>
              {/* Only fields that are genuinely a next step. An asset that
                  failed on type is not one save away from working. */}
              {row.actionableMissingFields?.length ? (
                <div className="creativestudio-eligibility-platform-note">
                  Missing: {row.actionableMissingFields.join(", ")}
                </div>
              ) : null}
              {/* ONE resolved sentence. This rendered `reasons.slice(0, 2)`,
                  and those entries carried machine codes, which is how
                  "VIDEO:UNSUPPORTED_FILE_TYPE" reached reviewers. */}
              {row.explanation ? (
                <div className="creativestudio-eligibility-platform-note">
                  {row.explanation}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {/* The omission is stated, never silent. */}
      {hiddenPlatformCount > 0 ? (
        <button
          type="button"
          className="creativestudio-eligibility-diagnostic-toggle"
          onClick={() => setShowAllPlatforms((previous) => !previous)}
          aria-expanded={showAllPlatforms}
        >
          {showAllPlatforms
            ? "Hide platforms with no path for this asset"
            : `Show ${hiddenPlatformCount} platform${hiddenPlatformCount === 1 ? "" : "s"} with no path for this asset`}
        </button>
      ) : null}

      {state.warnings?.length ? (
        <div className="creativestudio-eligibility-message">
          {state.warnings.join(" ")}
        </div>
      ) : null}
      {state.errors?.length ? (
        <div className="creativestudio-eligibility-message is-error">
          {state.errors.map((entry) => entry?.message || entry).join(" ")}
        </div>
      ) : null}
    </div>
  );
}

function getAuthToken() {
  return (
    localStorage.getItem("clientAuthToken") ||
    localStorage.getItem("superadminToken") ||
    localStorage.getItem("authToken") ||
    ""
  );
}

async function apiJson(path, { method = "GET", body } = {}) {
  const base = safeBase(API_BASE_URL);
  const token = getAuthToken();
  const res = await fetch(`${base}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!res.ok) {
    throw new Error(
      json?.error || json?.message || `HTTP ${res.status} ${res.statusText} on ${path}`
    );
  }

  return json;
}

// Multipart helper for the governed approved-copy workbook import. Same auth
// posture as apiJson; the browser sets the multipart boundary itself.
async function apiWorkbookRequest(path, formData) {
  const base = safeBase(API_BASE_URL);
  const token = getAuthToken();
  const res = await fetch(`${base}${path}`, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: formData,
  });

  const text = await res.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!res.ok) {
    const error = new Error(
      json?.error || json?.message || `HTTP ${res.status} ${res.statusText} on ${path}`
    );
    error.code = json?.code || null;
    error.details = json?.details;
    throw error;
  }

  return json;
}

async function fetchCreativeAssetPlatformEligibilityBatch(assetIds, {
  advertiserId,
  approvedPlatformKeys = [],
} = {}) {
  const cleanAssetIds = Array.from(
    new Set((Array.isArray(assetIds) ? assetIds : []).map((id) => Number(id)).filter(Boolean))
  );
  if (!cleanAssetIds.length) return { results: {}, errors: {} };

  const payload = await apiJson(creativeAssetPath("platform-eligibility", "batch"), {
    method: "POST",
    body: {
      advertiserId,
      assetIds: cleanAssetIds,
      context: {
        approvedPlatformKeys: (Array.isArray(approvedPlatformKeys) ? approvedPlatformKeys : [])
          .map((key) => String(key || "").trim())
          .filter(Boolean),
      },
    },
  });
  const data = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const rawResults = data?.results && typeof data.results === "object" ? data.results : {};
  const rawErrors = data?.errors && typeof data.errors === "object" ? data.errors : {};
  const results = {};
  Object.entries(rawResults).forEach(([assetId, value]) => {
    const normalized = normalizeCreativePlatformEligibilityResponse(value);
    if (normalized) results[String(assetId)] = normalized;
  });
  return { results, errors: rawErrors };
}

async function apiBlob(path) {
  const base = safeBase(API_BASE_URL);
  const token = getAuthToken();
  const res = await fetch(`${base}${path}`, {
    method: "GET",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let json = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = null;
    }
    throw new Error(
      json?.error || json?.message || text || `HTTP ${res.status} ${res.statusText} on ${path}`
    );
  }

  return res.blob();
}

async function loadCreativePreviewBlob({
  assetId,
  versionId,
  currentVersionId = null,
  mimeType = "",
}) {
  const normalizePreviewBlob = (rawBlob) => {
    const normalizedMimeType = String(mimeType || "").trim().toLowerCase();
    const resolvedBlobType = String(rawBlob?.type || "").trim().toLowerCase();
    if (
      rawBlob &&
      normalizedMimeType &&
      (!resolvedBlobType ||
        resolvedBlobType === "application/octet-stream" ||
        resolvedBlobType !== normalizedMimeType)
    ) {
      return new Blob([rawBlob], { type: normalizedMimeType });
    }
    return rawBlob;
  };

  if (!versionId && !assetId) return null;

  if (versionId) {
    try {
      const blob = await apiBlob(creativeAssetPath("versions", versionId, "stream"));
      return normalizePreviewBlob(blob);
    } catch (versionErr) {
      const canFallbackToAssetStream =
        assetId && (!currentVersionId || String(versionId) === String(currentVersionId));
      if (!canFallbackToAssetStream) throw versionErr;
      const blob = await apiBlob(creativeAssetPath(assetId, "stream"));
      return normalizePreviewBlob(blob);
    }
  }

  if (!assetId) return null;
  const blob = await apiBlob(creativeAssetPath(assetId, "stream"));
  return normalizePreviewBlob(blob);
}

function ApprovalChip({ label, state }) {
  const approved = String(state || "").toUpperCase() === "APPROVED";
  return (
    <span className="cs-approval-group" aria-label={`${label} ${state || "Pending"}`}>
      <span className="cs-approval-label">{label}</span>
      <span className={`cs-approval-check ${approved ? "is-checked" : ""}`} />
    </span>
  );
}

function createDefaultCopyForm(asset = null) {
  return {
    platform: resolveDefaultCopyPlatform(asset?.studioPlatform, asset?.channel),
    format: String(asset?.studioFormat || asset?.format || "").trim().replace(/^—$/, ""),
    campaignId: "",
    objective: "",
    targetAudience: "",
    tone: "",
    cta: "",
    messagingNotes: "",
    variantCount: 3,
  };
}

const COPY_BEARING_PLATFORM_LABELS = COPY_BEARING_PLATFORMS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const AI_COPY_NO_CAMPAIGN_COPY_PLATFORMS =
  "No copy-bearing campaign platforms found. Display, CTV, and DOOH use embedded creative messaging or trafficking context.";

const AI_COPY_DISPLAY_ONLY_NOTICE =
  "This selected campaign does not include copy-bearing platforms. Display banners use embedded creative text.";

const MANUAL_COPY_PERSISTENCE_NOTE =
  "Local character-limit check only. Saving manual copy is not enabled yet.";

// Phase 2A1 governed draft authoring. The backend capability block on
// GET /:id/versions is the ONLY authority for whether authoring is enabled —
// the frontend never infers it. Saving persists canonical copy fields to a
// governed CreativeVersion draft and nothing else.
const GOVERNED_SAVE_TRUTH_NOTE =
  "Saving this draft does not approve, traffic or launch the creative.";
const GOVERNED_FROZEN_NOTE =
  "The latest governed version is immutable; saving creates a new draft version.";

// AI copy authoring (generate/edit/regenerate) and comment replies have no
// backend routes yet — those arrive with the copy-package phase of the
// creative roadmap. The former network calls 404'd; controls now carry
// honest disabled copy instead.
const COPY_AUTHORING_DISABLED_NOTE = "AI copy authoring is not enabled yet.";
const COMMENT_REPLIES_DISABLED_NOTE = "Comment replies are not enabled yet.";
const EXPORTS_DISABLED_NOTE = "Governed creative-package export is not yet enabled.";

const CAMPAIGN_COPY_PLATFORM_FIELDS = [
  "target_platforms",
  "targetPlatforms",
  "platforms",
  "channels",
  "channel_mix",
  "channelMix",
  "media_mix",
  "mediaMix",
  "recommended_channels",
  "recommendedChannels",
];

function uniqueCopyKeys(values = []) {
  return Array.from(new Set((Array.isArray(values) ? values : []).filter(Boolean)));
}

function extractCopyPlatformTokens(value) {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => extractCopyPlatformTokens(entry));
  }

  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  if (value && typeof value === "object") {
    const direct = [
      value.key,
      value.value,
      value.label,
      value.name,
      value.platform,
      value.platformKey,
      value.platform_key,
      value.channel,
      value.type,
    ].filter(Boolean);
    const nested = [
      value.platforms,
      value.targetPlatforms,
      value.target_platforms,
      value.selected,
      value.channels,
    ].filter(Boolean);
    return [...direct, ...nested.flatMap((entry) => extractCopyPlatformTokens(entry))];
  }

  return [];
}

function extractBudgetPlatformTokens(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.flatMap((entry) => extractBudgetPlatformTokens(entry));
  if (typeof value !== "object") return [];
  return Object.keys(value).filter(Boolean);
}

function extractCampaignCopyPlatformTokens(campaign) {
  if (!campaign || typeof campaign !== "object") return [];
  return uniqueCopyKeys([
    ...CAMPAIGN_COPY_PLATFORM_FIELDS.flatMap((field) => extractCopyPlatformTokens(campaign[field])),
    ...CAMPAIGN_COPY_PLATFORM_FIELDS.flatMap((field) => extractCopyPlatformTokens(campaign.strategy_payload?.[field])),
    ...CAMPAIGN_COPY_PLATFORM_FIELDS.flatMap((field) => extractCopyPlatformTokens(campaign.strategyPayload?.[field])),
    ...extractBudgetPlatformTokens(campaign.budget_breakdown),
    ...extractBudgetPlatformTokens(campaign.budgetBreakdown),
    ...extractBudgetPlatformTokens(campaign.strategy_payload?.budget_breakdown),
    ...extractBudgetPlatformTokens(campaign.strategy_payload?.budgetBreakdown),
    ...extractBudgetPlatformTokens(campaign.strategyPayload?.budget_breakdown),
    ...extractBudgetPlatformTokens(campaign.strategyPayload?.budgetBreakdown),
  ]);
}

function normalizeCampaignCopyPlatformToken(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[^a-z0-9 ]+/g, "")
    .replace(/\s+/g, " ");
  if (!normalized) return "";

  if (
    normalized === "meta" ||
    normalized === "meta social" ||
    normalized === "meta socials" ||
    normalized === "meta platforms" ||
    normalized === "facebook instagram" ||
    normalized === "instagram facebook" ||
    normalized === "facebook and instagram" ||
    normalized === "instagram and facebook"
  ) {
    return "meta";
  }

  return normalizeCopyBearingPlatform(value);
}

function buildCopyPlatformOption(key, source) {
  const spec = getCopyPlatformSpec(key);
  return {
    value: spec?.key || key,
    label: spec?.label || COPY_BEARING_PLATFORM_LABELS[key] || platformLabelFromKey(key) || key,
    source,
  };
}

function resolveCopyPlatformSelection({ approvedPlanContext, activeAsset, selectedAssetPlatform }) {
  const campaignTokens = extractCampaignCopyPlatformTokens(approvedPlanContext?.approvedCampaign);
  const campaignKeys = uniqueCopyKeys(
    campaignTokens.map((token) => normalizeCampaignCopyPlatformToken(token)).filter(Boolean)
  );
  const approvedCampaignFallbackKeys = uniqueCopyKeys(
    (approvedPlanContext?.approvedPlatformKeys || [])
      .map((key) => normalizeCopyBearingPlatform(key))
      .filter(Boolean)
  );
  const effectiveCampaignKeys = campaignKeys.length ? campaignKeys : approvedCampaignFallbackKeys;
  const hasCampaignPlatformSource = campaignTokens.length > 0 || approvedCampaignFallbackKeys.length > 0;

  if (effectiveCampaignKeys.length) {
    return {
      source: "campaign",
      sourceLabel: "Campaign-selected platforms",
      options: effectiveCampaignKeys.map((key) => buildCopyPlatformOption(key, "campaign")),
      nonCopyCampaignPlatforms: [],
      hasCampaignPlatformSource,
    };
  }

  const nonCopyCampaignPlatforms = uniqueCopyKeys(
    campaignTokens
      .map((token) => getCopyPlatformSpec(token)?.key || "")
      .filter((key) => key && !isCopyBearingPlatform(key))
  );

  if (hasCampaignPlatformSource) {
    return {
      source: "campaign",
      sourceLabel: "Campaign-selected platforms",
      options: [],
      nonCopyCampaignPlatforms,
      hasCampaignPlatformSource,
    };
  }

  const assetKeys = uniqueCopyKeys([
    ...(activeAsset?.studioPlatformKeys || []),
    selectedAssetPlatform,
    activeAsset?.channel,
    activeAsset?.platform,
    activeAsset?.platformKey,
    activeAsset?.platform_key,
  ].map((key) => normalizeCopyBearingPlatform(key)).filter(Boolean));

  return {
    source: assetKeys.length ? "asset" : "none",
    sourceLabel: assetKeys.length ? "Asset-derived fallback" : "No platform source",
    options: assetKeys.map((key) => buildCopyPlatformOption(key, "asset")),
    nonCopyCampaignPlatforms: [],
    hasCampaignPlatformSource: false,
  };
}

function createManualCopyDraftForSpec(spec, previous = {}) {
  if (!spec) return {};
  return spec.fields.reduce((acc, specField) => {
    acc[specField.key] = String(previous?.[specField.key] || "");
    return acc;
  }, {});
}

function formatCopyLimitText(check) {
  if (check.maxChars != null) return `${check.count}/${check.maxChars}`;
  if (check.recommendedChars != null) return `${check.count} (rec ≤ ${check.recommendedChars})`;
  return `${check.count} chars`;
}

function getManualReadinessLabel(validation, spec) {
  if (!spec) return "Choose platform";
  if (validation?.deployable) return "Passes local checks";
  if (validation?.hasErrors) return "Needs edit";
  if (validation?.isGuidance) return "Creative guidance";
  return "Ready for review";
}

function createDefaultImageGenerationForm() {
  return {
    title: "",
    prompt: "",
    aspectRatio: "1:1",
    styleMode: "product",
    variations: 1,
    channel: "",
    format: "",
    notes: "",
  };
}

function deriveTitleFromFilename(filename) {
  const raw = String(filename || "").trim();
  if (!raw) return "";

  const withoutExtension = raw.replace(/\.[^.]+$/, "").trim();
  if (!withoutExtension) return "";

  return withoutExtension
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function sanitizePreviewMeta(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  const normalized = text.toLowerCase();
  if (
    normalized === "not found" ||
    normalized === "undefined" ||
    normalized === "null" ||
    normalized === "n/a"
  ) {
    return "";
  }
  return text;
}

function getDisplayVersionLabel(version, fallback = "v1") {
  return (
    sanitizePreviewMeta(version?.version_label) ||
    sanitizePreviewMeta(version?.version) ||
    fallback
  );
}

function getDisplayFileName(version, fallback = "No file") {
  return sanitizePreviewMeta(version?.file_name) || fallback;
}

function createFallbackVersionFromAsset(asset) {
  if (!asset) return null;
  return {
    id: asset?.currentVersion?.id || asset?.current_version_id || null,
    version_label: getDisplayVersionLabel(asset?.currentVersion || asset, "v1"),
    version_number: asset?.currentVersion?.version_number || 1,
    file_name: sanitizePreviewMeta(asset?.currentVersion?.file_name || asset?.file_name || ""),
    mime_type: asset?.currentVersion?.mime_type || asset?.mime_type || "",
  };
}

function CommentItem({ comment }) {
  return (
    <div className="creativestudio-comment">
      <div className="creativestudio-comment-meta">
        <strong>{String(comment.author_role || "unknown").toUpperCase()}</strong>
        <span>{new Date(comment.created_at || comment.createdAt || Date.now()).toLocaleString()}</span>
        <span>{comment.status}</span>
      </div>
      <div className="creativestudio-comment-text">{comment.body}</div>
      <div className="creativestudio-preview-actions">
        <span className="creativeoptimus-metric-hint" title={COMMENT_REPLIES_DISABLED_NOTE}>
          {COMMENT_REPLIES_DISABLED_NOTE}
        </span>
      </div>
      {(comment.replies || []).length ? (
        <div className="creativestudio-thread-list">
          {comment.replies.map((reply) => (
            <CommentItem key={reply.id} comment={reply} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

const CreativeStudio = () => {
  const { role } = useAuth();

  const [items, setItems] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [approvedPlanCampaigns, setApprovedPlanCampaigns] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [selectedVersionId, setSelectedVersionId] = useState(null);

  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMediaLoading, setPreviewMediaLoading] = useState(false);
  // Cache key of the preview currently attached to the <img>. Lets the effect
  // tell "the asset changed" apart from "the detail object was refreshed".
  const previewCacheKeyRef = useRef("");
  const [previewError, setPreviewError] = useState("");
  const previewObjectUrlRef = useRef("");
  const previewObjectUrlCacheRef = useRef(new Map());
  const fileInputRef = useRef(null);

  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [channel, setChannel] = useState("");
  const [format, setFormat] = useState("");
  const [changeSummary, setChangeSummary] = useState("");
  const [fileObj, setFileObj] = useState(null);
  const [fileName, setFileName] = useState("");
  const [fileObjs, setFileObjs] = useState([]);
  const [uploadState, setUploadState] = useState({ active: false, done: 0, total: 0 });
  // Per-file progress and per-file failure detail. A six-file selection shows
  // six independent rows instead of one shared counter.
  const [uploadFileStates, setUploadFileStates] = useState([]);
  // The backend owns the upload limits; this portal never keeps a second copy.
  const [uploadPolicy, setUploadPolicy] = useState(null);
  const uploadBatchIdRef = useRef(null);
  // Inspection runs on the server after the transfer ends. Holding the
  // progress surface open through it — and clearing it once it settles — is
  // what stops a finished upload from either looking frozen at 100% or
  // vanishing before the user sees it land.
  const uploadInspectionTimerRef = useRef(null);
  const [uploadResults, setUploadResults] = useState([]);
  const [intakeEvidence, setIntakeEvidence] = useState(null);
  const [isUploaderDragActive, setIsUploaderDragActive] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [platformEligibilityByAssetId, setPlatformEligibilityByAssetId] = useState({});
  const [copyForm, setCopyForm] = useState(createDefaultCopyForm());
  const [copyMode, setCopyMode] = useState("ai");
  const [manualCopyDraft, setManualCopyDraft] = useState({});
  const [manualCopyValidated, setManualCopyValidated] = useState(false);
  const [governedVersions, setGovernedVersions] = useState([]);
  const [governedCapabilities, setGovernedCapabilities] = useState(null);
  const [isSavingGovernedDraft, setIsSavingGovernedDraft] = useState(false);

  // Governed APPROVED platform copy (client-approved workbook imports).
  // Campaign-scoped, separate from the asset-scoped working copy above.
  const [approvedCopyPlatforms, setApprovedCopyPlatforms] = useState(null);
  const [approvedCopyLoading, setApprovedCopyLoading] = useState(false);
  const [approvedCopyError, setApprovedCopyError] = useState("");
  const [approvedCopyImportPreview, setApprovedCopyImportPreview] = useState(null);
  const [approvedCopyImportFile, setApprovedCopyImportFile] = useState(null);
  const [approvedCopyOperatorEdits, setApprovedCopyOperatorEdits] = useState([]);
  const [approvedCopySelectedPlatforms, setApprovedCopySelectedPlatforms] = useState([]);
  const [isPreviewingApprovedCopy, setIsPreviewingApprovedCopy] = useState(false);
  const [isCommittingApprovedCopy, setIsCommittingApprovedCopy] = useState(false);
  const [approvedCopyImportError, setApprovedCopyImportError] = useState("");
  const [approvedCopyNotice, setApprovedCopyNotice] = useState("");
  const [approvedCopyHistoryOpen, setApprovedCopyHistoryOpen] = useState(false);
  const [approvedCopyViewPlatform, setApprovedCopyViewPlatform] = useState("");
  const approvedCopyFileInputRef = useRef(null);
  const [imageForm, setImageForm] = useState(createDefaultImageGenerationForm());
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [formatFilter, setFormatFilter] = useState("all");
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  // Dialog plumbing for the review sheet. The return target is captured at open
  // time rather than derived on close, because the row that opened the sheet can
  // be filtered out of the table while the sheet is still up.
  const reviewSheetRef = useRef(null);
  const reviewCloseButtonRef = useRef(null);
  const reviewReturnFocusRef = useRef(null);
  const [previewModalAssetId, setPreviewModalAssetId] = useState("");
  const [previewModalUrl, setPreviewModalUrl] = useState("");
  const [previewModalLoading, setPreviewModalLoading] = useState(false);
  const [previewModalMediaLoading, setPreviewModalMediaLoading] = useState(false);
  const [previewModalError, setPreviewModalError] = useState("");
  const previewModalObjectUrlRef = useRef("");
  const previewModalSwipeRef = useRef(null);
  const platformEligibilityRequestedRef = useRef(new Set());
  const advertiserScopeGenerationRef = useRef(0);
  const activeAdvertiserIdRef = useRef("");

  const isAdvertiserScopeCurrent = useCallback(
    (scopeGeneration, advertiserId) =>
      scopeGeneration === advertiserScopeGenerationRef.current &&
      normalizeAdvertiserScopeId(activeAdvertiserIdRef.current) ===
        normalizeAdvertiserScopeId(advertiserId),
    []
  );

  const clearAdvertiserScopedState = useCallback((nextAdvertiser, { reason } = {}) => {
    advertiserScopeGenerationRef.current += 1;
    activeAdvertiserIdRef.current = normalizeAdvertiserScopeId(
      nextAdvertiser?.id
    );

    setItems([]);
    setApprovedPlanCampaigns([]);
    setActiveId(null);
    setDetail(null);
    setListLoading(false);
    setListError("");
    setDetailLoading(false);
    setDetailError("");
    setSelectedVersionId(null);
    setPreviewUrl("");
    previewCacheKeyRef.current = "";
    setPreviewLoading(false);
    setPreviewMediaLoading(false);
    setPreviewError("");
    setPlatformEligibilityByAssetId({});
    platformEligibilityRequestedRef.current.clear();
    setCopyForm(createDefaultCopyForm());
    setManualCopyDraft({});
    setManualCopyValidated(false);
    setGovernedVersions([]);
    setGovernedCapabilities(null);
    setApprovedCopyPlatforms(null);
    setApprovedCopyLoading(false);
    setApprovedCopyError("");
    setApprovedCopyImportPreview(null);
    setApprovedCopyImportFile(null);
    setApprovedCopyOperatorEdits([]);
    setApprovedCopySelectedPlatforms([]);
    setApprovedCopyNotice("");
    setApprovedCopyImportError("");
    setApprovedCopyViewPlatform("");
    setIsReviewModalOpen(false);
    setPreviewModalAssetId("");
    setPreviewModalUrl("");
    setPreviewModalLoading(false);
    setPreviewModalMediaLoading(false);
    setPreviewModalError("");
    setNewComment("");

    setTitle("");
    setNotes("");
    setChannel("");
    setFormat("");
    setChangeSummary("");
    setFileObj(null);
    setFileName("");
    setFileObjs([]);
    setUploadState({ active: false, done: 0, total: 0 });
    setUploadResults([]);
    setIntakeEvidence(null);
    setIsUploaderDragActive(false);
    setImageForm(createDefaultImageGenerationForm());
    setIsGeneratingImages(false);
    setIsSavingGovernedDraft(false);
    if (fileInputRef.current) fileInputRef.current.value = "";

    revokeCreativePreviewCache(previewObjectUrlCacheRef.current);
    previewObjectUrlRef.current = "";
    previewModalObjectUrlRef.current = "";
    previewModalSwipeRef.current = null;

    setActionError("");
    setActionMessage(
      reason === "changed"
        ? "Advertiser changed. Unsaved uploads, drafts, results, and previews were cleared before loading the new scope."
        : "Advertiser access changed. Advertiser-scoped Creative Studio state was cleared."
    );
  }, []);

  const {
    selectedAdvertiserId,
    selectedAdvertiserName,
    visibleAdvertisers: advertisers,
    currentAdvertiser: resolvedAdvertiser,
    loading: advertisersLoading,
    error: advLoadError,
    selectorDisabled,
    selectorEmpty,
    modalOpen: advertiserSelectorOpen,
    openSelector: openAdvertiserSelector,
    closeSelector: closeAdvertiserSelector,
    handleAdvertiserSelect,
  } = useGovernedAdvertiserCatalog({
    apiBase: API_BASE_URL,
    role: "client",
    onAdvertiserChange: clearAdvertiserScopedState,
  });
  const effectiveSelectedAdvertiserId = selectedAdvertiserId || null;
  const normalizedEffectiveAdvertiserId = normalizeAdvertiserScopeId(
    effectiveSelectedAdvertiserId
  );
  if (
    normalizeAdvertiserScopeId(activeAdvertiserIdRef.current) !==
    normalizedEffectiveAdvertiserId
  ) {
    advertiserScopeGenerationRef.current += 1;
    activeAdvertiserIdRef.current = normalizedEffectiveAdvertiserId;
  }
  const renderedAdvertiserScopeGeneration =
    advertiserScopeGenerationRef.current;
  const renderedAdvertiserScopeIsCurrent = () =>
    isAdvertiserScopeCurrent(
      renderedAdvertiserScopeGeneration,
      normalizedEffectiveAdvertiserId
    );
  const advertiserSelectionBlocked =
    uploadState.active || isGeneratingImages || isSavingGovernedDraft;
  const handleGovernedAdvertiserSelect = useCallback(
    (advertiser) => {
      if (advertiserSelectionBlocked) {
        closeAdvertiserSelector();
        setActionError(
          "Finish the active upload, image generation, or governed draft save before changing advertisers."
        );
        return;
      }
      handleAdvertiserSelect(advertiser);
    },
    [
      advertiserSelectionBlocked,
      closeAdvertiserSelector,
      handleAdvertiserSelect,
    ]
  );

  const approvedPlanContext = useMemo(
    () => buildApprovedPlanContext(approvedPlanCampaigns),
    [approvedPlanCampaigns]
  );
  const approvedPlatformKeys = approvedPlanContext.approvedPlatformKeys;
  const platformEligibilityContextKey = useMemo(
    () => approvedPlatformKeys.map((key) => String(key || "").trim()).filter(Boolean).sort().join(","),
    [approvedPlatformKeys]
  );
  const enrichedItems = useMemo(
    () => items.map((item) => withResolvedStudioMeta(
      item,
      approvedPlatformKeys,
      platformEligibilityByAssetId[String(item.id)]
    )),
    [items, approvedPlatformKeys, platformEligibilityByAssetId]
  );
  const activeListItem = useMemo(
    () => enrichedItems.find((item) => String(item.id) === String(activeId)) || null,
    [enrichedItems, activeId]
  );
  const reviewableItems = useMemo(
    () => enrichedItems.filter((item) => !isAdvertiserBrandLogoAsset(item)),
    [enrichedItems]
  );
  const detailAsset = useMemo(() => {
    if (detail?.asset) return withResolvedStudioMeta(
      detail.asset,
      approvedPlatformKeys,
      platformEligibilityByAssetId[String(detail.asset.id)]
    );
    if (detail && (detail.id || detail.title || detail.drive_file_id)) {
      return withResolvedStudioMeta(
        detail,
        approvedPlatformKeys,
        platformEligibilityByAssetId[String(detail.id)]
      );
    }
    return null;
  }, [detail, approvedPlatformKeys, platformEligibilityByAssetId]);
  const activeAsset = detailAsset || activeListItem || null;
  const versions = useMemo(() => {
    const detailVersions = Array.isArray(detail?.versions) ? detail.versions : [];
    if (detailVersions.length) return detailVersions;
    const fallbackVersion = createFallbackVersionFromAsset(activeAsset);
    return fallbackVersion ? [fallbackVersion] : [];
  }, [detail?.versions, activeAsset]);
  const comments = useMemo(() => {
    if (Array.isArray(detail?.comments)) return detail.comments;
    if (Array.isArray(activeAsset?.comments)) return activeAsset.comments;
    return [];
  }, [detail?.comments, activeAsset?.comments]);
  const approvalSummary =
    detail?.approvalSummary ||
    activeAsset?.approvalSummary || {
      client: activeAsset?.approvals?.clientApproved ? "APPROVED" : "PENDING",
      superadmin: activeAsset?.approvals?.superadminApproved ? "APPROVED" : "PENDING",
      fullyApproved: Boolean(
        activeAsset?.approvals?.clientApproved && activeAsset?.approvals?.superadminApproved
      ),
    };
  const approvedVersionForCopy =
    versions.find((version) => String(version.id) === String(activeAsset?.approved_version_id || "")) ||
    activeAsset?.approvedVersion ||
    null;
  const selectedVersion =
    versions.find((version) => String(version.id) === String(selectedVersionId)) ||
    activeAsset?.currentVersion ||
    versions[0] ||
    null;
  const isCreativeUser = String(role || "client").toLowerCase() === "creative";
  const canMutateCopy = Boolean(
    isCreativeUser &&
      activeAsset?.approved_version_id &&
      !activeAsset?.archived_at &&
      String(activeAsset?.lifecycle_status || "").toUpperCase() !== "ARCHIVED"
  );
  // Phase 2A1 governed authoring. Deliberately NOT gated on legacy approval
  // state (canMutateCopy's approved_version_id precondition is a legacy
  // AI-copy gate) — governed drafts are authorable whenever the backend
  // capability says so.
  const activeGovernedDraft = useMemo(
    () => governedVersions.find((version) => String(version?.status || "") === "draft") || null,
    [governedVersions]
  );
  const latestGovernedVersion = governedVersions[0] || null;
  const governedAuthoringEnabled = Boolean(
    isCreativeUser && governedCapabilities?.governedAuthoring === true
  );
  const selectedAssetName = String(activeAsset?.title || activeListItem?.title || "").trim();
  const selectedAssetStatus = getStudioEffectiveStatusLabel(activeAsset || activeListItem, "Draft");
  const selectedAssetVersionLabel = getDisplayVersionLabel(selectedVersion || activeAsset?.currentVersion, "v1");
  const selectedAssetPlatform = activeAsset?.studioPlatform || "—";
  const copyPlatformSelection = useMemo(
    () => resolveCopyPlatformSelection({
      approvedPlanContext,
      activeAsset,
      selectedAssetPlatform,
    }),
    [approvedPlanContext, activeAsset, selectedAssetPlatform]
  );
  const copyPlatformOptions = useMemo(
    () => finalizeCopyPlatformOptions(copyPlatformSelection.options),
    [copyPlatformSelection.options]
  );
  const copyPlatformSelected = isCopyBearingPlatform(copyForm.platform);
  const selectedCopySpec = getCopyPlatformSpec(copyForm.platform);
  const manualCopyValidation = useMemo(
    () => validateCopyEntry(copyForm.platform, manualCopyDraft),
    [copyForm.platform, manualCopyDraft]
  );
  const manualCopyReadinessLabel = getManualReadinessLabel(manualCopyValidation, selectedCopySpec);
  const hasManualCopyHardErrors = Boolean(manualCopyValidation?.hasErrors);
  // Governed approved copy: campaign identity comes from the approved plan
  // context; readiness for the selected platform prefers the governed
  // approved payload over any working-copy draft state.
  const approvedCopyCampaignId = approvedPlanContext?.approvedCampaign?.id || null;
  const approvedCopyCurrentForSelectedPlatform =
    approvedCopyPlatforms?.[copyForm.platform]?.current || null;
  const approvedCopyReadiness = useMemo(
    () =>
      approvedCopyCurrentForSelectedPlatform
        ? evaluateApprovedCopyReadiness(approvedCopyCurrentForSelectedPlatform)
        : null,
    [approvedCopyCurrentForSelectedPlatform]
  );
  const approvedCopyPlatformKeys = useMemo(
    () => Object.keys(approvedCopyPlatforms || {}),
    [approvedCopyPlatforms]
  );
  // The COPY PLATFORM selector drives the governed approved-copy display; the
  // card's own chips only override until the selector changes again. A
  // selected platform WITHOUT approved copy still displays (as the governed
  // NO APPROVED COPY state) rather than falling back to another platform.
  const approvedCopyDisplayPlatform =
    approvedCopyViewPlatform ||
    (isCopyBearingPlatform(copyForm.platform) ? copyForm.platform : "") ||
    approvedCopyPlatformKeys[0] ||
    "";
  const approvedCopyChipPlatforms =
    approvedCopyDisplayPlatform && !approvedCopyPlatformKeys.includes(approvedCopyDisplayPlatform)
      ? [...approvedCopyPlatformKeys, approvedCopyDisplayPlatform]
      : approvedCopyPlatformKeys;
  const approvedCopyDisplayEntry = approvedCopyDisplayPlatform
    ? approvedCopyPlatforms?.[approvedCopyDisplayPlatform] || null
    : null;
  const canImportApprovedCopy = Boolean(
    isCreativeUser && governedCapabilities?.governedAuthoring === true && approvedCopyCampaignId
  );
  const approvedCopyPreviewBlocks = useMemo(
    () =>
      (approvedCopyImportPreview?.platforms || []).map((platformPreview) => ({
        platformPreview,
        ...buildApprovedCopyPreviewRows(platformPreview, approvedCopyOperatorEdits),
      })),
    [approvedCopyImportPreview, approvedCopyOperatorEdits]
  );
  const approvedCopyPreviewHasBlockingErrors = approvedCopyPreviewBlocks.some(
    (block) =>
      approvedCopySelectedPlatforms.includes(block.platformPreview.platform) &&
      block.validation?.hasErrors
  );
  const selectedAssetFormat = activeAsset?.studioFormat || "—";
  const selectedAssetDimensions = activeAsset?.studioMeta?.dimensionsLabel || "—";
  const selectedAssetRatio = activeAsset?.studioMeta?.ratioLabel || "—";
  // Display-only reads of timestamps the record already carries. An unparseable
  // or absent stamp yields no label, and the review grid then renders no row.
  const formatReviewTimestamp = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };
  const reviewCreatedLabel = formatReviewTimestamp(
    activeAsset?.created_at || activeAsset?.createdAt
  );
  const reviewUpdatedLabel = formatReviewTimestamp(
    activeAsset?.updated_at || activeAsset?.updatedAt
  );
  const previewModalAsset = useMemo(
    () => enrichedItems.find((item) => String(item.id) === String(previewModalAssetId || "")) || null,
    [enrichedItems, previewModalAssetId]
  );
  const isPreviewModalOpen = Boolean(previewModalAsset);
  const previewModalVersion = useMemo(
    () => createFallbackVersionFromAsset(previewModalAsset),
    [previewModalAsset]
  );
  const previewModalAssetName = String(previewModalAsset?.title || "").trim();
  const previewModalStatus = getStudioEffectiveStatusLabel(previewModalAsset, "Draft");
  const previewModalFormat = previewModalAsset?.studioFormat || "—";
  const previewModalMediaMimeType = String(
    previewModalVersion?.mime_type ||
      previewModalAsset?.currentVersion?.mime_type ||
      previewModalAsset?.mime_type ||
      ""
  )
    .trim()
    .toLowerCase();
  const previewModalMediaIsVideo = useMemo(() => {
    if (!previewModalAsset) return false;
    return isVideoByNameOrMime({
      file_name:
        previewModalVersion?.file_name ||
        previewModalAsset?.currentVersion?.file_name ||
        previewModalAsset?.file_name,
      mime_type:
        previewModalVersion?.mime_type ||
        previewModalAsset?.currentVersion?.mime_type ||
        previewModalAsset?.mime_type,
    });
  }, [previewModalAsset, previewModalVersion]);
  const previewModalPosterUrl = useMemo(
    () => pickCreativePreviewPosterUrl(previewModalVersion, previewModalAsset),
    [previewModalVersion, previewModalAsset]
  );
  const previewModalMediaIsDocument = useMemo(() => {
    if (!previewModalAsset) return false;
    return isDocumentByNameOrMime({
      file_name:
        previewModalVersion?.file_name ||
        previewModalAsset?.currentVersion?.file_name ||
        previewModalAsset?.file_name,
      mime_type:
        previewModalVersion?.mime_type ||
        previewModalAsset?.currentVersion?.mime_type ||
        previewModalAsset?.mime_type,
    });
  }, [previewModalAsset, previewModalVersion]);
  const creativeSummary = useMemo(() => {
    const states = new Set(
      reviewableItems.flatMap((item) => item.studioPlatformKeys || [])
    );
    return {
      totalAssets: reviewableItems.length,
      reviewQueue: reviewableItems.filter((item) => {
        if (isStudioApprovedAsset(item)) return false;
        return ["IN_REVIEW", "CHANGES_REQUESTED"].includes(normalizeLifecycleBucket(item.lifecycle_status));
      }).length,
      approvedAssets: reviewableItems.filter((item) => isStudioApprovedAsset(item)).length,
      activeChannels: states.size,
    };
  }, [reviewableItems]);
  const platformOptions = useMemo(
    () => {
      // Filter options come ONLY from the approved campaign's platforms. They
      // previously unioned in every platform any creative happened to match,
      // which could offer the operator a platform the campaign never selected.
      const orderedKeys = Array.from(
        new Set(
          approvedPlatformKeys
            .map((key) => String(key || "").trim())
            .filter((key) => key && !CREATIVE_STUDIO_PLATFORM_FILTER_EXCLUDED_KEYS.has(key.toLowerCase()))
        )
      );
      const approvedKeySet = new Set(
        approvedPlatformKeys.filter((key) => !CREATIVE_STUDIO_PLATFORM_FILTER_EXCLUDED_KEYS.has(String(key || "").toLowerCase()))
      );

      return placeYahooAfterAmazon(orderedKeys
        .map((key) => ({
          key,
          label: platformLabelFromKey(key) || key,
        }))
        .sort((left, right) => {
          const leftApproved = approvedKeySet.has(left.key) ? 0 : 1;
          const rightApproved = approvedKeySet.has(right.key) ? 0 : 1;
          if (leftApproved !== rightApproved) return leftApproved - rightApproved;
          return left.label.localeCompare(right.label);
        }), (platform) => platform.key);
    },
    [approvedPlatformKeys]
  );
  useEffect(() => {
    if (CREATIVE_STUDIO_PLATFORM_FILTER_EXCLUDED_KEYS.has(String(channelFilter || "").toLowerCase())) {
      setChannelFilter("all");
    }
  }, [channelFilter]);
  const formatOptions = useMemo(
    () =>
      Array.from(new Set(reviewableItems.map((item) => String(item.studioFormat || "").trim().replace(/^—$/, "")).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [reviewableItems]
  );
  const filteredItems = useMemo(() => {
    const normalizedQuery = String(searchQuery || "").trim().toLowerCase();
    return reviewableItems.filter((item) => {
      const effectiveStatusBucket = getStudioFilterBucket(item, "Draft");
      const statusMatch = statusFilter === "all" || effectiveStatusBucket === statusFilter;
      const channelMatch =
        channelFilter === "all" ||
        (Array.isArray(item.studioPlatformKeys) && item.studioPlatformKeys.includes(channelFilter));
      const formatMatch = formatFilter === "all" || String(item.studioFormat || "").trim() === formatFilter;
      const queryMatch =
        !normalizedQuery ||
        [
          item.title,
          item.studioPlatform,
          item.studioPlatformLabelText,
          item.studioFormat,
          item.lifecycle_status,
          getStudioEffectiveStatusLabel(item, "Draft"),
          getDisplayVersionLabel(item.currentVersion, "v1"),
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedQuery));
      return statusMatch && channelMatch && formatMatch && queryMatch;
    });
  }, [reviewableItems, searchQuery, statusFilter, channelFilter, formatFilter]);
  const activeFilteredIndex = useMemo(
    () => filteredItems.findIndex((item) => String(item.id) === String(activeId || "")),
    [filteredItems, activeId]
  );
  // REVIEW QUEUE. Built from the rows the operator can currently SEE, so it
  // inherits the advertiser, campaign and status filtering already applied and
  // can never pull in an unrelated creative mid-review.
  const reviewQueue = useMemo(() => buildReviewQueue(filteredItems), [filteredItems]);
  const reviewPosition = useMemo(
    () => reviewQueuePosition(reviewQueue, activeId),
    [reviewQueue, activeId]
  );
  const reviewAdvance = useMemo(
    () => resolveAdvanceTarget(reviewQueue, activeId),
    [reviewQueue, activeId]
  );
  const canGoPreviousAsset = activeFilteredIndex > 0;
  const canGoNextAsset = activeFilteredIndex >= 0 && activeFilteredIndex < filteredItems.length - 1;
  const previewModalIndex = useMemo(
    () =>
      filteredItems.findIndex(
        (item) => String(item.id) === String(previewModalAssetId || "")
      ),
    [filteredItems, previewModalAssetId]
  );
  const previousPreviewModalAsset =
    previewModalIndex > 0 ? filteredItems[previewModalIndex - 1] : null;
  const nextPreviewModalAsset =
    previewModalIndex >= 0 && previewModalIndex < filteredItems.length - 1
      ? filteredItems[previewModalIndex + 1]
      : null;
  const previewModalPositionLabel =
    previewModalIndex >= 0 && filteredItems.length > 1
      ? `${previewModalIndex + 1} / ${filteredItems.length}`
      : "";
  const openCommentCount = useMemo(
    () =>
      comments.filter((comment) => String(comment?.status || "").toUpperCase() !== "RESOLVED").length,
    [comments]
  );
  const reviewThreadLabel =
    openCommentCount === 1 ? "1 open thread" : `${openCommentCount} open threads`;
  const derivedUploadTitle = useMemo(
    () => deriveTitleFromFilename(fileName || fileObj?.name || ""),
    [fileName, fileObj]
  );
  const effectiveUploadTitle = useMemo(
    () => String(title || "").trim() || derivedUploadTitle,
    [title, derivedUploadTitle]
  );
  const pendingUploadCount = fileObjs.length || (fileObj ? 1 : 0);
  const canCreateAsset = Boolean(
    effectiveSelectedAdvertiserId &&
      pendingUploadCount > 0 &&
      // Bulk derives a per-file title from each filename, so a manual title is
      // only required for the single-file path.
      (pendingUploadCount > 1 || effectiveUploadTitle)
  );
  const uploaderDisabled = !effectiveSelectedAdvertiserId;
  // In flight covers the whole journey, not just the byte transfer: a creative
  // being verified, registered, or inspected is still an upload in progress,
  // and the drop zone must not go back to inviting new files underneath it.
  const uploadInFlight =
    uploadState.active || uploadFileStates.some((entry) => isActiveStage(entry.stage));
  // Counted from the rows actually on screen, so retrying one creative out of
  // several failures can never leave the control disagreeing with what the
  // user can see.
  const failedUploadCount = uploadFileStates.filter(
    (entry) => entry.stage === UPLOAD_STAGES.FAILED
  ).length;

  const mediaIsVideo = useMemo(() => {
    if (!selectedVersion) return false;
    return isVideoByNameOrMime({
      file_name: selectedVersion.file_name,
      mime_type: selectedVersion.mime_type,
    });
  }, [selectedVersion]);
  const selectedMediaMimeType = String(
    selectedVersion?.mime_type ||
      activeAsset?.currentVersion?.mime_type ||
      activeAsset?.mime_type ||
      ""
  )
    .trim()
    .toLowerCase();
  // The asset payload's poster fields are the preferred source, but the backend
  // has never populated them — the generated poster lives in
  // intake_json.derivatives and, until now, had no route. Falling back to
  // GET /:id/poster is what actually puts a picture on the screen.
  const previewPosterUrl = useMemo(
    () =>
      pickCreativePreviewPosterUrl(selectedVersion, activeAsset) ||
      (activeAsset?.id ? creativeAssetPath(activeAsset.id, "poster") : ""),
    [selectedVersion, activeAsset]
  );
  const mediaIsDocument = useMemo(() => {
    if (!selectedVersion) return false;
    return isDocumentByNameOrMime({
      file_name: selectedVersion.file_name,
      mime_type: selectedVersion.mime_type,
    });
  }, [selectedVersion]);
  const previewShowLoaderOnly = (detailLoading || previewLoading) && !previewUrl;
  const previewShowLoadingOverlay = !previewError && previewMediaLoading && Boolean(previewUrl);
  const previewModalShowLoaderOnly = previewModalLoading && !previewModalUrl;
  const previewModalShowLoadingOverlay =
    !previewModalError && previewModalMediaLoading && Boolean(previewModalUrl);

  useEffect(
    () => () => {
      revokeCreativePreviewCache(previewObjectUrlCacheRef.current);
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    const advertiserId = effectiveSelectedAdvertiserId;
    const scopeGeneration = advertiserScopeGenerationRef.current;
    const requestIsCurrent = () =>
      !cancelled &&
      isAdvertiserScopeCurrent(scopeGeneration, advertiserId);

    const run = async () => {
      setApprovedPlanCampaigns([]);
      if (!advertiserId) return;

      try {
        const res = await apiJson(
          `/api/media-campaigns?advertiserId=${encodeURIComponent(advertiserId)}`
        );
        const rows = Array.isArray(res?.campaigns)
          ? res.campaigns
          : Array.isArray(res?.data)
          ? res.data
          : [];
        if (requestIsCurrent()) setApprovedPlanCampaigns(rows);
      } catch (_error) {
        if (requestIsCurrent()) setApprovedPlanCampaigns([]);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [effectiveSelectedAdvertiserId, isAdvertiserScopeCurrent]);

  useEffect(() => {
    setIsReviewModalOpen(false);
  }, [effectiveSelectedAdvertiserId]);

  useEffect(() => {
    let cancelled = false;
    const advertiserId = effectiveSelectedAdvertiserId;
    const scopeGeneration = advertiserScopeGenerationRef.current;
    const requestIsCurrent = () =>
      !cancelled &&
      isAdvertiserScopeCurrent(scopeGeneration, advertiserId);

    const run = async () => {
      setItems([]);
      setActiveId(null);
      setDetail(null);
      setSelectedVersionId(null);
      setListError("");

      const advId = advertiserId;
      if (!advId) return;

      setListLoading(true);
      try {
        const res = await apiJson(creativeAssetListPath(advId));
        const rows = filterClientVisibleCreativeAssets(res?.data);
        if (requestIsCurrent()) {
          setItems(rows);
          setActiveId(getPreferredReviewAssetId(rows));
        }
      } catch (e) {
        if (requestIsCurrent()) {
          setListError(String(e?.message || "Failed to load assets"));
        }
      } finally {
        if (requestIsCurrent()) setListLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [effectiveSelectedAdvertiserId, isAdvertiserScopeCurrent]);

  useEffect(() => {
    platformEligibilityRequestedRef.current.clear();
    setPlatformEligibilityByAssetId({});
  }, [effectiveSelectedAdvertiserId, platformEligibilityContextKey]);

  useEffect(() => {
    if (!items.length) return undefined;

    let cancelled = false;
    const advertiserId = effectiveSelectedAdvertiserId;
    const scopeGeneration = advertiserScopeGenerationRef.current;
    const requestIsCurrent = () =>
      !cancelled &&
      isAdvertiserScopeCurrent(scopeGeneration, advertiserId);
    const contextApprovedPlatformKeys = platformEligibilityContextKey
      ? platformEligibilityContextKey.split(",").map((key) => key.trim()).filter(Boolean)
      : [];
    const assetIds = Array.from(
      new Set(
        items
          .map((item) => String(item?.id || "").trim())
          .filter(Boolean)
      )
    );
    const pendingAssetIds = assetIds.filter((assetId) => {
      const requestKey = `${effectiveSelectedAdvertiserId}|${assetId}|${platformEligibilityContextKey}`;
      if (platformEligibilityRequestedRef.current.has(requestKey)) return false;
      platformEligibilityRequestedRef.current.add(requestKey);
      return true;
    });

    if (!pendingAssetIds.length || !advertiserId) return undefined;

    setPlatformEligibilityByAssetId((prev) => {
      const next = { ...prev };
      pendingAssetIds.forEach((assetId) => {
        next[assetId] = {
          loading: true,
          data: prev[assetId]?.data || null,
          error: null,
          contextKey: platformEligibilityContextKey,
        };
      });
      return next;
    });

    const run = async () => {
      for (let index = 0; index < pendingAssetIds.length; index += CREATIVE_ELIGIBILITY_BATCH_SIZE) {
        const chunk = pendingAssetIds.slice(index, index + CREATIVE_ELIGIBILITY_BATCH_SIZE);
        try {
          const { results, errors } = await fetchCreativeAssetPlatformEligibilityBatch(chunk, {
            advertiserId,
            approvedPlatformKeys: contextApprovedPlatformKeys,
          });
          if (!requestIsCurrent()) return;
          setPlatformEligibilityByAssetId((prev) => ({
            ...prev,
            ...Object.fromEntries(chunk.map((assetId) => {
              const key = String(assetId);
              const data = results[key] || null;
              const error = errors?.[key];
              return [key, {
                loading: false,
                data,
                error: data ? null : error?.message || error?.error || "Eligibility unavailable",
                contextKey: platformEligibilityContextKey,
              }];
            })),
          }));
        } catch (error) {
          if (!requestIsCurrent()) return;
          setPlatformEligibilityByAssetId((prev) => ({
            ...prev,
            ...Object.fromEntries(chunk.map((assetId) => [String(assetId), {
              loading: false,
              data: null,
              error: error?.message || "Eligibility unavailable",
              contextKey: platformEligibilityContextKey,
            }])),
          }));
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    items,
    effectiveSelectedAdvertiserId,
    isAdvertiserScopeCurrent,
    platformEligibilityContextKey,
  ]);

  useEffect(() => {
    let cancelled = false;
    const advertiserId = effectiveSelectedAdvertiserId;
    const scopeGeneration = advertiserScopeGenerationRef.current;
    const requestIsCurrent = () =>
      !cancelled &&
      isAdvertiserScopeCurrent(scopeGeneration, advertiserId);

    const run = async () => {
      setDetail(null);
      setDetailError("");
      if (!activeId) return;

      setActionError("");
      setActionMessage("");
      setDetailLoading(true);
      try {
        const res = await apiJson(creativeAssetPath(activeId));
        if (requestIsCurrent()) {
          setDetail(res?.data || null);
          const nextVersionId =
            res?.data?.asset?.currentVersion?.id ||
            res?.data?.asset?.current_version_id ||
            res?.data?.versions?.[0]?.id ||
            null;
          setSelectedVersionId(nextVersionId);
        }
      } catch (e) {
        if (requestIsCurrent()) {
          setDetailError(String(e?.message || "Failed to load asset detail"));
        }
      } finally {
        if (requestIsCurrent()) setDetailLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    activeId,
    effectiveSelectedAdvertiserId,
    isAdvertiserScopeCurrent,
  ]);

  useEffect(() => {
    const optionValues = copyPlatformOptions.map((option) => option.value);
    const nextPlatform = optionValues[0] || resolveDefaultCopyPlatform(selectedAssetPlatform, activeAsset?.channel);
    const nextFormat = String(selectedAssetFormat || activeAsset?.format || "").trim().replace(/^—$/, "");

    setCopyForm((prev) => ({
      platform: optionValues.includes(prev.platform) ? prev.platform : nextPlatform,
      format: nextFormat,
      campaignId: prev.campaignId || "",
      objective: prev.objective || "",
      targetAudience: prev.targetAudience || "",
      tone: prev.tone || "",
      cta: prev.cta || "",
      messagingNotes: prev.messagingNotes || "",
      variantCount: prev.variantCount || 3,
    }));
    setManualCopyValidated(false);
  }, [
    activeAsset?.id,
    activeAsset?.approved_version_id,
    activeAsset?.channel,
    activeAsset?.format,
    copyPlatformOptions,
    selectedAssetFormat,
    selectedAssetPlatform,
  ]);

  useEffect(() => {
    setManualCopyDraft((prev) => createManualCopyDraftForSpec(selectedCopySpec, prev));
    setManualCopyValidated(false);
  }, [selectedCopySpec]);

  // Phase 2A1: governed versions + backend-owned authoring capability for the
  // selected asset. Failure is honest: no capability means no authoring UI.
  useEffect(() => {
    let cancelled = false;
    const advertiserId = effectiveSelectedAdvertiserId;
    const scopeGeneration = advertiserScopeGenerationRef.current;
    const requestIsCurrent = () =>
      !cancelled &&
      isAdvertiserScopeCurrent(scopeGeneration, advertiserId);
    const run = async () => {
      setGovernedVersions([]);
      setGovernedCapabilities(null);
      if (!activeId) return;
      try {
        const res = await apiJson(creativeAssetPath(activeId, "versions"));
        if (requestIsCurrent()) {
          setGovernedVersions(Array.isArray(res?.data?.versions) ? res.data.versions : []);
          setGovernedCapabilities(res?.data?.capabilities || null);
        }
      } catch (e) {
        if (requestIsCurrent()) {
          setGovernedVersions([]);
          setGovernedCapabilities(null);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    activeId,
    effectiveSelectedAdvertiserId,
    isAdvertiserScopeCurrent,
  ]);

  // Hydrate Manual Entry from the canonical governed draft. Re-runs after
  // every save (versions re-fetch) so the backend's normalized values are
  // the authority, and after platform switches (which wipe the local form).
  useEffect(() => {
    if (!governedAuthoringEnabled || !activeGovernedDraft) return;
    setManualCopyDraft({
      headline: activeGovernedDraft.headline || "",
      primaryText: activeGovernedDraft.primaryText || "",
      description: activeGovernedDraft.description || "",
      cta: activeGovernedDraft.cta || "",
      path: activeGovernedDraft.displayUrl || "",
      destinationUrl: activeGovernedDraft.destinationUrl || "",
    });
    setManualCopyValidated(false);
  }, [governedAuthoringEnabled, activeGovernedDraft, copyForm.platform]);


  useEffect(() => {
    let cancelled = false;
    const previewCache = previewObjectUrlCacheRef.current;
    const advertiserId = effectiveSelectedAdvertiserId;
    const scopeGeneration = advertiserScopeGenerationRef.current;
    const requestIsCurrent = () =>
      !cancelled &&
      isAdvertiserScopeCurrent(scopeGeneration, advertiserId);

    const run = async () => {
      const runStartedAt = getCreativePreviewNow();

      const resolvedVersionId =
        selectedVersionId ||
        selectedVersion?.id ||
        activeAsset?.currentVersion?.id ||
        activeAsset?.current_version_id ||
        versions?.[0]?.id ||
        null;
      const resolvedMimeType =
        selectedVersion?.mime_type ||
        activeAsset?.currentVersion?.mime_type ||
        activeAsset?.mime_type ||
        "";
      const resolvedFileName =
        selectedVersion?.file_name ||
        activeAsset?.currentVersion?.file_name ||
        activeAsset?.file_name ||
        "";
      const resolvedCurrentVersionId =
        activeAsset?.currentVersion?.id || activeAsset?.current_version_id || null;
      const cacheKey = getCreativePreviewCacheKey({
        assetId: activeId,
        versionId: resolvedVersionId,
        currentVersionId: resolvedCurrentVersionId,
        mimeType: resolvedMimeType,
        fileName: resolvedFileName,
      });
      const shouldAwaitReady = shouldAwaitCreativePreviewReady({
        file_name: resolvedFileName,
        mime_type: resolvedMimeType,
      });

      // The preview source is unchanged. This effect re-runs on every detail
      // refresh (approval included) because `versions`/`activeAsset` change
      // identity, not because the asset changed. Tearing the preview down and
      // re-attaching the SAME object URL means the browser never re-fires
      // `load`, so the media-loading flag would never clear and the spinner
      // would run forever. Approval must not disturb a preview already shown.
      if (shouldReuseExistingPreview({
        currentCacheKey: previewCacheKeyRef.current,
        nextCacheKey: cacheKey,
        hasObjectUrl: Boolean(previewObjectUrlRef.current),
      })) {
        if (requestIsCurrent()) {
          setPreviewLoading(false);
          traceCreativePreview("preview_source_unchanged", {
            scope: "stage",
            assetId: activeId,
            versionId: resolvedVersionId,
          });
        }
        return;
      }

      setPreviewError("");
      setPreviewLoading(true);
      setPreviewMediaLoading(false);

      if (previewObjectUrlRef.current) {
        releaseCreativePreviewObjectUrl(
          previewCache,
          previewObjectUrlRef.current
        );
        previewObjectUrlRef.current = "";
        previewCacheKeyRef.current = "";
        setPreviewUrl("");
      }

      traceCreativePreview("asset_preview_open", {
        scope: "stage",
        assetId: activeId,
        versionId: resolvedVersionId,
        mimeType: resolvedMimeType,
        fileName: resolvedFileName,
      });

      if (!resolvedVersionId && !activeId) {
        if (requestIsCurrent()) setPreviewLoading(false);
        return;
      }

      // VIDEO IS NEVER FETCHED AS SOURCE BYTES.
      //
      // This effect used to download the whole asset as a Blob to render a
      // preview. For a banner that is fine. For the Never Stop Chasing masters
      // it is 288-751MB of ProRes into browser memory for a file the browser
      // cannot decode — the indefinite "Loading Preview" with no way to finish.
      //
      // Video is reviewed by its generated poster instead (resolved in
      // `reviewMedia`, served by GET /:id/poster). Bailing out here is what
      // makes the review finite; the poster is a plain <img> with its own
      // load/error handlers, so it always settles.
      if (isVideoByNameOrMime({ file_name: resolvedFileName, mime_type: resolvedMimeType })) {
        if (requestIsCurrent()) {
          setPreviewLoading(false);
          setPreviewMediaLoading(false);
        }
        return;
      }

      const cachedPreview = getCreativePreviewCachedEntry(
        previewCache,
        cacheKey
      );
      if (cachedPreview?.url) {
        if (requestIsCurrent()) {
          previewObjectUrlRef.current = cachedPreview.url;
          previewCacheKeyRef.current = cacheKey;
          setPreviewUrl(cachedPreview.url);
          setPreviewMediaLoading(shouldAwaitReady);
          setPreviewLoading(false);
          traceCreativePreview("preview_url_cache_hit", {
            scope: "stage",
            assetId: activeId,
            versionId: resolvedVersionId,
            elapsedMs: Math.round(getCreativePreviewNow() - runStartedAt),
          });
        }
        return;
      }

      try {
        const fetchStartedAt = getCreativePreviewNow();
        traceCreativePreview("preview_asset_fetch_started", {
          scope: "stage",
          assetId: activeId,
          versionId: resolvedVersionId,
          mimeType: resolvedMimeType,
        });
        const blob = await loadCreativePreviewBlob({
          assetId: activeId,
          versionId: resolvedVersionId,
          currentVersionId: resolvedCurrentVersionId,
          mimeType: resolvedMimeType,
        });
        if (!requestIsCurrent()) return;
        traceCreativePreview("preview_asset_fetch_completed", {
          scope: "stage",
          assetId: activeId,
          versionId: resolvedVersionId,
          durationMs: Math.round(getCreativePreviewNow() - fetchStartedAt),
          blobSize: blob?.size || 0,
          blobType: blob?.type || "",
        });

        if (!blob) {
          if (requestIsCurrent()) setPreviewMediaLoading(false);
          return;
        }

        // Another current preview may have completed this same asset version
        // while this fetch was pending. Share its URL instead of replacing and
        // revoking a URL still rendered by the stage or full-screen preview.
        let url = getCreativePreviewCachedEntry(previewCache, cacheKey)?.url;
        if (!url) {
          url = URL.createObjectURL(blob);
          setCreativePreviewCachedEntry(previewCache, cacheKey, {
            url,
            mimeType: blob?.type || resolvedMimeType || "",
            size: blob?.size || 0,
          }, [previewObjectUrlRef.current, previewModalObjectUrlRef.current]);
        }
        if (requestIsCurrent()) {
          previewObjectUrlRef.current = url;
          previewCacheKeyRef.current = cacheKey;
          setPreviewUrl(url);
          setPreviewMediaLoading(shouldAwaitReady);
          traceCreativePreview("preview_url_ready", {
            scope: "stage",
            assetId: activeId,
            versionId: resolvedVersionId,
            elapsedMs: Math.round(getCreativePreviewNow() - runStartedAt),
          });
        }
      } catch (e) {
        if (requestIsCurrent()) {
          setPreviewMediaLoading(false);
          setPreviewError(String(e?.message || "Failed to load preview"));
          traceCreativePreview("preview_asset_fetch_failed", {
            scope: "stage",
            assetId: activeId,
            versionId: resolvedVersionId,
            message: String(e?.message || e || "Failed to load preview"),
          });
        }
      } finally {
        if (requestIsCurrent()) setPreviewLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
      if (previewObjectUrlRef.current) {
        releaseCreativePreviewObjectUrl(
          previewCache,
          previewObjectUrlRef.current
        );
        previewObjectUrlRef.current = "";
        previewCacheKeyRef.current = "";
      }
    };
  }, [
    activeAsset?.currentVersion?.mime_type,
    activeAsset?.currentVersion?.id,
    activeAsset?.currentVersion?.file_name,
    activeAsset?.mime_type,
    activeAsset?.file_name,
    activeAsset?.current_version_id,
    activeId,
    effectiveSelectedAdvertiserId,
    isAdvertiserScopeCurrent,
    selectedVersion?.file_name,
    selectedVersion?.mime_type,
    selectedVersion?.id,
    selectedVersionId,
    versions,
  ]);

  useEffect(() => {
    let cancelled = false;
    const previewCache = previewObjectUrlCacheRef.current;
    const advertiserId = effectiveSelectedAdvertiserId;
    const scopeGeneration = advertiserScopeGenerationRef.current;
    const requestIsCurrent = () =>
      !cancelled &&
      isAdvertiserScopeCurrent(scopeGeneration, advertiserId);

    const run = async () => {
      const runStartedAt = getCreativePreviewNow();
      setPreviewModalError("");
      setPreviewModalLoading(true);
      setPreviewModalMediaLoading(false);

      if (previewModalObjectUrlRef.current) {
        releaseCreativePreviewObjectUrl(
          previewCache,
          previewModalObjectUrlRef.current
        );
        previewModalObjectUrlRef.current = "";
        setPreviewModalUrl("");
      }

      const resolvedMimeType =
        previewModalVersion?.mime_type ||
        previewModalAsset?.currentVersion?.mime_type ||
        previewModalAsset?.mime_type ||
        "";
      const resolvedFileName =
        previewModalVersion?.file_name ||
        previewModalAsset?.currentVersion?.file_name ||
        previewModalAsset?.file_name ||
        "";
      const resolvedVersionId =
        previewModalVersion?.id ||
        previewModalAsset?.currentVersion?.id ||
        previewModalAsset?.current_version_id ||
        null;
      const resolvedCurrentVersionId =
        previewModalAsset?.currentVersion?.id || previewModalAsset?.current_version_id || null;
      const cacheKey = getCreativePreviewCacheKey({
        assetId: previewModalAssetId,
        versionId: resolvedVersionId,
        currentVersionId: resolvedCurrentVersionId,
        mimeType: resolvedMimeType,
        fileName: resolvedFileName,
      });
      const shouldAwaitReady = shouldAwaitCreativePreviewReady({
        file_name: resolvedFileName,
        mime_type: resolvedMimeType,
      });

      traceCreativePreview("asset_preview_open", {
        scope: "modal",
        assetId: previewModalAssetId,
        versionId: resolvedVersionId,
        mimeType: resolvedMimeType,
        fileName: resolvedFileName,
      });

      if (!previewModalAssetId) {
        if (requestIsCurrent()) setPreviewModalLoading(false);
        return;
      }

      const cachedPreview = getCreativePreviewCachedEntry(
        previewCache,
        cacheKey
      );
      if (cachedPreview?.url) {
        if (requestIsCurrent()) {
          previewModalObjectUrlRef.current = cachedPreview.url;
          setPreviewModalUrl(cachedPreview.url);
          setPreviewModalMediaLoading(shouldAwaitReady);
          setPreviewModalLoading(false);
          traceCreativePreview("preview_url_cache_hit", {
            scope: "modal",
            assetId: previewModalAssetId,
            versionId: resolvedVersionId,
            elapsedMs: Math.round(getCreativePreviewNow() - runStartedAt),
          });
        }
        return;
      }

      try {
        const fetchStartedAt = getCreativePreviewNow();
        traceCreativePreview("preview_asset_fetch_started", {
          scope: "modal",
          assetId: previewModalAssetId,
          versionId: resolvedVersionId,
          mimeType: resolvedMimeType,
        });
        const blob = await loadCreativePreviewBlob({
          assetId: previewModalAssetId,
          versionId: resolvedVersionId,
          currentVersionId: resolvedCurrentVersionId,
          mimeType: resolvedMimeType,
        });
        if (!requestIsCurrent()) return;
        traceCreativePreview("preview_asset_fetch_completed", {
          scope: "modal",
          assetId: previewModalAssetId,
          versionId: resolvedVersionId,
          durationMs: Math.round(getCreativePreviewNow() - fetchStartedAt),
          blobSize: blob?.size || 0,
          blobType: blob?.type || "",
        });

        if (!blob) {
          if (requestIsCurrent()) setPreviewModalMediaLoading(false);
          return;
        }

        // Another current preview may have completed this same asset version
        // while this fetch was pending. Share its URL instead of replacing and
        // revoking a URL still rendered by the stage or full-screen preview.
        let url = getCreativePreviewCachedEntry(previewCache, cacheKey)?.url;
        if (!url) {
          url = URL.createObjectURL(blob);
          setCreativePreviewCachedEntry(previewCache, cacheKey, {
            url,
            mimeType: blob?.type || resolvedMimeType || "",
            size: blob?.size || 0,
          }, [previewObjectUrlRef.current, previewModalObjectUrlRef.current]);
        }
        if (requestIsCurrent()) {
          previewModalObjectUrlRef.current = url;
          setPreviewModalUrl(url);
          setPreviewModalMediaLoading(shouldAwaitReady);
          traceCreativePreview("preview_url_ready", {
            scope: "modal",
            assetId: previewModalAssetId,
            versionId: resolvedVersionId,
            elapsedMs: Math.round(getCreativePreviewNow() - runStartedAt),
          });
        }
      } catch (e) {
        if (requestIsCurrent()) {
          setPreviewModalMediaLoading(false);
          setPreviewModalError(String(e?.message || "Failed to load preview"));
          traceCreativePreview("preview_asset_fetch_failed", {
            scope: "modal",
            assetId: previewModalAssetId,
            versionId: resolvedVersionId,
            message: String(e?.message || e || "Failed to load preview"),
          });
        }
      } finally {
        if (requestIsCurrent()) setPreviewModalLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
      if (previewModalObjectUrlRef.current) {
        releaseCreativePreviewObjectUrl(
          previewCache,
          previewModalObjectUrlRef.current
        );
        previewModalObjectUrlRef.current = "";
      }
    };
  }, [
    previewModalAsset?.currentVersion?.id,
    previewModalAsset?.currentVersion?.mime_type,
    previewModalAsset?.currentVersion?.file_name,
    previewModalAsset?.current_version_id,
    previewModalAsset?.mime_type,
    previewModalAsset?.file_name,
    previewModalAssetId,
    effectiveSelectedAdvertiserId,
    isAdvertiserScopeCurrent,
    previewModalVersion?.file_name,
    previewModalVersion?.id,
    previewModalVersion?.mime_type,
  ]);

  const refreshActiveDetail = async (requestScope = null) => {
    const currentActiveId = activeId;
    const advertiserId =
      requestScope?.advertiserId ?? effectiveSelectedAdvertiserId;
    const scopeGeneration =
      requestScope?.scopeGeneration ?? advertiserScopeGenerationRef.current;
    const requestIsCurrent = () =>
      isAdvertiserScopeCurrent(scopeGeneration, advertiserId);
    if (!currentActiveId || !requestIsCurrent()) return false;
    const currentEligibilityKey = String(currentActiveId || "");
    platformEligibilityRequestedRef.current.delete(
      `${advertiserId}|${currentEligibilityKey}|${platformEligibilityContextKey}`
    );
    setPlatformEligibilityByAssetId((prev) => {
      const next = { ...prev };
      delete next[currentEligibilityKey];
      return next;
    });
    const [listRes, res] = await Promise.all([
      apiJson(creativeAssetListPath(advertiserId)),
      apiJson(creativeAssetPath(currentActiveId)),
    ]);
    if (!requestIsCurrent()) return false;
    const rows = filterClientVisibleCreativeAssets(listRes?.data);
    const resolvedActiveId = getPreferredReviewAssetId(rows, currentActiveId);
    const commentsFlat = Array.isArray(res?.data?.commentsFlat) ? res.data.commentsFlat : [];
    const commentCounts = commentsFlat.reduce(
      (acc, comment) => {
        if (String(comment?.status || "").toUpperCase() === "RESOLVED") acc.resolved += 1;
        else acc.open += 1;
        return acc;
      },
      { open: 0, resolved: 0 }
    );
    setItems(rows);
    setActiveId(resolvedActiveId);
    if (String(resolvedActiveId || "") !== String(currentActiveId || "")) {
      setDetail(null);
      setSelectedVersionId(null);
      return true;
    }
    setDetail(res?.data || null);
    setItems((prev) =>
      prev.map((item) =>
        String(item.id) === String(currentActiveId)
          ? {
              ...item,
              lifecycle_status: res?.data?.asset?.lifecycle_status,
              currentVersion: res?.data?.asset?.currentVersion || item.currentVersion,
              approvedVersion: res?.data?.asset?.approvedVersion || item.approvedVersion,
              approvalSummary: res?.data?.approvalSummary || item.approvalSummary,
              latestExport: res?.data?.latestExport || item.latestExport,
              commentCounts,
            }
          : item
      )
    );
    return true;
  };

  const handleAddComment = async () => {
    if (!activeId || !selectedVersionId || !newComment.trim()) return;
    const scopeGeneration = advertiserScopeGenerationRef.current;
    const advertiserId = effectiveSelectedAdvertiserId;
    const requestScope = { scopeGeneration, advertiserId };
    const requestIsCurrent = () =>
      isAdvertiserScopeCurrent(scopeGeneration, advertiserId);
    setActionError("");
    setActionMessage("");
    try {
      await apiJson(creativeAssetPath(activeId, "comments"), {
        method: "POST",
        body: {
          body: newComment.trim(),
          creativeAssetVersionId: selectedVersionId,
        },
      });
      if (!requestIsCurrent()) return;
      setNewComment("");
      setActionMessage("Comment added.");
      await refreshActiveDetail(requestScope);
    } catch (e) {
      if (requestIsCurrent()) {
        setActionError(String(e?.message || "Failed to add comment"));
      }
    }
  };

  const handleApprovalAction = async (action) => {
    if (!activeId || !selectedVersionId) return;
    const scopeGeneration = advertiserScopeGenerationRef.current;
    const advertiserId = effectiveSelectedAdvertiserId;
    const requestScope = { scopeGeneration, advertiserId };
    const requestIsCurrent = () =>
      isAdvertiserScopeCurrent(scopeGeneration, advertiserId);
    const reason = action === "reject" ? newComment.trim() || "Changes requested" : "Approved";
    setActionError("");
    setActionMessage("");
    try {
      await apiJson(creativeAssetPath(activeId, "approvals", action), {
        method: "POST",
        body: {
          approvalType: "CLIENT",
          creativeAssetVersionId: selectedVersionId,
          reason,
        },
      });
      if (!requestIsCurrent()) return false;
      if (action === "reject") setNewComment("");
      setActionMessage(action === "approve" ? "Creative approved." : "Revision requested.");
      await refreshActiveDetail(requestScope);
      // Reported so "… & Next" can refuse to advance past a decision that was
      // never actually recorded.
      return true;
    } catch (e) {
      if (requestIsCurrent()) {
        setActionError(String(e?.message || "Failed to update approval"));
      }
      return false;
    }
  };

  const handleCopyFormChange = (field, value) => {
    setCopyForm((prev) => ({
      ...prev,
      [field]: field === "variantCount" ? Number(value || 3) : value,
    }));
  };

  const handleManualCopyDraftChange = (field, value) => {
    setManualCopyDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
    setManualCopyValidated(false);
  };

  const handleValidateManualCopy = () => {
    setManualCopyValidated(true);
    if (!selectedCopySpec) {
      setActionError("Choose a copy-bearing platform before validating manual copy.");
      return;
    }
    setActionError("");
    setActionMessage(
      hasManualCopyHardErrors
        ? "Manual copy has required fields missing or over platform limits."
        : governedAuthoringEnabled
          ? "Manual copy passes local checks."
          : "Manual copy passes local checks. Saving manual copy is not enabled yet."
    );
  };

  // Phase 2A1 canonical draft fields only. The spec's Google display "path"
  // maps to the governed displayUrl column; nothing else is invented.
  const buildGovernedDraftFieldsPayload = () => ({
    headline: manualCopyDraft.headline || "",
    primaryText: manualCopyDraft.primaryText || "",
    description: manualCopyDraft.description || "",
    cta: manualCopyDraft.cta || "",
    displayUrl: manualCopyDraft.path || "",
    destinationUrl: manualCopyDraft.destinationUrl || "",
  });

  const refreshGovernedVersions = async (requestScope = null) => {
    const advertiserId =
      requestScope?.advertiserId ?? effectiveSelectedAdvertiserId;
    const scopeGeneration =
      requestScope?.scopeGeneration ?? advertiserScopeGenerationRef.current;
    const assetId = requestScope?.assetId ?? activeId;
    const requestIsCurrent = () =>
      isAdvertiserScopeCurrent(scopeGeneration, advertiserId);
    if (!assetId || !requestIsCurrent()) return false;
    const res = await apiJson(creativeAssetPath(assetId, "versions"));
    if (!requestIsCurrent()) return false;
    setGovernedVersions(Array.isArray(res?.data?.versions) ? res.data.versions : []);
    setGovernedCapabilities(res?.data?.capabilities || null);
    return true;
  };

  // -------------------------------------------------------------------------
  // Governed APPROVED platform copy (client-approved workbook import)
  // -------------------------------------------------------------------------

  const refreshApprovedCopy = async (requestScope = null) => {
    const advertiserId = requestScope?.advertiserId ?? effectiveSelectedAdvertiserId;
    const campaignId = requestScope?.campaignId ?? approvedCopyCampaignId;
    const scopeGeneration =
      requestScope?.scopeGeneration ?? advertiserScopeGenerationRef.current;
    const requestIsCurrent = () =>
      isAdvertiserScopeCurrent(scopeGeneration, advertiserId);
    if (!advertiserId || !campaignId) {
      setApprovedCopyPlatforms(null);
      return false;
    }
    setApprovedCopyLoading(true);
    setApprovedCopyError("");
    try {
      const res = await apiJson(
        `/api/creative-assets/approved-copy?advertiserId=${encodeURIComponent(advertiserId)}&campaignId=${encodeURIComponent(campaignId)}`
      );
      if (!requestIsCurrent()) return false;
      setApprovedCopyPlatforms(res?.data?.platforms || {});
      return true;
    } catch (error) {
      if (requestIsCurrent()) {
        setApprovedCopyPlatforms(null);
        setApprovedCopyError(error?.message || "Approved copy could not be loaded.");
      }
      return false;
    } finally {
      if (requestIsCurrent()) setApprovedCopyLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const advertiserId = effectiveSelectedAdvertiserId;
    const campaignId = approvedCopyCampaignId;
    const scopeGeneration = advertiserScopeGenerationRef.current;
    const requestIsCurrent = () =>
      !cancelled && isAdvertiserScopeCurrent(scopeGeneration, advertiserId);

    const run = async () => {
      setApprovedCopyPlatforms(null);
      setApprovedCopyError("");
      if (!advertiserId || !campaignId) return;
      setApprovedCopyLoading(true);
      try {
        const res = await apiJson(
          `/api/creative-assets/approved-copy?advertiserId=${encodeURIComponent(advertiserId)}&campaignId=${encodeURIComponent(campaignId)}`
        );
        if (requestIsCurrent()) setApprovedCopyPlatforms(res?.data?.platforms || {});
      } catch (error) {
        if (requestIsCurrent()) {
          setApprovedCopyError(error?.message || "Approved copy could not be loaded.");
        }
      } finally {
        if (requestIsCurrent()) setApprovedCopyLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [effectiveSelectedAdvertiserId, approvedCopyCampaignId, isAdvertiserScopeCurrent]);

  // Selecting a platform in COPY PLATFORM retargets the governed approved-copy
  // display (clearing any card-local override).
  useEffect(() => {
    setApprovedCopyViewPlatform("");
  }, [copyForm.platform]);

  const clearApprovedCopyImportState = () => {
    setApprovedCopyImportPreview(null);
    setApprovedCopyImportFile(null);
    setApprovedCopyOperatorEdits([]);
    setApprovedCopySelectedPlatforms([]);
    setApprovedCopyImportError("");
  };

  const handleImportApprovedCopyClick = () => {
    setApprovedCopyNotice("");
    setApprovedCopyImportError("");
    approvedCopyFileInputRef.current?.click();
  };

  const handleApprovedCopyFileChange = async (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    if (!file || !approvedCopyCampaignId) return;
    clearApprovedCopyImportState();
    setIsPreviewingApprovedCopy(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("advertiserId", String(effectiveSelectedAdvertiserId));
      formData.append("campaignId", String(approvedCopyCampaignId));
      const res = await apiWorkbookRequest("/api/creative-assets/approved-copy/preview", formData);
      const preview = res?.data || null;
      setApprovedCopyImportFile(file);
      setApprovedCopyImportPreview(preview);
      setApprovedCopySelectedPlatforms(
        (preview?.platforms || []).map((platform) => platform.platform)
      );
    } catch (error) {
      setApprovedCopyImportError(
        error?.message || "The workbook could not be parsed. Nothing was imported."
      );
    } finally {
      setIsPreviewingApprovedCopy(false);
    }
  };

  // Operator corrections are provenance-preserving: the workbook's original
  // value rides along so the commit records the edit as an operator change,
  // never as workbook content.
  const handleApprovedCopyOperatorEdit = (platform, path, value, workbookValue) => {
    setApprovedCopyOperatorEdits((previous) => {
      const rest = previous.filter(
        (edit) => !(edit.platform === platform && edit.path === path)
      );
      if (String(value) === String(workbookValue)) return rest;
      return [...rest, { platform, path, value, workbookValue }];
    });
  };

  const handleToggleApprovedCopyPlatform = (platform) => {
    setApprovedCopySelectedPlatforms((previous) =>
      previous.includes(platform)
        ? previous.filter((key) => key !== platform)
        : [...previous, platform]
    );
  };

  const handleCancelApprovedCopyImport = () => {
    clearApprovedCopyImportState();
    setApprovedCopyNotice("Import cancelled. Nothing was changed.");
  };

  const handleConfirmApprovedCopyImport = async () => {
    if (
      !approvedCopyImportFile ||
      !approvedCopyImportPreview ||
      !approvedCopySelectedPlatforms.length ||
      isCommittingApprovedCopy
    ) {
      return;
    }
    setIsCommittingApprovedCopy(true);
    setApprovedCopyImportError("");
    try {
      const formData = new FormData();
      formData.append("file", approvedCopyImportFile);
      formData.append("advertiserId", String(effectiveSelectedAdvertiserId));
      formData.append("campaignId", String(approvedCopyCampaignId));
      formData.append("platforms", JSON.stringify(approvedCopySelectedPlatforms));
      formData.append(
        "operatorEdits",
        JSON.stringify(
          approvedCopyOperatorEdits
            .filter((edit) => approvedCopySelectedPlatforms.includes(edit.platform))
            .map((edit) => ({ platform: edit.platform, path: edit.path, value: edit.value }))
        )
      );
      formData.append("expectedFileHash", approvedCopyImportPreview.fileHash || "");
      const res = await apiWorkbookRequest("/api/creative-assets/approved-copy/commit", formData);
      const committed = Array.isArray(res?.data?.versions) ? res.data.versions : [];
      clearApprovedCopyImportState();
      setApprovedCopyNotice(
        committed.length
          ? `Approved copy imported: ${committed
              .map((version) => `${version.platform} v${version.versionNumber}`)
              .join(", ")}.`
          : "Approved copy imported."
      );
      await refreshApprovedCopy();
    } catch (error) {
      setApprovedCopyImportError(
        error?.message || "Approved copy import failed. Nothing was committed."
      );
    } finally {
      setIsCommittingApprovedCopy(false);
    }
  };

  const handleSaveGovernedDraft = async () => {
    if (!activeId || !governedAuthoringEnabled || isSavingGovernedDraft) return;
    const requestScope = {
      advertiserId: effectiveSelectedAdvertiserId,
      assetId: activeId,
      scopeGeneration: advertiserScopeGenerationRef.current,
    };
    const requestIsCurrent = () =>
      isAdvertiserScopeCurrent(
        requestScope.scopeGeneration,
        requestScope.advertiserId
      );
    const governedDraftId = activeGovernedDraft?.id || null;
    setActionError("");
    setActionMessage("");
    setIsSavingGovernedDraft(true);
    try {
      const fields = buildGovernedDraftFieldsPayload();
      if (governedDraftId) {
        await apiJson(creativeAssetPath("versions", governedDraftId), {
          method: "PATCH",
          body: { fields },
        });
      } else {
        await apiJson(
          creativeAssetPath(requestScope.assetId, "versions", "draft"),
          {
            method: "POST",
            body: { fields },
          }
        );
      }
      if (!requestIsCurrent()) return;
      // Canonical reload: the backend's normalized draft is the authority,
      // never the local component state.
      await refreshGovernedVersions(requestScope);
      if (!requestIsCurrent()) return;
      setActionMessage(`Draft saved. ${GOVERNED_SAVE_TRUTH_NOTE}`);
    } catch (e) {
      if (requestIsCurrent()) {
        setActionError(String(e?.message || "Failed to save governed draft"));
      }
    } finally {
      if (requestIsCurrent()) setIsSavingGovernedDraft(false);
    }
  };

  const handleImageFormChange = (field, value) => {
    setImageForm((prev) => ({
      ...prev,
      [field]: field === "variations" ? Number(value || 3) : value,
    }));
  };

  // REVIEW MEDIA. Resolved by rule rather than by attempting the source: a
  // video is reviewed by its generated poster, never by streaming a master that
  // may be 751MB of ProRes the browser cannot decode. Approval never waits on
  // it — the inspected metadata beside it is what the decision is made from.
  const reviewMedia = useMemo(
    () =>
      resolveReviewMedia(activeAsset, {
        posterUrlFor: (asset) => creativeAssetPath(asset.id, "poster"),
        sourceUrlFor: (asset) => creativeAssetPath(asset.id, "stream"),
      }),
    [activeAsset]
  );
  const [reviewPosterFailed, setReviewPosterFailed] = useState(false);
  const [reviewPosterDecoding, setReviewPosterDecoding] = useState(false);
  const reviewMediaState = resolveReviewMediaState({
    media: reviewMedia,
    isDecoding: reviewPosterDecoding,
    failed: reviewPosterFailed,
  });
  const reviewMediaNotice = reviewMediaMessage(reviewMediaState, reviewMedia);

  // A failure on one asset must never poison the next.
  useEffect(() => {
    setReviewPosterFailed(false);
    setReviewPosterDecoding(Boolean(reviewMedia?.src));
  }, [reviewMedia?.src]);

  const handleSelectAsset = useCallback((itemId) => {
    if (!itemId || String(itemId) === String(activeId)) return;
    setActiveId(itemId);
    setDetail(null);
    setSelectedVersionId(null);
    setPreviewUrl("");
    setPreviewError("");
    setNewComment("");
    setActionError("");
    setActionMessage("");
  }, [activeId]);

  // Leaving an asset with unsaved text would silently discard it. Confirm
  // rather than block, so a reviewer is never trapped on one creative.
  const confirmLeavingActiveAsset = useCallback(() => {
    if (!shouldWarnBeforeLeavingAsset({ draftComment: newComment, savedComment: "" })) {
      return true;
    }
    if (typeof window === "undefined" || typeof window.confirm !== "function") return true;
    return window.confirm(
      "You have an unsaved comment on this creative. Leave it and lose that text?"
    );
  }, [newComment]);

  const goToReviewAsset = useCallback(
    (nextAssetId, { confirmUnsaved = true } = {}) => {
      if (!nextAssetId) return false;
      if (confirmUnsaved && !confirmLeavingActiveAsset()) return false;
      handleSelectAsset(nextAssetId);
      return true;
    },
    [confirmLeavingActiveAsset, handleSelectAsset]
  );

  const stepReviewQueue = useCallback(
    (direction) => {
      const target = reviewQueueNeighborId(reviewQueue, activeId, direction);
      if (!target) return;
      goToReviewAsset(target);
    },
    [activeId, goToReviewAsset, reviewQueue]
  );

  // APPROVE & NEXT. Persist the governance decision, confirm it succeeded, and
  // only then advance. A failed action must leave the reviewer on the creative
  // it failed for — advancing past an unrecorded decision is how a batch ends
  // up with a gap nobody notices.
  // Deliberately NOT memoized. `handleApprovalAction` is a plain function
  // redefined each render, so a useCallback here would take a dependency that
  // changes every time — the memo would never hold, and the hook lint rightly
  // rejects it. This is only ever called from a click handler, so there is
  // nothing to gain by memoizing it.
  const handleApprovalActionAndAdvance = async (action) => {
    const succeeded = await handleApprovalAction(action);
    if (succeeded === false) return;
    if (reviewAdvance.isFinish || !reviewAdvance.nextAssetId) return;
    goToReviewAsset(reviewAdvance.nextAssetId, { confirmUnsaved: false });
  };

  const handleOpenReviewModal = (itemId) => {
    setPreviewModalAssetId("");
    // The creative card that opened the sheet is where focus goes back to.
    reviewReturnFocusRef.current =
      typeof document !== "undefined" ? document.activeElement : null;
    if (itemId && String(itemId) !== String(activeId || "")) {
      handleSelectAsset(itemId);
    }
    setIsReviewModalOpen(true);
  };

  const handleCloseReviewModal = () => {
    setIsReviewModalOpen(false);
  };

  const handleOpenPreviewModal = useCallback((itemId) => {
    if (!itemId) return;
    setPreviewModalAssetId(String(itemId));
  }, []);

  const handleClosePreviewModal = useCallback(() => {
    previewModalSwipeRef.current = null;
    setPreviewModalAssetId("");
  }, []);

  const stepPreviewModal = useCallback(
    (direction) => {
      const target =
        direction === "previous" ? previousPreviewModalAsset : nextPreviewModalAsset;
      if (!target?.id) return;
      setPreviewModalAssetId(String(target.id));
      traceCreativePreview("preview_modal_navigation", {
        direction,
        fromAssetId: previewModalAssetId,
        toAssetId: target.id,
        index: direction === "previous" ? previewModalIndex - 1 : previewModalIndex + 1,
        total: filteredItems.length,
      });
    },
    [
      filteredItems.length,
      nextPreviewModalAsset,
      previousPreviewModalAsset,
      previewModalAssetId,
      previewModalIndex,
    ]
  );

  const handlePreviewModalPointerDown = useCallback((event) => {
    if (!previewModalAssetId || event.pointerType === "mouse") return;
    if (isCreativePreviewSwipeInteractiveTarget(event.target)) return;
    previewModalSwipeRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      at: Date.now(),
    };
  }, [previewModalAssetId]);

  const handlePreviewModalPointerEnd = useCallback(
    (event) => {
      const start = previewModalSwipeRef.current;
      previewModalSwipeRef.current = null;
      if (!start || start.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - start.x;
      const deltaY = event.clientY - start.y;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      if (absX < 56 || absX < absY * 1.35) return;

      if (deltaX < 0 && nextPreviewModalAsset) {
        stepPreviewModal("next");
        return;
      }
      if (deltaX > 0 && previousPreviewModalAsset) {
        stepPreviewModal("previous");
      }
    },
    [nextPreviewModalAsset, previousPreviewModalAsset, stepPreviewModal]
  );

  const handleMoveReviewSelection = useCallback((direction) => {
    const nextIndex = activeFilteredIndex + direction;
    if (nextIndex < 0 || nextIndex >= filteredItems.length) return;
    const nextItem = filteredItems[nextIndex];
    if (!nextItem?.id) return;
    handleSelectAsset(nextItem.id);
  }, [activeFilteredIndex, filteredItems, handleSelectAsset]);

  const resetCreateAssetForm = () => {
    setTitle("");
    setNotes("");
    setChannel("");
    setFormat("");
    setChangeSummary("");
    setFileObj(null);
    setFileName("");
    setFileObjs([]);
    setUploadState({ active: false, done: 0, total: 0 });
    setIsUploaderDragActive(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Accept one OR many files (file picker + drag-and-drop). Valid files are kept;
  // unsupported/oversized files are reported but never block the rest.
  const handlePendingUploadFiles = useCallback((input) => {
    setIsUploaderDragActive(false);

    const { accepted: typeAccepted, rejected } = partitionCreativeUploadFiles(input);

    // Size/type validation against the SERVER'S policy, before anything
    // transfers. An oversized file is refused here with its real size and the
    // real limit, so nobody waits through a doomed upload to learn that.
    const policyRejected = [];
    const accepted = [];
    for (const file of typeAccepted) {
      const verdict = validateFileAgainstPolicy(file, uploadPolicy);
      if (verdict.accepted) accepted.push(file);
      else policyRejected.push({ name: String(file?.name || "file"), message: verdict.message, code: verdict.code });
    }

    const rejectionCopy = [
      rejected.length ? describeRejectedCreativeUploads(rejected) : "",
      ...policyRejected.map((entry) => entry.message),
    ]
      .filter(Boolean)
      .join(" ");

    if (!accepted.length) {
      setFileObjs([]);
      setFileObj(null);
      setFileName("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (rejectionCopy) {
        setActionMessage("");
        setActionError(rejectionCopy);
      }
      return;
    }

    setActionMessage("");
    setActionError(rejectionCopy);
    setFileObjs(accepted);
    setFileObj(accepted[0]);
    setFileName(String(accepted[0]?.name || ""));
  }, [uploadPolicy]);

  const handleUploaderBrowse = useCallback(() => {
    if (uploaderDisabled) return;
    fileInputRef.current?.click();
  }, [uploaderDisabled]);

  const handleUploaderKeyDown = useCallback(
    (event) => {
      if (uploaderDisabled) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleUploaderBrowse();
      }
    },
    [handleUploaderBrowse, uploaderDisabled]
  );

  const handleUploaderDrop = useCallback(
    (event) => {
      event.preventDefault();
      if (uploaderDisabled) {
        setIsUploaderDragActive(false);
        return;
      }
      handlePendingUploadFiles(event.dataTransfer?.files || null);
    },
    [handlePendingUploadFiles, uploaderDisabled]
  );

  // Upload a single file as its own creative asset. Each call is independent so
  // a failure on one file does not affect the others in a bulk batch.
  // Fetch the governed upload policy once. The server still enforces every
  // limit, so a slow policy fetch only delays the client-side courtesy check.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiJson(creativeAssetPath("upload-policy"));
        if (!cancelled && res?.data) setUploadPolicy(res.data);
      } catch (_error) {
        if (!cancelled) setUploadPolicy(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Session-based upload API. Creative bytes go straight from the browser to
  // Google Drive; these calls carry only metadata. Errors keep the backend's
  // stable `code` and support-safe session id so the UI can state a real
  // reason instead of a generic "Upload failed".
  const uploadSessionApi = useMemo(() => {
    const post = async (path, body) => {
      const token = getAuthToken();
      const res = await fetch(`${safeBase(API_BASE_URL)}${path}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      const text = await res.text();
      let json = null;
      try {
        json = text ? JSON.parse(text) : null;
      } catch {
        json = null;
      }
      if (!res.ok) {
        const error = new Error(json?.error || `HTTP ${res.status}`);
        error.code = json?.code || null;
        error.uploadSessionId = json?.uploadSessionId || null;
        throw error;
      }
      return json;
    };
    return {
      createUploadSession: async (payload) =>
        (await post(creativeAssetPath("upload-sessions"), payload))?.data,
      finalizeUploadSession: async ({ sessionId, advertiserId }) =>
        post(creativeAssetPath("upload-sessions", sessionId, "finalize"), { advertiserId }),
      // Authoritative upload state. Must be a SERVER call — Google omits CORS
      // headers from the terminal response, so the browser cannot tell a
      // finished upload from an interrupted one on its own.
      getUploadSessionState: async ({ sessionId, advertiserId }) =>
        (await post(creativeAssetPath("upload-sessions", sessionId, "state"), { advertiserId }))?.data,
    };
  }, []);

  // Truthful upload summary: duplicates are successes but NOT creations, so
  // the banner must never say "Creative asset created." for them. Mixed
  // batches report each outcome; per-file cards stay independently truthful.
  const buildUploadOutcomeMessage = ({ newCount, duplicateCount }) => {
    if (newCount > 0 && duplicateCount === 0) {
      return newCount > 1 ? `Uploaded ${newCount} creatives.` : "Creative asset created.";
    }
    if (duplicateCount > 0 && newCount === 0) {
      return duplicateCount > 1
        ? `Duplicates found — no new assets were created (${duplicateCount} files already exist).`
        : "Duplicate found — no new asset was created.";
    }
    if (newCount > 0 && duplicateCount > 0) {
      return `Uploaded ${newCount} new creative${newCount > 1 ? "s" : ""}; ${duplicateCount} duplicate${duplicateCount > 1 ? "s" : ""} — no new asset created for duplicates.`;
    }
    return "";
  };

  const INTAKE_STATUS_PRESENTATION = {
    legacy_unprocessed: { label: "Uploaded (pre-verification)", tone: "neutral", note: "Uploaded before server-side inspection existed." },
    received: { label: "Received", tone: "pending", note: "Upload received." },
    storing: { label: "Storing", tone: "pending", note: "Being written to governed storage." },
    stored: { label: "Stored — awaiting inspection", tone: "pending", note: "Identity verified. Server-side inspection has not run yet." },
    processing: { label: "Inspecting", tone: "pending", note: "Server-side media inspection is running." },
    needs_metadata: { label: "Needs metadata", tone: "warning", note: "Inspection could not establish all required facts." },
    processed: { label: "Inspected", tone: "success", note: "Server-verified. Review and approval are still separate steps." },
    processing_failed: { label: "Inspection failed", tone: "danger", note: "Processing failed. It can be retried safely." },
    quarantined: { label: "Quarantined", tone: "danger", note: "File-safety checks quarantined this file. It cannot be approved or trafficked." },
  };

  const intakePresentation = (status) =>
    INTAKE_STATUS_PRESENTATION[String(status || "legacy_unprocessed")] ||
    INTAKE_STATUS_PRESENTATION.legacy_unprocessed;

  useEffect(() => {
    let cancelled = false;
    const advertiserId = effectiveSelectedAdvertiserId;
    const scopeGeneration = advertiserScopeGenerationRef.current;
    const requestIsCurrent = () =>
      !cancelled &&
      isAdvertiserScopeCurrent(scopeGeneration, advertiserId);
    setIntakeEvidence(null);
    if (!activeId) return undefined;
    (async () => {
      try {
        const res = await apiJson(creativeAssetPath(activeId, "intake"));
        if (requestIsCurrent()) setIntakeEvidence(res?.data || null);
      } catch (_e) {
        if (requestIsCurrent()) setIntakeEvidence(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activeId,
    effectiveSelectedAdvertiserId,
    isAdvertiserScopeCurrent,
  ]);

  const handleIntakeRetry = async (assetId) => {
    const requestScope = {
      advertiserId: effectiveSelectedAdvertiserId,
      scopeGeneration: advertiserScopeGenerationRef.current,
    };
    const requestIsCurrent = () =>
      isAdvertiserScopeCurrent(
        requestScope.scopeGeneration,
        requestScope.advertiserId
      );
    setActionError("");
    setActionMessage("");
    try {
      await apiJson(creativeAssetPath(assetId, "intake", "retry"), { method: "POST", body: {} });
      if (!requestIsCurrent()) return;
      setActionMessage("Inspection retry requested. The original file is reused — nothing is re-uploaded.");
      await refreshActiveDetail(requestScope);
    } catch (e) {
      if (requestIsCurrent()) {
        setActionError(String(e?.message || "Failed to retry inspection"));
      }
    }
  };

  /**
   * Follow the just-stored creatives through server-side inspection.
   *
   * The transfer ending is not the end of the story — the intake worker still
   * has to validate the file before it is genuinely ready. Watching that is
   * what lets the interface say INSPECTING and then hand over cleanly,
   * instead of dropping the user at a bar parked on 100% with nothing
   * visibly happening.
   *
   * Bounded on purpose: it reports what it observes and then stops. It never
   * polls indefinitely, and it never claims inspection finished on a timeout —
   * the asset's own governed state takes over at that point.
   */
  const watchCreativeInspection = ({ advertiserId, requestScope, assetIds, attempt = 0 }) => {
    const MAX_ATTEMPTS = 20;
    const INTERVAL_MS = 4000;
    const SETTLED_DWELL_MS = 2600;
    const watched = (assetIds || []).filter(Boolean).map(String);
    if (!watched.length) return;

    uploadInspectionTimerRef.current = setTimeout(async () => {
      if (!requestScope.isCurrent()) return;
      let rows = [];
      try {
        const res = await apiJson(creativeAssetListPath(advertiserId));
        rows = filterClientVisibleCreativeAssets(res?.data);
      } catch (_error) {
        rows = [];
      }
      if (!requestScope.isCurrent()) return;

      const statusById = new Map(
        (rows || [])
          .filter((row) => row?.id != null)
          .map((row) => [String(row.id), row?.intake?.status || null])
      );
      setUploadFileStates((prev) =>
        withIntakeStages(prev, (entry) => statusById.get(String(entry.assetId)) || null)
      );

      const settled = watched.every((assetId) => {
        const status = statusById.get(assetId);
        return Boolean(status) && stageForIntakeStatus(status) === UPLOAD_STAGES.COMPLETE;
      });

      if (settled || attempt + 1 >= MAX_ATTEMPTS) {
        // Let the finished state actually be seen, then hand the creative over
        // to the library rather than leaving a completed bar on screen.
        uploadInspectionTimerRef.current = setTimeout(() => {
          if (!requestScope.isCurrent()) return;
          // Failures stay on screen with their retry; only a clean batch is
          // handed over to the library.
          setUploadFileStates((prev) =>
            prev.some((entry) => entry.stage === UPLOAD_STAGES.FAILED) ? prev : []
          );
        }, SETTLED_DWELL_MS);
        return;
      }
      watchCreativeInspection({ advertiserId, requestScope, assetIds: watched, attempt: attempt + 1 });
    }, INTERVAL_MS);
  };

  useEffect(
    () => () => {
      if (uploadInspectionTimerRef.current) clearTimeout(uploadInspectionTimerRef.current);
    },
    []
  );

  const handleCreateAsset = async (filesToUpload = null) => {
    if ((!canCreateAsset && !filesToUpload) || uploadState.active) return;
    const requestScope = {
      advertiserId: effectiveSelectedAdvertiserId,
      scopeGeneration: advertiserScopeGenerationRef.current,
      isCurrent: () =>
        isAdvertiserScopeCurrent(
          requestScope.scopeGeneration,
          requestScope.advertiserId
        ),
    };
    if (!requestScope.isCurrent()) return;
    setActionError("");
    setActionMessage("");

    const pendingFiles = filesToUpload || (fileObjs.length ? fileObjs : fileObj ? [fileObj] : []);
    if (!pendingFiles.length) return;
    const isBulk = pendingFiles.length > 1;
    const batchId = uploadBatchIdRef.current || `batch-${Date.now()}`;
    uploadBatchIdRef.current = batchId;

    if (uploadInspectionTimerRef.current) {
      clearTimeout(uploadInspectionTimerRef.current);
      uploadInspectionTimerRef.current = null;
    }

    setUploadState({ active: true, done: 0, total: pendingFiles.length });
    setUploadFileStates((prev) => {
      const attempted = new Set(pendingFiles.map((file) => String(file?.name || "file")));
      // Retrying ONE failed creative must not erase the other failures from
      // view. They are appended AFTER this run's rows so the transport's
      // positional progress indices still address the right entries.
      const retained = prev.filter(
        (entry) => entry.stage === UPLOAD_STAGES.FAILED && !attempted.has(String(entry.name))
      );
      const seeded = pendingFiles.map((file) => ({
        name: String(file?.name || "file"),
        size: Number(file?.size) || 0,
        // Bounded concurrency means most of a six-file selection starts out
        // waiting. Saying QUEUED is what keeps those files from reading as
        // frozen.
        stage: UPLOAD_STAGES.QUEUED,
        loaded: 0,
        total: Number(file?.size) || 0,
        samples: [],
        assetId: null,
        intakeStatus: null,
        error: null,
      }));
      return [...seeded, ...retained];
    });

    const results = await uploadCreativeBatch({
      files: pendingFiles,
      advertiserId: requestScope.advertiserId,
      titleFor: (file, index) =>
        isBulk
          ? deriveTitleFromFilename(String(file?.name || "")) || `Creative ${index + 1}`
          : effectiveUploadTitle,
      notes: notes.trim(),
      batchId,
      policy: uploadPolicy,
      metadataFor: async (file) => {
        try {
          return await extractCreativeAssetMetadata(file);
        } catch (_error) {
          return null;
        }
      },
      api: uploadSessionApi,
      onProgress: (update) => {
        if (!requestScope.isCurrent()) return;
        setUploadFileStates((prev) => {
          const next = [...prev];
          const entry = next[update.index];
          if (!entry) return prev;
          const stage = stageForPhase(update.phase);
          next[update.index] = {
            ...entry,
            stage,
            loaded: update.loaded,
            // Samples exist only so the rate/ETA estimator has history. It
            // withholds an estimate until that history is long enough to be
            // stable, so an early transfer shows no number rather than a
            // number that would visibly lurch.
            samples: isByteMeasurable(stage)
              ? recordTransferSample(entry.samples, { at: Date.now(), loaded: update.loaded })
              : entry.samples,
          };
          return next;
        });
        if (update.phase === UPLOAD_PHASES.DONE || update.phase === UPLOAD_PHASES.FAILED) {
          setUploadState((prev) => ({ ...prev, done: Math.min(prev.total, prev.done + 1) }));
        }
      },
    });

    if (!requestScope.isCurrent()) return;

    const succeeded = [];
    const failures = [];
    const perFileResults = [];
    let lastCreatedId = null;

    results.forEach((result, index) => {
      const fileLabel = result?.name || String(pendingFiles[index]?.name || `file ${index + 1}`);
      if (result?.ok && result.duplicate?.assetId) {
        // Exact duplicate: the backend kept the existing asset and stored
        // nothing new. Truthful outcome with a deep link, not a failure.
        perFileResults.push({
          name: fileLabel,
          outcome: "duplicate",
          assetId: result.duplicate.assetId,
          detail: result.duplicate.message || "This exact file already exists.",
        });
        succeeded.push(fileLabel);
      } else if (result?.ok) {
        if (result.assetId) lastCreatedId = result.assetId;
        perFileResults.push({
          name: fileLabel,
          outcome: "stored",
          assetId: result.assetId,
          detail:
            "Stored and identity-verified. Server inspection, review, and approval are still ahead.",
        });
        succeeded.push(fileLabel);
      } else {
        const reason = result?.message || "This file could not be uploaded.";
        perFileResults.push({ name: fileLabel, outcome: "failed", detail: reason });
        failures.push({
          name: fileLabel,
          title: result?.title,
          message: reason,
          code: result?.code,
          action: result?.action,
          confirmedBytes: result?.confirmedBytes,
          total: result?.total,
          uploadSessionId: result?.uploadSessionId,
        });
      }
    });

    setUploadResults(perFileResults);
    setUploadState({ active: false, done: 0, total: 0 });

    // Bind each row to its outcome BEFORE any stage advances, so a failed file
    // carries the reason its own copy is derived from and a stored file
    // carries the asset whose inspection is about to be watched.
    setUploadFileStates((prev) =>
      prev.map((entry, index) => {
        // Rows past this run's length are retained failures from an earlier
        // attempt; they keep their own outcome.
        if (index >= results.length) return entry;
        const result = results[index];
        if (result?.ok) {
          return {
            ...entry,
            stage: UPLOAD_STAGES.FINALIZING,
            loaded: entry.total,
            assetId: result.assetId || result.duplicate?.assetId || null,
            error: null,
          };
        }
        // The failure record goes through #145's normalizer, so the row
        // inherits its evidence-based fields — the interruption title, the
        // resume-vs-retry action chosen from what storage confirmed, and the
        // durable byte count. The progress surface renders them; nothing that
        // list used to show is lost.
        const [failureDetail] = buildFailureDetails([
          {
            name: entry.name,
            title: result?.title,
            message: result?.message,
            code: result?.code,
            action: result?.action,
            confirmedBytes: result?.confirmedBytes,
            total: result?.total,
            uploadSessionId: result?.uploadSessionId,
          },
        ]);
        return {
          ...entry,
          stage: UPLOAD_STAGES.FAILED,
          // Roll the bar back to what storage actually kept. A bar sitting at
          // 99% beside "interrupted" was the exact incoherence #145 removed.
          loaded:
            typeof result?.confirmedBytes === "number" ? result.confirmedBytes : entry.loaded,
          error: failureDetail,
        };
      })
    );

    // Refresh the library once after the whole batch so successful assets appear
    // without a full page reload (eligibility recomputes off the items effect).
    if (succeeded.length) {
      try {
        const listRes = await apiJson(
          creativeAssetListPath(requestScope.advertiserId)
        );
        if (!requestScope.isCurrent()) return;
        const rows = filterClientVisibleCreativeAssets(listRes?.data);
        setItems(rows);
        setDetail(null);
        setSelectedVersionId(null);
        if (lastCreatedId) setActiveId(lastCreatedId);
        else if (rows[0]?.id) setActiveId(rows[0].id);
      } catch (refreshError) {
        if (requestScope.isCurrent()) {
          setActionError(
            `Uploaded, but refreshing the library failed: ${String(
              refreshError?.message || ""
            )}`.trim()
          );
        }
      }
    }
    if (!requestScope.isCurrent()) return;

    // Stored creatives are now watched through inspection; failed ones stay on
    // screen with their own reason and their own retry.
    const inspectableAssetIds = results.filter((r) => r?.ok).map((r) => r?.assetId || r?.duplicate?.assetId);
    setUploadFileStates((prev) => withStage(prev, UPLOAD_STAGES.INSPECTING));
    watchCreativeInspection({
      advertiserId: requestScope.advertiserId,
      requestScope,
      assetIds: inspectableAssetIds,
    });

    // Keep ONLY the failed files selected so a retry can never re-upload
    // something that already succeeded. Files this run did not attempt (a
    // single-file retry out of several failures) keep their place.
    const failedNames = new Set(failures.map((f) => f.name));
    const attemptedNames = new Set(pendingFiles.map((file) => String(file?.name || "")));
    const selectionPool = fileObjs.length ? fileObjs : fileObj ? [fileObj] : [];
    const remaining = selectionPool.filter((file) => {
      const name = String(file?.name || "");
      return attemptedNames.has(name) ? failedNames.has(name) : true;
    });
    setFileObjs(remaining);
    setFileObj(remaining[0] || null);
    setFileName(remaining.length === 1 ? String(remaining[0]?.name || "") : "");
    if (fileInputRef.current && !remaining.length) fileInputRef.current.value = "";

    const newCount = perFileResults.filter((r) => r.outcome === "stored").length;
    const duplicateCount = perFileResults.filter((r) => r.outcome === "duplicate").length;
    if (!failures.length) {
      resetCreateAssetForm();
      uploadBatchIdRef.current = null;
      setActionMessage(buildUploadOutcomeMessage({ newCount, duplicateCount }));
    } else {
      // Aggregation is built by the shared, tested helper. The previous copy
      // claimed "all files" failed whenever anything failed — untrue whenever
      // part of a batch succeeded — and could emit dangling fragments.
      const summary = buildUploadSummary({
        succeeded,
        failed: failures,
        total: pendingFiles.length,
      });
      if (succeeded.length) {
        setActionMessage(
          [summary.message, duplicateCount ? `${duplicateCount} duplicate${duplicateCount > 1 ? "s" : ""} — no new asset created.` : ""]
            .filter(Boolean)
            .join(" ")
        );
        setActionError("");
      } else {
        setActionMessage("");
        setActionError(summary.message);
      }
    }
  };

  /** Retry ONLY the files that failed. Completed uploads are never redone. */
  const handleRetryFailedUploads = async () => {
    if (uploadState.active) return;
    const pendingFiles = fileObjs.length ? fileObjs : fileObj ? [fileObj] : [];
    if (!pendingFiles.length) return;
    await handleCreateAsset(pendingFiles);
  };

  /**
   * Retry ONE failed creative.
   *
   * Scoped to the single file the user pointed at: nothing that already
   * succeeded is re-sent, and the other failures keep their rows and their
   * own retry.
   */
  const handleRetrySingleUpload = async (entry) => {
    if (uploadState.active) return;
    const pendingFiles = fileObjs.length ? fileObjs : fileObj ? [fileObj] : [];
    const file = pendingFiles.find((candidate) => String(candidate?.name || "") === String(entry?.name || ""));
    if (!file) return;
    await handleCreateAsset([file]);
  };

  const handleGenerateImages = async () => {
    if (!effectiveSelectedAdvertiserId || !isCreativeUser || !String(imageForm.prompt || "").trim()) return;
    const requestScope = {
      advertiserId: effectiveSelectedAdvertiserId,
      scopeGeneration: advertiserScopeGenerationRef.current,
      isCurrent: () =>
        isAdvertiserScopeCurrent(
          requestScope.scopeGeneration,
          requestScope.advertiserId
        ),
    };
    if (!requestScope.isCurrent()) return;
    setActionError("");
    setActionMessage("");
    setIsGeneratingImages(true);

    try {
      const res = await apiJson(creativeAssetPath("generate-image"), {
        method: "POST",
        body: {
          advertiserId: requestScope.advertiserId,
          advertiserName: resolvedAdvertiser?.name || resolvedAdvertiser?.advertiser || "",
          title: imageForm.title,
          prompt: imageForm.prompt,
          aspectRatio: imageForm.aspectRatio,
          styleMode: imageForm.styleMode,
          variations: imageForm.variations,
          channel: imageForm.channel,
          format: imageForm.format,
          notes: imageForm.notes,
        },
      });

      if (!requestScope.isCurrent()) return;
      const firstCreatedId = res?.data?.createdAssets?.[0]?.asset?.id || null;
      setActionMessage(
        `Generated ${res?.data?.createdAssets?.length || 0} creative image variation${res?.data?.createdAssets?.length === 1 ? "" : "s"}.`
      );
      setImageForm((prev) => ({
        ...createDefaultImageGenerationForm(),
        channel: prev.channel,
        format: prev.format,
      }));
      const createdIds = new Set(
        Array.isArray(res?.data?.createdAssets)
          ? res.data.createdAssets
              .map((entry) => entry?.asset?.id)
              .filter((value) => value !== null && value !== undefined)
              .map((value) => String(value))
          : []
      );
      await refreshActiveDetail(requestScope).catch(() => {});
      if (!requestScope.isCurrent()) return;
      const listRes = await apiJson(
        creativeAssetListPath(requestScope.advertiserId)
      );
      if (!requestScope.isCurrent()) return;
      const refreshedItems = filterClientVisibleCreativeAssets(listRes?.data);
      setItems(refreshedItems);
      if (firstCreatedId) setActiveId(firstCreatedId);
      else {
        const latestCreated = refreshedItems.find((item) => createdIds.has(String(item?.id || "")));
        if (latestCreated?.id) setActiveId(latestCreated.id);
      }
    } catch (e) {
      if (requestScope.isCurrent()) {
        setActionError(String(e?.message || "Image generation failed"));
      }
    } finally {
      if (requestScope.isCurrent()) setIsGeneratingImages(false);
    }
  };

  useEffect(() => {
    if (!isReviewModalOpen) return undefined;

    const onKeyDown = (event) => {
      // Focus stays inside the sheet. Only the two wrap points are claimed, so
      // the reviewer's natural tab order through the sheet is untouched.
      if (event.key === "Tab") {
        const wrapTarget = resolveFocusWrapTarget(
          reviewSheetRef.current,
          document.activeElement,
          event.shiftKey
        );
        if (wrapTarget) {
          event.preventDefault();
          wrapTarget.focus();
        }
        return;
      }
      // Intent resolution lives in creativeReviewQueue so the rules — arrows
      // never steal a caret, Escape always escapes, modified keystrokes belong
      // to the browser — are testable without mounting this page.
      const action = resolveReviewKeyAction(event);
      if (!action) {
        // Up/Down kept as the pre-existing alternative binding.
        const tag = String(event.target?.tagName || "").toLowerCase();
        const isTypingField =
          tag === "input" || tag === "textarea" || tag === "select" || event.target?.isContentEditable;
        if (isTypingField) return;
        if (event.key === "ArrowUp" && canGoPreviousAsset) {
          event.preventDefault();
          handleMoveReviewSelection(-1);
        }
        if (event.key === "ArrowDown" && canGoNextAsset) {
          event.preventDefault();
          handleMoveReviewSelection(1);
        }
        return;
      }
      if (action === REVIEW_QUEUE_ACTIONS.CLOSE) {
        event.preventDefault();
        handleCloseReviewModal();
        return;
      }
      event.preventDefault();
      stepReviewQueue(action);
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [
    canGoNextAsset,
    canGoPreviousAsset,
    isReviewModalOpen,
    handleMoveReviewSelection,
    stepReviewQueue,
  ]);

  // OPEN/CLOSE LIFECYCLE. Deliberately depends on nothing but the open flag:
  // walking the review queue changes the neighbour guards several times per
  // session, and if this ran on those changes it would drop focus back to the
  // table and flicker the page chrome in the middle of a review.
  useEffect(() => {
    if (!isReviewModalOpen) return undefined;

    const unlockDocumentScroll = lockCreativeStudioDocumentScroll();
    // Suppresses the floating Ask / Eden / previous-page chrome, which is fixed
    // far above this modal and lives outside the page root, so it cannot be
    // layered away from here. Hiding it also takes it out of the a11y tree.
    const restorePageChrome = markCreativeReviewOpen(document);
    const revealBackground = hideBackgroundFromAssistiveTech(reviewSheetRef.current);
    const returnFocusTo = reviewReturnFocusRef.current;

    // Close is the safest landing spot: it is the one control that is always
    // present and always enabled, whatever state the creative is in.
    if (reviewCloseButtonRef.current) reviewCloseButtonRef.current.focus();

    return () => {
      unlockDocumentScroll();
      restorePageChrome();
      revealBackground();
      restoreFocus(returnFocusTo);
    };
  }, [isReviewModalOpen]);

  // BOUNDED PREFETCH. Exactly one asset ahead, metadata and poster only. The
  // masters in this library run to 751MB; prefetching one would reintroduce the
  // stall this change removes, so source media is never a prefetch target.
  useEffect(() => {
    if (!isReviewModalOpen) return undefined;
    const { assetIds } = resolveReviewPrefetch(reviewQueue, activeId);
    if (!assetIds.length) return undefined;
    let cancelled = false;
    const image = typeof Image !== "undefined" ? new Image() : null;
    if (image) image.src = creativeAssetPath(assetIds[0], "poster");
    return () => {
      cancelled = true;
      // Detach so a queue move cannot leave a decode running against a row the
      // reviewer has already left.
      if (image && cancelled) image.src = "";
    };
  }, [activeId, isReviewModalOpen, reviewQueue]);

  // Preview Window owns pointer interaction while open. The global Logout
  // control lives outside this page, like the review sheet's floating chrome.
  useEffect(() => {
    if (!isPreviewModalOpen) return undefined;
    document.body.setAttribute("data-creative-preview-open", "true");
    return () => document.body.removeAttribute("data-creative-preview-open");
  }, [isPreviewModalOpen]);

  useEffect(() => {
    if (!previewModalAssetId) return undefined;

    const handlePreviewKeyDown = (event) => {
      const targetTagName = String(event.target?.tagName || "").toLowerCase();
      const isTypingTarget =
        event.target?.isContentEditable ||
        ["input", "textarea", "select"].includes(targetTagName);

      if (event.key === "Escape") {
        setPreviewModalAssetId("");
        return;
      }
      if (isTypingTarget) return;
      if (event.key === "ArrowLeft" && previousPreviewModalAsset) {
        event.preventDefault();
        stepPreviewModal("previous");
      }
      if (event.key === "ArrowRight" && nextPreviewModalAsset) {
        event.preventDefault();
        stepPreviewModal("next");
      }
    };

    window.addEventListener("keydown", handlePreviewKeyDown);
    return () => window.removeEventListener("keydown", handlePreviewKeyDown);
  }, [nextPreviewModalAsset, previewModalAssetId, previousPreviewModalAsset, stepPreviewModal]);

  return (
    <div className="creativestudio-scope smc-page-container creativetrafficking-suite creativestudio-trafficking-framework">
      <div className="smc-page-content">
        <div className="creativestudio-header-logo-row">
          <CortexDashboardBrandLink
            linkTestId="creativestudio-brand-dashboard-link"
            logoTestId="creativestudio-brand-logo"
          />
        </div>
        <div className="cphere-governed-hero-selector-stack">
          <div className="smc-hero-container">
            <div className="smc-hero-pill">
              <span className="smc-hero-pill-label cphere-hero-pill-wordmark">Creative</span>
              <span className="smc-hero-pill-tag cphere-hero-pill-tag">Studio</span>
            </div>
            <div className="smc-hero-shimmer-bar" />
          </div>

          <div className="cphere-governed-advertiser-selector-stage">
            <AdvertiserIdentityTrigger
              advertiser={resolvedAdvertiser}
              advertiserName={selectedAdvertiserName}
              apiBase={API_BASE_URL}
              disabled={selectorDisabled || advertiserSelectionBlocked}
              empty={selectorEmpty}
              loading={advertisersLoading}
              modalOpen={advertiserSelectorOpen}
              onClick={openAdvertiserSelector}
            />
          </div>
        </div>

        {advertiserSelectorOpen ? (
          <GovernedAdvertiserSelectorModal
            advertisers={advertisers}
            selectedAdvertiserId={selectedAdvertiserId}
            selectedAdvertiserName={selectedAdvertiserName}
            apiBase={API_BASE_URL}
            onSelect={handleGovernedAdvertiserSelect}
            onClose={closeAdvertiserSelector}
          />
        ) : null}

        <input
          ref={fileInputRef}
          className="creativestudio-fileinput-hidden"
          type="file"
          multiple
          accept={CREATIVE_UPLOAD_ACCEPT_ATTR}
          onChange={(event) => handlePendingUploadFiles(event.target.files)}
        />

        <details className="creativetrafficking-disclosure creativetrafficking-secondary-disclosure--outer" open>
          <summary className="creativetrafficking-disclosure-summary">
            <span className="creativetrafficking-disclosure-summary-label">Filters</span>
            {(searchQuery || statusFilter !== "all" || channelFilter !== "all" || formatFilter !== "all") ? <span className="creativetrafficking-disclosure-badge">Active</span> : null}
          </summary>
          <div className="creativetrafficking-disclosure-body">
            <div className="creativestudio-filter-row">
              <label className="creativestudio-filter-cell creativestudio-filter-cell--search">
                <span className="creativestudio-filter-label">Search</span>
                <input className="creativeoptimus-input creativestudio-filter-control" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Search title, platform, format, or version" />
              </label>
              <label className="creativestudio-filter-cell">
                <span className="creativestudio-filter-label">Status</span>
                <div className="creativestudio-filter-select-shell">
                  <select className="creativeoptimus-select creativestudio-filter-control" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                    <option value="all">All statuses</option>
                    {CREATIVE_STATUS_GROUPS.map((group) => <option key={group.key} value={group.key}>{group.label}</option>)}
                  </select>
                </div>
              </label>
              <label className="creativestudio-filter-cell">
                <span className="creativestudio-filter-label">Platform</span>
                <div className="creativestudio-filter-select-shell">
                  <select className="creativeoptimus-select creativestudio-filter-control" value={channelFilter} onChange={(event) => setChannelFilter(event.target.value)}>
                    <option value="all">All platforms</option>
                    {platformOptions.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
                  </select>
                </div>
              </label>
              <label className="creativestudio-filter-cell">
                <span className="creativestudio-filter-label">Format</span>
                <div className="creativestudio-filter-select-shell">
                  <select className="creativeoptimus-select creativestudio-filter-control" value={formatFilter} onChange={(event) => setFormatFilter(event.target.value)}>
                    <option value="all">All formats</option>
                    {formatOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </div>
              </label>
            </div>
            <div className="creativestudio-filter-row creativestudio-filter-row--upload">
              <div className="creativestudio-filter-cell creativestudio-filter-cell--nav">
                <span className="creativestudio-filter-label">Upload</span>
                <div className="creativestudio-filter-upload-stack">
                  {/* The upload surface owns the whole journey: stage, honest
                      byte progress, per-file outcome, and per-file retry. */}
                  {uploadFileStates.length > 0 && (
                    <CreativeUploadProgress
                      entries={uploadFileStates}
                      onRetryFile={handleRetrySingleUpload}
                      onRetryAll={handleRetryFailedUploads}
                    />
                  )}

                  {/* Once a transfer starts the drop zone gives way to it —
                      telling someone to drag and drop while their 716MB
                      master is mid-flight is instruction they cannot use. */}
                  {!uploadInFlight && (
                  <div
                    className={`creativestudio-filter-uploader${isUploaderDragActive ? " is-dragging" : ""}${fileObj ? " has-file" : ""}${uploaderDisabled ? " is-disabled" : ""}`}
                    role="button"
                    tabIndex={uploaderDisabled ? -1 : 0}
                    aria-disabled={uploaderDisabled}
                    onClick={handleUploaderBrowse}
                    onKeyDown={handleUploaderKeyDown}
                    onDragEnter={(event) => {
                      event.preventDefault();
                      if (!uploaderDisabled) setIsUploaderDragActive(true);
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      if (!uploaderDisabled) setIsUploaderDragActive(true);
                    }}
                    onDragLeave={(event) => {
                      if (event.currentTarget.contains(event.relatedTarget)) return;
                      setIsUploaderDragActive(false);
                    }}
                    onDrop={handleUploaderDrop}
                  >
                    <span className="creativestudio-filter-uploader-eyebrow">
                      {uploaderDisabled ? "Select advertiser" : isUploaderDragActive ? "Drop to upload" : "Drag & drop"}
                    </span>
                    <span className="creativestudio-filter-uploader-title">
                      {pendingUploadCount > 1
                        ? `${pendingUploadCount} files selected`
                        : fileName || (uploaderDisabled ? "Choose advertiser first" : "Upload creatives here")}
                    </span>
                    <div className="creativestudio-filter-uploader-copy" aria-live="polite">
                      {pendingUploadCount > 0 ? (
                        <>
                          <span className="creativestudio-filter-uploader-copy-line">
                            {pendingUploadCount > 1
                              ? `Ready to upload ${pendingUploadCount} creatives`
                              : `Ready for ${effectiveUploadTitle || fileName}`}
                          </span>
                          {/* Product language, not storage language: where a
                              creative lands is Creative Studio. The provider's
                              folder is an implementation detail nobody here
                              can act on. */}
                          <span className="creativestudio-filter-uploader-copy-line creativestudio-filter-uploader-copy-line--subtle">
                            {describeDropZone({ pendingCount: pendingUploadCount })}
                          </span>
                        </>
                      ) : uploaderDisabled ? (
                        <>
                          <span className="creativestudio-filter-uploader-copy-line">
                            Choose an advertiser to unlock uploads.
                          </span>
                          <span className="creativestudio-filter-uploader-copy-line creativestudio-filter-uploader-copy-line--subtle">
                            Brand-safe files route into that advertiser&apos;s creative folder automatically.
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="creativestudio-filter-uploader-copy-line">Click to browse or drop files</span>
                          <span className="creativestudio-filter-uploader-copy-line creativestudio-filter-uploader-copy-line--subtle">
                            Select one or many. {CREATIVE_UPLOAD_ACCEPT_HINT}.
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  )}

                  {/* The submit control never becomes an unreadable disabled
                      status. While a batch is in flight it is replaced by a
                      plain statement of what is happening; the actionable
                      control returns when there is something to act on. */}
                  {uploadInFlight ? (
                    <p className="creativestudio-filter-upload-status" role="status">
                      {describeUploadAction({ active: true })}
                    </p>
                  ) : (
                    <button
                      className="creativeoptimus-primary-btn creativestudio-filter-upload-submit"
                      type="button"
                      onClick={
                        pendingUploadCount > 0
                          ? failedUploadCount > 0
                            ? handleRetryFailedUploads
                            : () => handleCreateAsset()
                          : handleUploaderBrowse
                      }
                      disabled={pendingUploadCount > 0 ? !canCreateAsset : uploaderDisabled}
                    >
                      {describeUploadAction({
                        pendingCount: pendingUploadCount,
                        failedCount: failedUploadCount,
                      })}
                    </button>
                  )}

                  {/* Per-file progress, per-file outcome, and per-file retry
                      all now live in the shared upload surface above — the
                      same component Quantum renders, so the two portals cannot
                      drift in wording or behaviour. */}
                </div>
              </div>
            </div>
          </div>
        </details>

        {uploadResults.length ? (
          <section className="mediacampaigns-runs-card creativetrafficking-runs-card" aria-label="Upload results">
            <div className="mediacampaigns-runs-header">
              <div className="mediacampaigns-runs-title">Upload Results</div>
              <div className="mediacampaigns-runs-subtitle">
                Per-file intake outcome. A stored file still needs inspection, review, and approval before anything else can happen.
              </div>
            </div>
            <div className="creativestudio-thread-list">
              {uploadResults.map((result, index) => (
                <div
                  key={`${result.name}-${index}`}
                  className={`creativeoptimus-metric-card creativestudio-workflow-card${result.outcome === "duplicate" ? " creativestudio-upload-result--duplicate" : ""}`}
                >
                  <div className="creativeoptimus-metric-label">
                    {result.name} · {result.outcome === "duplicate" ? "Duplicate found" : result.outcome === "failed" ? "Refused" : intakePresentation(result.intakeStatus || "stored").label}
                  </div>
                  <div className="creativeoptimus-metric-hint">{result.detail}</div>
                  {result.outcome === "duplicate" && result.assetId ? (
                    <button
                      className="creativeoptimus-secondary-btn"
                      type="button"
                      onClick={() => handleSelectAsset(result.assetId)}
                    >
                      Open the existing asset
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
            <button
              className="creativeoptimus-secondary-btn"
              type="button"
              onClick={() => setUploadResults([])}
            >
              Dismiss results
            </button>
          </section>
        ) : null}

        <section className="mediacampaigns-runs-card creativetrafficking-runs-card">
          <div className="mediacampaigns-runs-header">
            <div className="mediacampaigns-runs-title">Creative Library</div>
            <div className="mediacampaigns-runs-subtitle">
              {filteredItems.length} visible • {creativeSummary.reviewQueue} in review • {creativeSummary.approvedAssets} approved
            </div>
            {/* BATCH REVIEW. Opens the same review experience as a queue rather
                than N isolated sessions. The queue IS the visible list, so it
                inherits the advertiser, campaign and status filtering already
                applied — it can never pull in an unrelated creative. */}
            {reviewQueue.total > 1 ? (
              <button
                type="button"
                className="creativeoptimus-secondary-btn creativestudio-review-queue-cta"
                onClick={() => handleOpenReviewModal(reviewQueue.ids[0])}
              >
                {`Review ${reviewQueue.total} creatives`}
              </button>
            ) : null}
          </div>

          {(advLoadError || listError || detailError || actionError) ? (
            <div className="mediacampaigns-error">{advLoadError || listError || detailError || actionError}</div>
          ) : null}
          {actionMessage ? <div className="creativestudio-trafficking-message">{actionMessage}</div> : null}
          {listLoading ? <div className="mediacampaigns-loading">Loading studio assets...</div> : null}

          {filteredItems.length ? (
            <div className="mediacampaigns-table-wrapper creativetrafficking-table-wrapper">
              <div className="mediacampaigns-table-scroll creativetrafficking-table-scroll">
                <table className="mediacampaigns-table creativetrafficking-table creativestudio-review-table">
                  <colgroup>
                    <col className="creativestudio-col-creative" />
                    <col className="creativestudio-col-status" />
                    <col className="creativestudio-col-version" />
                    <col className="creativestudio-col-platform" />
                    <col className="creativestudio-col-format" />
                    <col className="creativestudio-col-compatibility" />
                    <col className="creativestudio-col-approval" />
                    <col className="creativestudio-col-threads" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Creative</th>
                      <th>Status</th>
                      <th>Version</th>
                      <th>Platform</th>
                      <th>Format</th>
                      <th>Compatibility</th>
                      <th>Approval</th>
                      <th>Threads</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => {
                      const isActive = String(item.id) === String(activeId || "");
                      const approvalLabel =
                        item?.approvalSummary?.fullyApproved ||
                        (item?.approvals?.clientApproved && item?.approvals?.superadminApproved)
                          ? "Fully Approved"
                          : "In Progress";
                      return (
                        <tr
                          key={item.id}
                          className={`mediacampaigns-row creativetrafficking-row ${isActive ? "is-selected" : ""}`}
                          onClick={() => handleOpenReviewModal(item.id)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleOpenReviewModal(item.id);
                            }
                          }}
                          tabIndex={0}
                        >
                          <td>
                            <div className="creativetrafficking-sheet-title">{item.title}</div>
                            <div className="creativestudio-row-subline">
                              <span className="creativetrafficking-sheet-sub">
                                {isActive && isReviewModalOpen ? "Open in review" : "Click to review"}
                              </span>
                              <button
                                type="button"
                                className="creativestudio-preview-link"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleOpenPreviewModal(item.id);
                                }}
                              >
                                Preview Window
                              </button>
                            </div>
                          </td>
                          <td data-label="Status"><span className={getStatusChipClass(getStudioEffectiveStatusLabel(item, "Draft"))}>{getStudioEffectiveStatusLabel(item, "Draft")}</span></td>
                          <td data-label="Version">{getDisplayVersionLabel(item.currentVersion, "v1")}</td>
                          <td className="creativestudio-platform-cell" data-label="Platform">
                            <CreativeStudioPlatformEligibilityCell
                              display={item.platformEligibilityDisplay}
                              compact
                            />
                          </td>
                          <td data-label="Format">{item.studioFormat || "—"}</td>
                          <td className="creativestudio-compatibility-cell" data-label="Compatibility">
                            <CreativeStudioCompatibilityCell display={item.platformEligibilityDisplay} />
                          </td>
                          <td data-label="Approval">
                            {approvalLabel === "Fully Approved" ? (
                              <span className={getStatusChipClass("Approved")}>{approvalLabel}</span>
                            ) : (
                              approvalLabel
                            )}
                          </td>
                          <td data-label="Threads">{item.commentCounts?.open || 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="mediacampaigns-empty creativetrafficking-empty-state">
              <strong>{effectiveSelectedAdvertiserId ? "No matching creatives" : "Choose an advertiser"}</strong>
              <span>{effectiveSelectedAdvertiserId ? "Adjust the filters or create a new asset to begin review." : "Select an advertiser to load the Creative Studio library."}</span>
            </div>
          )}
        </section>

        <details className="creativetrafficking-secondary-disclosure creativetrafficking-secondary-disclosure--outer" open>
          <summary className="creativetrafficking-disclosure-summary">
            <span className="creativetrafficking-disclosure-summary-label">Studio Workflow</span>
            <span className="creativetrafficking-disclosure-badge">{comments.length} comments</span>
          </summary>
          <div className="creativetrafficking-disclosure-body creativetrafficking-secondary-body">
            {isCreativeUser ? (
              <section className="creativestudio-asset-ops-shell">
                <div className="creativestudio-asset-ops-header">
                  <h2 className="creativeoptimus-section-title">Asset Operations</h2>
                  <p className="creativestudio-asset-ops-copy">Create new assets or generate fresh image variants inside the same review framework.</p>
                </div>
                <div className="creativestudio-asset-ops-columns">
                  <section className="creativestudio-asset-ops-card creativestudio-asset-ops-section">
                    <div className="creativestudio-asset-ops-card-title">Create Asset</div>
                    <div className="creativestudio-asset-ops-form creativestudio-asset-ops-form--create">
                      <label className="creativestudio-asset-ops-field">
                        <span className="creativestudio-asset-ops-label">Title</span>
                        <input className="creativeoptimus-input creativestudio-asset-ops-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={derivedUploadTitle ? derivedUploadTitle : "Creative title"} />
                        {!String(title || "").trim() && derivedUploadTitle ? (
                          <span className="creativestudio-asset-ops-helper">Using filename title: {derivedUploadTitle}</span>
                        ) : null}
                      </label>
                      <label className="creativestudio-asset-ops-field">
                        <span className="creativestudio-asset-ops-label">Change Summary</span>
                        <input className="creativeoptimus-input creativestudio-asset-ops-input" value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)} placeholder="Initial version summary" />
                      </label>
                      <label className="creativestudio-asset-ops-field">
                        <span className="creativestudio-asset-ops-label">Platform</span>
                        <input className="creativeoptimus-input creativestudio-asset-ops-input" value={channel} onChange={(e) => setChannel(e.target.value)} placeholder="Meta, TikTok, Display..." />
                      </label>
                      <label className="creativestudio-asset-ops-field">
                        <span className="creativestudio-asset-ops-label">Format</span>
                        <input className="creativeoptimus-input creativestudio-asset-ops-input" value={format} onChange={(e) => setFormat(e.target.value)} placeholder="static" />
                      </label>
                      <label className="creativestudio-asset-ops-field creativestudio-asset-ops-field--full">
                        <span className="creativestudio-asset-ops-label">Notes</span>
                        <textarea className="creativeoptimus-input creativestudio-asset-ops-input creativestudio-trafficking-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes for the creative team..." />
                      </label>
                      <div className="creativestudio-asset-ops-field creativestudio-file-field creativestudio-asset-ops-field--file creativestudio-asset-ops-field--full">
                      <span className="creativestudio-asset-ops-label">Creative File</span>
                      <button type="button" className="creativeoptimus-secondary-btn" onClick={() => fileInputRef.current?.click()}>Choose File</button>
                      <div className="creativestudio-file-field-meta creativestudio-asset-ops-filemeta">{fileName || "No file chosen"}</div>
                    </div>
                      <div className="creativestudio-asset-ops-actions creativestudio-asset-ops-field--full">
                        <span className="creativestudio-asset-ops-label">Action</span>
                        <button className="creativeoptimus-primary-btn" type="button" onClick={handleCreateAsset} disabled={!canCreateAsset}>Create Asset</button>
                      </div>
                    </div>
                  </section>
                  <section className="creativestudio-asset-ops-card creativestudio-asset-ops-section">
                    <div className="creativestudio-asset-ops-card-title">Image Generation</div>
                    <div className="creativestudio-asset-ops-form creativestudio-asset-ops-form--image">
                      <label className="creativestudio-asset-ops-field creativestudio-asset-ops-field--full"><span className="creativestudio-asset-ops-label">Generated Asset Title</span><input className="creativeoptimus-input creativestudio-asset-ops-input" value={imageForm.title} onChange={(e) => handleImageFormChange("title", e.target.value)} placeholder="Optional asset title" /></label>
                      <label className="creativestudio-asset-ops-field creativestudio-asset-ops-field--full"><span className="creativestudio-asset-ops-label">Prompt</span><textarea className="creativeoptimus-input creativestudio-asset-ops-input creativestudio-trafficking-textarea" value={imageForm.prompt} onChange={(e) => handleImageFormChange("prompt", e.target.value)} placeholder="Describe the creative you want to generate..." /></label>
                      <label className="creativestudio-asset-ops-field"><span className="creativestudio-asset-ops-label">Platform</span><input className="creativeoptimus-input creativestudio-asset-ops-input" value={imageForm.channel} onChange={(e) => handleImageFormChange("channel", e.target.value)} placeholder="Meta, TikTok, Display..." /></label>
                      <label className="creativestudio-asset-ops-field"><span className="creativestudio-asset-ops-label">Format</span><input className="creativeoptimus-input creativestudio-asset-ops-input" value={imageForm.format} onChange={(e) => handleImageFormChange("format", e.target.value)} placeholder="single_image" /></label>
                      <label className="creativestudio-asset-ops-field"><span className="creativestudio-asset-ops-label">Aspect Ratio</span><div className="creativestudio-asset-ops-select-shell"><select className="creativeoptimus-select creativestudio-asset-ops-input" value={imageForm.aspectRatio} onChange={(e) => handleImageFormChange("aspectRatio", e.target.value)}>{IMAGE_ASPECT_RATIO_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select></div></label>
                      <label className="creativestudio-asset-ops-field"><span className="creativestudio-asset-ops-label">Style Mode</span><div className="creativestudio-asset-ops-select-shell"><select className="creativeoptimus-select creativestudio-asset-ops-input" value={imageForm.styleMode} onChange={(e) => handleImageFormChange("styleMode", e.target.value)}>{IMAGE_STYLE_MODE_OPTIONS.map((option) => <option key={option.label} value={option.value}>{option.label}</option>)}</select></div></label>
                      <label className="creativestudio-asset-ops-field"><span className="creativestudio-asset-ops-label">Variations</span><div className="creativestudio-asset-ops-select-shell"><select className="creativeoptimus-select creativestudio-asset-ops-input" value={imageForm.variations} onChange={(e) => handleImageFormChange("variations", e.target.value)}>{IMAGE_VARIATION_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select></div></label>
                      <label className="creativestudio-asset-ops-field creativestudio-asset-ops-field--full"><span className="creativestudio-asset-ops-label">Notes</span><textarea className="creativeoptimus-input creativestudio-asset-ops-input creativestudio-trafficking-textarea" value={imageForm.notes} onChange={(e) => handleImageFormChange("notes", e.target.value)} placeholder="Optional internal notes" /></label>
                      <div className="creativestudio-asset-ops-actions creativestudio-asset-ops-field--full"><span className="creativestudio-asset-ops-label">Action</span><button className="creativeoptimus-primary-btn" type="button" onClick={handleGenerateImages} disabled={!effectiveSelectedAdvertiserId || !String(imageForm.prompt || "").trim() || isGeneratingImages}>{isGeneratingImages ? "Generating & reviewing images..." : "Generate Images"}</button></div>
                    </div>
                  </section>
                </div>
              </section>
            ) : null}

            <section className="creativetrafficking-secondary-panel creativestudio-workflow-panel creativestudio-workflow-panel--copy">
              <div className="creativetrafficking-panel-header">
                <h2 className="creativeoptimus-section-title">AI Copy & Exports</h2>
                <p className="creativetrafficking-panel-copy">The currently selected creative drives copy generation, export history, and all linked operational context.</p>
              </div>
              {activeAsset ? (
                <div className="creativetrafficking-secondary-columns creativestudio-workflow-columns">
                  <div className="creativetrafficking-history-column">
                    <div className="creativetrafficking-history-column-title">AI Copy</div>
                    <div className="creativestudio-compact-meta">
                      <div className="creativestudio-stage-chip">Approved {getDisplayVersionLabel(approvedVersionForCopy, "None")}</div>
                      <div className="creativestudio-stage-chip">
                        Approved Copy {approvedCopyCurrentForSelectedPlatform ? `V${approvedCopyCurrentForSelectedPlatform.versionNumber}` : "None"}
                      </div>
                    </div>
                    {isCreativeUser ? (
                      canMutateCopy ? (
                        <div className="creativestudio-aicopy-form">
                          <section className="creativestudio-aicopy-card creativestudio-aicopy-card--platform">
                            <div className="creativestudio-aicopy-card-title">Copy Platform</div>
                            <p className="creativestudio-aicopy-card-helper">Choose a campaign platform that requires platform copy.</p>
                            {copyPlatformOptions.length ? (
                              <div className="creativestudio-aicopy-chip-row" aria-label="Copy-bearing campaign platforms">
                                {copyPlatformOptions.map((option) => (
                                  <button
                                    key={`${option.source}-${option.value}`}
                                    type="button"
                                    className={`creativestudio-aicopy-platform-chip ${copyForm.platform === option.value ? "is-active" : ""}`}
                                    onClick={() => handleCopyFormChange("platform", option.value)}
                                  >
                                    {option.label}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="creativeoptimus-empty creativestudio-aicopy-notice">
                                {copyPlatformSelection.nonCopyCampaignPlatforms.includes("display")
                                  ? AI_COPY_DISPLAY_ONLY_NOTICE
                                  : AI_COPY_NO_CAMPAIGN_COPY_PLATFORMS}
                              </div>
                            )}
                            {copyPlatformSelection.source === "asset" ? (
                              <div className="creativestudio-aicopy-source-note">Asset-derived fallback: campaign platform selections were unavailable.</div>
                            ) : null}
                          </section>

                          <section className="creativestudio-aicopy-card creativestudio-aicopy-card--mode">
                            <div className="creativestudio-aicopy-card-title">Copy Mode / Brief</div>
                            <p className="creativestudio-aicopy-card-helper">Build a platform brief for AI generation or validate client-provided copy.</p>
                            <div className="creativestudio-aicopy-mode-toggle" role="tablist" aria-label="Copy mode">
                              <button type="button" className={copyMode === "ai" ? "is-active" : ""} onClick={() => setCopyMode("ai")}>AI Copy</button>
                              <button type="button" className={copyMode === "manual" ? "is-active" : ""} onClick={() => setCopyMode("manual")}>Manual Entry</button>
                            </div>

                            {copyMode === "ai" ? (
                              <div className="creativestudio-aicopy-mode-body creativestudio-aicopy-ai-grid">
                                <label className="creativestudio-aicopy-field creativestudio-aicopy-field--objective"><span className="creativetrafficking-surface-label">Objective</span><input className="creativeoptimus-input" value={copyForm.objective} onChange={(e) => handleCopyFormChange("objective", e.target.value)} placeholder="Drive leads" /></label>
                                <label className="creativestudio-aicopy-field creativestudio-aicopy-field--audience"><span className="creativetrafficking-surface-label">Target Audience</span><input className="creativeoptimus-input" value={copyForm.targetAudience} onChange={(e) => handleCopyFormChange("targetAudience", e.target.value)} placeholder="Who should this speak to?" /></label>
                                <label className="creativestudio-aicopy-field creativestudio-aicopy-field--tone"><span className="creativetrafficking-surface-label">Tone</span><input className="creativeoptimus-input" value={copyForm.tone} onChange={(e) => handleCopyFormChange("tone", e.target.value)} placeholder="Confident" /></label>
                                <label className="creativestudio-aicopy-field creativestudio-aicopy-field--cta"><span className="creativetrafficking-surface-label">CTA</span><input className="creativeoptimus-input" value={copyForm.cta} onChange={(e) => handleCopyFormChange("cta", e.target.value)} placeholder="Book a Demo" /></label>
                                <label className="creativestudio-aicopy-field creativestudio-aicopy-field--count"><span className="creativetrafficking-surface-label">Variant Count</span><input className="creativeoptimus-input" type="number" min="3" max="5" value={copyForm.variantCount} onChange={(e) => handleCopyFormChange("variantCount", e.target.value)} /></label>
                                <label className="creativestudio-aicopy-field creativestudio-aicopy-notes"><span className="creativetrafficking-surface-label">Messaging Notes</span><textarea className="creativeoptimus-input creativestudio-trafficking-textarea" value={copyForm.messagingNotes} onChange={(e) => handleCopyFormChange("messagingNotes", e.target.value)} placeholder="Important context for the generator..." /></label>
                                {!copyPlatformSelected ? (
                                  <div className="creativeoptimus-empty creativestudio-aicopy-notice">{AI_COPY_NO_COPY_PLATFORM_NOTICE}</div>
                                ) : null}
                                <div className="creativestudio-aicopy-action"><span className="creativetrafficking-surface-label">Action</span><button className="creativeoptimus-primary-btn" type="button" disabled aria-disabled="true" title={COPY_AUTHORING_DISABLED_NOTE}>Generate Copy</button><span className="creativestudio-aicopy-source-note">{COPY_AUTHORING_DISABLED_NOTE}</span></div>
                              </div>
                            ) : (
                              <div className="creativestudio-aicopy-mode-body">
                                {selectedCopySpec ? (
                                  <div className="creativestudio-aicopy-manual-grid">
                                    {selectedCopySpec.fields.map((specField) => {
                                      const check = validateCopyField(copyForm.platform, specField.key, manualCopyDraft[specField.key]);
                                      const countClass =
                                        check.status === "over_max" || check.status === "missing"
                                          ? "creativestudio-copy-charcount creativestudio-copy-charcount--error"
                                          : check.status === "over_recommended"
                                            ? "creativestudio-copy-charcount creativestudio-copy-charcount--warn"
                                            : "creativestudio-copy-charcount";
                                      const fieldClass =
                                        check.status === "over_max" || check.status === "missing"
                                          ? " has-error"
                                          : check.status === "over_recommended"
                                            ? " has-warning"
                                            : "";
                                      const isLongField = specField.key === "primaryText" || specField.key === "description";
                                      return (
                                        <label key={specField.key} className={`creativestudio-aicopy-field creativestudio-aicopy-manual-field${isLongField ? " creativestudio-aicopy-manual-field--wide" : ""}${fieldClass}`}>
                                          <span className="creativetrafficking-surface-label">{specField.label}{specField.required ? <span className="creativestudio-aicopy-required"> *</span> : null}</span>
                                          {isLongField ? (
                                            <textarea className="creativeoptimus-input creativestudio-trafficking-textarea" value={manualCopyDraft[specField.key] || ""} onChange={(e) => handleManualCopyDraftChange(specField.key, e.target.value)} />
                                          ) : (
                                            <input className="creativeoptimus-input" value={manualCopyDraft[specField.key] || ""} onChange={(e) => handleManualCopyDraftChange(specField.key, e.target.value)} />
                                          )}
                                          <span className={countClass}>
                                            {formatCopyLimitText(check)}
                                            {check.status === "missing" ? " · required" : check.status === "over_max" ? " · over limit" : ""}
                                          </span>
                                        </label>
                                      );
                                    })}
                                    {governedAuthoringEnabled ? (
                                      <label className="creativestudio-aicopy-field creativestudio-aicopy-manual-field creativestudio-aicopy-manual-field--wide">
                                        <span className="creativetrafficking-surface-label">Destination URL</span>
                                        <input className="creativeoptimus-input" type="url" value={manualCopyDraft.destinationUrl || ""} onChange={(e) => handleManualCopyDraftChange("destinationUrl", e.target.value)} placeholder="https://example.com/landing" />
                                        <span className="creativestudio-copy-charcount">http(s) only, no embedded credentials · saved to the governed draft</span>
                                      </label>
                                    ) : null}
                                    <div className="creativestudio-aicopy-action creativestudio-aicopy-manual-action">
                                      <span className="creativetrafficking-surface-label">Action</span>
                                      <button className="creativeoptimus-primary-btn" type="button" onClick={handleValidateManualCopy}>Validate Copy</button>
                                      {governedAuthoringEnabled ? (
                                        <button className="creativeoptimus-primary-btn" type="button" onClick={handleSaveGovernedDraft} disabled={isSavingGovernedDraft} aria-busy={isSavingGovernedDraft ? "true" : "false"}>
                                          {isSavingGovernedDraft ? "Saving…" : activeGovernedDraft ? `Save Draft v${activeGovernedDraft.versionNumber}` : "Save as New Draft"}
                                        </button>
                                      ) : null}
                                      <span className="creativestudio-aicopy-source-note">
                                        {governedAuthoringEnabled
                                          ? `${manualCopyValidated ? `${manualCopyReadinessLabel} · ` : ""}${
                                              activeGovernedDraft
                                                ? `Editing governed draft v${activeGovernedDraft.versionNumber}.`
                                                : latestGovernedVersion
                                                  ? GOVERNED_FROZEN_NOTE
                                                  : "Saving creates governed draft v1."
                                            } ${GOVERNED_SAVE_TRUTH_NOTE}`
                                          : manualCopyValidated ? manualCopyReadinessLabel : MANUAL_COPY_PERSISTENCE_NOTE}
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="creativeoptimus-empty creativestudio-aicopy-notice">{AI_COPY_NO_COPY_PLATFORM_NOTICE}</div>
                                )}
                              </div>
                            )}
                          </section>

                          <section className="creativestudio-aicopy-card creativestudio-aicopy-card--spec">
                            <div className="creativestudio-aicopy-card-title">Platform Copy Specs / Readiness</div>
                            <p className="creativestudio-aicopy-card-helper">Review required fields, character limits, and deployability for the selected platform.</p>
                            <div className="creativestudio-aicopy-spec">
                              <div className="creativestudio-aicopy-spec-head">
                                <span className="creativetrafficking-surface-label">{selectedCopySpec ? `${selectedCopySpec.label} Copy Fields` : "Platform Copy Specs / Readiness"}</span>
                                <span className={`creativestudio-aicopy-spec-badge creativestudio-aicopy-spec-badge--${approvedCopyReadiness ? approvedCopyReadiness.badge : copyMode === "manual" && hasManualCopyHardErrors ? "needs-edit" : selectedCopySpec?.copyModel === "platform_ad_copy" ? "deployable" : "guidance"}`}>
                                  {approvedCopyReadiness ? approvedCopyReadiness.label : copyMode === "manual" ? manualCopyReadinessLabel : selectedCopySpec?.copyModel === "platform_ad_copy" ? "Deployable platform copy" : "Creative messaging guidance"}
                                </span>
                              </div>
                              {approvedCopyReadiness ? (
                                <div className="creativestudio-aicopy-source-note">
                                  Readiness reflects the governed client-approved copy (v{approvedCopyCurrentForSelectedPlatform?.versionNumber}) for this platform, evaluated against the canonical spec — not working drafts.
                                </div>
                              ) : null}
                              {selectedCopySpec ? (
                                <ul className="creativestudio-aicopy-spec-list">
                                  {selectedCopySpec.fields.map((specField) => (
                                    <li key={specField.key} className="creativestudio-aicopy-spec-item">
                                      <span className="creativestudio-aicopy-spec-field">{specField.label}{specField.required ? " *" : ""}</span>
                                      <span className="creativestudio-aicopy-spec-limit">
                                        {specField.recommendedChars != null ? `rec ≤ ${specField.recommendedChars}` : "no limit"}
                                        {specField.maxChars != null ? ` · max ${specField.maxChars}` : ""}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <div className="creativeoptimus-empty creativestudio-aicopy-notice">{AI_COPY_NO_CAMPAIGN_COPY_PLATFORMS}</div>
                              )}
                            </div>
                          </section>
                        </div>
                      ) : (
                        <div className="creativeoptimus-empty">AI copy is available only for non-archived approved assets.</div>
                      )
                    ) : (
                      <div className="creativeoptimus-empty">Client access is read-only for AI copy.</div>
                    )}
                    <section className="creativestudio-aicopy-card creativestudio-approvedcopy-card">
                      <div className="creativestudio-approvedcopy-head">
                        <div>
                          <div className="creativestudio-aicopy-card-title">Approved Platform Copy</div>
                          <p className="creativestudio-aicopy-card-helper">Import client-approved platform copy and destination URLs. Approved copy is versioned and becomes the governed source for campaign deployment.</p>
                        </div>
                        {canImportApprovedCopy ? (
                          <button
                            className="creativeoptimus-primary-btn creativestudio-approvedcopy-import-btn"
                            type="button"
                            onClick={handleImportApprovedCopyClick}
                            disabled={isPreviewingApprovedCopy || isCommittingApprovedCopy}
                            aria-busy={isPreviewingApprovedCopy ? "true" : "false"}
                          >
                            {isPreviewingApprovedCopy ? "Reading Workbook…" : "Import Approved Copy"}
                          </button>
                        ) : null}
                      </div>
                      <input
                        ref={approvedCopyFileInputRef}
                        type="file"
                        accept=".xlsx"
                        style={{ display: "none" }}
                        onChange={handleApprovedCopyFileChange}
                        aria-label="Approved copy workbook (.xlsx)"
                      />
                      {approvedCopyNotice ? <div className="creativestudio-approvedcopy-notice">{approvedCopyNotice}</div> : null}
                      {approvedCopyImportError ? <div className="creativestudio-approvedcopy-error">{approvedCopyImportError}</div> : null}
                      {!approvedCopyCampaignId ? (
                        <div className="creativeoptimus-empty creativestudio-aicopy-notice">Approved copy is governed per campaign. An approved campaign plan is required before final copy can be imported.</div>
                      ) : approvedCopyImportPreview ? (
                        <div className="creativestudio-approvedcopy-preview" aria-label="Approved copy import preview">
                          <div className="creativestudio-approvedcopy-preview-title">Import Preview — {approvedCopyImportPreview.fileName}</div>
                          <p className="creativestudio-aicopy-card-helper">Review every mapped field before approving. Nothing is imported until you confirm.</p>
                          {approvedCopyPreviewBlocks.map(({ platformPreview, rows, validation }) => {
                            const isSelected = approvedCopySelectedPlatforms.includes(platformPreview.platform);
                            return (
                              <div key={platformPreview.platform} className="creativestudio-approvedcopy-preview-platform">
                                <div className="creativestudio-approvedcopy-preview-platform-head">
                                  <button
                                    type="button"
                                    className={`creativestudio-aicopy-platform-chip ${isSelected ? "is-active" : ""}`}
                                    onClick={() => handleToggleApprovedCopyPlatform(platformPreview.platform)}
                                    aria-pressed={isSelected ? "true" : "false"}
                                  >
                                    {platformPreview.platformLabel}
                                  </button>
                                  <span className="creativestudio-aicopy-source-note">
                                    Sheet “{platformPreview.sheetName}” · {isSelected ? "will be imported" : "excluded from this import"}
                                    {validation?.hasErrors ? ` · ${validation.errorCount} field(s) need correction` : " · passes canonical spec"}
                                  </span>
                                </div>
                                <div className="creativestudio-approvedcopy-preview-rows">
                                  {rows.map((row) => (
                                    <div key={`${platformPreview.platform}-${row.path}`} className={`creativestudio-approvedcopy-preview-row is-${row.state}`}>
                                      <div className="creativestudio-approvedcopy-preview-row-head">
                                        <span className="creativetrafficking-surface-label">{row.label}</span>
                                        <span className={`creativestudio-approvedcopy-state-chip creativestudio-approvedcopy-state-chip--${row.state}`}>
                                          {APPROVED_COPY_DIFF_STATE_LABELS[row.state] || row.state}
                                        </span>
                                        {row.edited ? <span className="creativestudio-approvedcopy-state-chip creativestudio-approvedcopy-state-chip--edited">Operator edited</span> : null}
                                      </div>
                                      {row.state === "removed" ? (
                                        <div className="creativestudio-approvedcopy-preview-current">Currently approved: “{row.currentValue}” — absent from this workbook.</div>
                                      ) : (
                                        <>
                                          {canImportApprovedCopy && isSelected ? (
                                            <input
                                              className="creativeoptimus-input"
                                              value={row.value}
                                              onChange={(e) =>
                                                handleApprovedCopyOperatorEdit(
                                                  platformPreview.platform,
                                                  row.path,
                                                  e.target.value,
                                                  row.edited ? row.workbookValue : row.value
                                                )
                                              }
                                              aria-label={`${platformPreview.platformLabel} ${row.label}`}
                                            />
                                          ) : (
                                            <div className="creativestudio-approvedcopy-preview-value">{row.value}</div>
                                          )}
                                          {row.check && (row.check.maxChars != null || row.check.status !== "ok") ? (
                                            <span className={`creativestudio-copy-charcount${row.check.status === "over_max" || row.check.status === "missing" || row.check.status === "invalid_url" ? " creativestudio-copy-charcount--error" : row.check.status === "over_recommended" ? " creativestudio-copy-charcount--warn" : ""}`}>
                                              {row.check.maxChars != null ? `${row.check.count}/${row.check.maxChars}` : null}
                                              {row.check.status === "missing" ? " · required" : row.check.status === "over_max" ? " · over limit" : row.check.status === "invalid_url" ? " · must be a valid http(s) URL" : ""}
                                            </span>
                                          ) : null}
                                          {row.edited ? (
                                            <div className="creativestudio-approvedcopy-preview-current">Workbook value: “{row.workbookValue}”</div>
                                          ) : null}
                                          {row.state === "changed" ? (
                                            <div className="creativestudio-approvedcopy-preview-current">Currently approved: “{row.currentValue}”</div>
                                          ) : null}
                                        </>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                          {(approvedCopyImportPreview.issues || []).length ? (
                            <div className="creativestudio-approvedcopy-issues">
                              <span className="creativetrafficking-surface-label">Not imported</span>
                              {(approvedCopyImportPreview.issues || []).map((issue, index) => (
                                <div key={`${issue.code}-${index}`} className="creativestudio-approvedcopy-preview-row is-unmapped">
                                  <span className="creativestudio-approvedcopy-state-chip creativestudio-approvedcopy-state-chip--unmapped">Unmapped</span>
                                  <span className="creativestudio-aicopy-source-note">{issue.message}</span>
                                </div>
                              ))}
                            </div>
                          ) : null}
                          <div className="creativestudio-approvedcopy-confirm-row">
                            <button
                              className="creativeoptimus-secondary-btn"
                              type="button"
                              onClick={handleCancelApprovedCopyImport}
                              disabled={isCommittingApprovedCopy}
                            >
                              Cancel
                            </button>
                            <button
                              className="creativeoptimus-primary-btn"
                              type="button"
                              onClick={handleConfirmApprovedCopyImport}
                              disabled={
                                !canImportApprovedCopy ||
                                isCommittingApprovedCopy ||
                                !approvedCopySelectedPlatforms.length ||
                                approvedCopyPreviewHasBlockingErrors
                              }
                              aria-busy={isCommittingApprovedCopy ? "true" : "false"}
                            >
                              {isCommittingApprovedCopy ? "Importing…" : "Approve & Import"}
                            </button>
                            {approvedCopyPreviewHasBlockingErrors ? (
                              <span className="creativestudio-aicopy-source-note">Correct the invalid fields (or exclude their platform) to enable import.</span>
                            ) : null}
                          </div>
                        </div>
                      ) : approvedCopyLoading ? (
                        <div className="creativeoptimus-empty">Loading approved copy…</div>
                      ) : approvedCopyError ? (
                        <div className="creativestudio-approvedcopy-error">{approvedCopyError}</div>
                      ) : !approvedCopyChipPlatforms.length ? (
                        <div className="creativeoptimus-empty creativestudio-aicopy-notice">
                          {APPROVED_COPY_STATE_LABELS[APPROVED_COPY_STATES.NO_APPROVED_COPY]} for this campaign yet. AI-generated concepts and manual drafts remain working copy — importing the client-approved workbook establishes the governed final copy.
                        </div>
                      ) : (
                        <div className="creativestudio-approvedcopy-current-wrap">
                          {/* Selection semantics are unchanged — this is the same
                              single-select over approvedCopyChipPlatforms, promoted
                              from text pills to the canonical platform hero chip. */}
                          <div
                            className="creativestudio-aicopy-chip-row creativestudio-approvedcopy-platform-row"
                            role="group"
                            aria-label="Approved copy platforms"
                          >
                            {approvedCopyChipPlatforms.map((platformKey) => {
                              const isSelected = approvedCopyDisplayPlatform === platformKey;
                              const platformLabel = getCopyPlatformSpec(platformKey)?.label || platformKey;
                              return (
                                <button
                                  key={platformKey}
                                  type="button"
                                  className={`creativestudio-approvedcopy-platform-chip ${isSelected ? "is-active" : ""}`}
                                  aria-pressed={isSelected}
                                  title={platformLabel}
                                  onClick={() => setApprovedCopyViewPlatform(platformKey)}
                                >
                                  <CreativeStudioPlatformHeroBadge
                                    platformKey={platformKey}
                                    label={platformLabel}
                                  />
                                </button>
                              );
                            })}
                          </div>
                          {approvedCopyDisplayEntry?.current ? (
                            <>
                              <div className="creativestudio-approvedcopy-provenance">
                                <span className="creativestudio-approvedcopy-state-chip creativestudio-approvedcopy-state-chip--approved">
                                  CLIENT-APPROVED · CURRENT V{approvedCopyDisplayEntry.current.versionNumber}
                                </span>
                                <span className="creativestudio-aicopy-source-note">
                                  Imported from {approvedCopyDisplayEntry.current.sourceFilename || "workbook"}
                                  {approvedCopyDisplayEntry.current.approvedAt ? ` · ${new Date(approvedCopyDisplayEntry.current.approvedAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}` : ""}
                                  {approvedCopyDisplayEntry.current.importedByRole ? ` · ${approvedCopyDisplayEntry.current.importedByRole}` : ""}
                                </span>
                              </div>
                              {(() => {
                                const structure = getApprovedCopyStructure(approvedCopyDisplayPlatform);
                                const payload = approvedCopyDisplayEntry.current.payload || {};
                                if (!structure) return null;
                                if (structure.shape === "creatives") {
                                  return (
                                    <div className="creativestudio-approvedcopy-creatives">
                                      {(payload.creatives || []).map((creative, index) => (
                                        <div key={creative.label || index} className="creativeoptimus-metric-card creativestudio-approvedcopy-creative">
                                          <div className="creativeoptimus-metric-label creativestudio-approvedcopy-creative-title">{(creative.label || `Creative ${index + 1}`).toUpperCase()}</div>
                                          <div className="creativestudio-approvedcopy-field"><span className="creativetrafficking-surface-label">Primary Text</span><div className="creativestudio-approvedcopy-preview-value">{creative.primaryText}</div></div>
                                          <div className="creativestudio-approvedcopy-field"><span className="creativetrafficking-surface-label">CTA</span><div className="creativestudio-approvedcopy-preview-value">{creative.cta}</div></div>
                                          <div className="creativestudio-approvedcopy-field"><span className="creativetrafficking-surface-label">Destination URL</span><a className="creativestudio-approvedcopy-url" href={creative.destinationUrl} target="_blank" rel="noreferrer">{creative.destinationUrl}</a></div>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                }
                                return (
                                  <div className="creativestudio-approvedcopy-groups">
                                    <div className="creativestudio-approvedcopy-field"><span className="creativetrafficking-surface-label">{structure.destinationField.label}</span><a className="creativestudio-approvedcopy-url" href={payload[structure.destinationField.key]} target="_blank" rel="noreferrer">{payload[structure.destinationField.key]}</a></div>
                                    {structure.groups.map((group) => (
                                      <div key={group.key} className="creativestudio-approvedcopy-group">
                                        <span className="creativetrafficking-surface-label">{group.label}</span>
                                        <ol className="creativestudio-approvedcopy-group-list">
                                          {(payload[group.key] || []).map((item, index) => (
                                            <li key={`${group.key}-${index}`} className="creativestudio-approvedcopy-preview-value">{item}</li>
                                          ))}
                                        </ol>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                              <button
                                type="button"
                                className="creativeoptimus-secondary-btn creativestudio-approvedcopy-history-toggle"
                                onClick={() => setApprovedCopyHistoryOpen((open) => !open)}
                              >
                                {approvedCopyHistoryOpen ? "Hide Version History" : "View Version History"}
                              </button>
                              {approvedCopyHistoryOpen ? (
                                <div className="creativestudio-approvedcopy-history" aria-label="Approved copy version history">
                                  {(approvedCopyDisplayEntry.history || []).map((version) => (
                                    <div key={version.id} className="creativestudio-approvedcopy-history-row">
                                      <span className={`creativestudio-approvedcopy-state-chip creativestudio-approvedcopy-state-chip--${version.status === "current" ? "approved" : "superseded"}`}>
                                        APPROVED V{version.versionNumber} — {version.status === "current" ? "CURRENT" : "SUPERSEDED"}
                                      </span>
                                      <span className="creativestudio-aicopy-source-note">
                                        {version.approvedAt ? new Date(version.approvedAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : ""}
                                        {version.importedByRole ? ` · ${version.importedByRole}` : ""}
                                        {version.sourceFilename ? ` · ${version.sourceFilename}` : ""}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                            </>
                          ) : (
                            <div className="creativestudio-approvedcopy-empty" aria-label="No approved copy for this platform">
                              <span className="creativestudio-approvedcopy-state-chip creativestudio-approvedcopy-state-chip--none">NO APPROVED COPY</span>
                              <span className="creativestudio-aicopy-source-note">
                                No client-approved copy has been established for this platform yet. Generated concepts and manual drafts are working material, never a substitute — importing the client-approved workbook establishes the governed deployment copy.
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </section>
                  </div>
                  <div className="creativetrafficking-history-column">
                    <div className="creativetrafficking-history-column-title">Exports</div>
                    <div className="creativestudio-thread-list">
                      <div className="creativeoptimus-empty">{EXPORTS_DISABLED_NOTE}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="creativeoptimus-empty">Select a creative to load AI copy and export history.</div>
              )}
            </section>

            <section className="creativetrafficking-secondary-panel creativestudio-workflow-panel creativestudio-workflow-panel--comments">
              <div className="creativetrafficking-panel-header">
                <h2 className="creativeoptimus-section-title">Comments</h2>
                <p className="creativetrafficking-panel-copy">Comments and replies stay pinned to the creative currently loaded into Stage.</p>
              </div>
              {activeAsset ? (
                <>
                  <div className="creativestudio-thread-input">
                    <input value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add team comment..." />
                    <button className="creativeoptimus-primary-btn" type="button" onClick={handleAddComment}>Add Comment</button>
                  </div>
                  <div className="creativestudio-thread-list">
                    {comments.length === 0 ? (
                      <div className="creativeoptimus-empty">No comments yet.</div>
                    ) : (
                      comments.map((comment) => (
                        <CommentItem key={comment.id} comment={comment} />
                      ))
                    )}
                  </div>
                </>
              ) : (
                <div className="creativeoptimus-empty">Select a creative to load discussion history.</div>
              )}
            </section>
          </div>
        </details>
      </div>
      {previewModalAsset ? (
        <div
          className="creativetrafficking-preview-modal-overlay"
          onClick={handleClosePreviewModal}
          role="presentation"
        >
          <div
            className="creativetrafficking-preview-modal creativestudio-preview-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`${previewModalAssetName || "Creative"} preview`}
          >
            <div className="creativetrafficking-preview-modal-header">
              <div className="creativetrafficking-preview-modal-title-block">
                <div className="creativetrafficking-preview-modal-title">
                  {previewModalAssetName || "Creative Preview"}
                </div>
                <div className="creativetrafficking-preview-modal-subtitle">
                  <strong>{getDisplayVersionLabel(previewModalVersion, "v1")}</strong>
                  {previewModalAsset?.studioMeta?.dimensionsLabel &&
                  previewModalAsset.studioMeta.dimensionsLabel !== "—"
                    ? ` · ${previewModalAsset.studioMeta.dimensionsLabel}`
                    : ""}
                  {previewModalFormat && previewModalFormat !== "—" ? ` · ${previewModalFormat}` : ""}
                  {previewModalPositionLabel ? ` · ${previewModalPositionLabel}` : ""}
                </div>
              </div>
              <button
                type="button"
                className="creativetrafficking-preview-modal-close"
                onClick={handleClosePreviewModal}
              >
                CLOSE
              </button>
            </div>

            <div className="creativetrafficking-preview-modal-body">
              <div
                className="creativetrafficking-preview-modal-media"
                onPointerDown={handlePreviewModalPointerDown}
                onPointerUp={handlePreviewModalPointerEnd}
                onPointerCancel={() => {
                  previewModalSwipeRef.current = null;
                }}
              >
                {previousPreviewModalAsset ? (
                  <button
                    type="button"
                    className="creativetrafficking-preview-modal-nav creativetrafficking-preview-modal-nav--left"
                    onClick={() => stepPreviewModal("previous")}
                    aria-label="Previous creative"
                    data-preview-swipe-block="true"
                  >
                    <span aria-hidden="true">‹</span>
                  </button>
                ) : null}
                {previewModalShowLoaderOnly ? (
                  previewModalMediaIsVideo && previewModalPosterUrl ? (
                    <>
                      <img
                        className="creativetrafficking-preview-modal-media-asset"
                        src={previewModalPosterUrl}
                        alt={previewModalAssetName || "Creative preview poster"}
                      />
                      <CreativeStudioPreviewLoader overlay />
                    </>
                  ) : (
                    <CreativeStudioPreviewLoader />
                  )
                ) : previewModalError ? (
                  <div className="creativetrafficking-preview-fallback creativetrafficking-preview-modal-fallback">
                    <span className="creativetrafficking-preview-fallback-title">Preview Error</span>
                    <span className="creativetrafficking-preview-fallback-meta">{previewModalError}</span>
                  </div>
                ) : previewModalMediaIsVideo && previewModalUrl ? (
                  <video
                    key={previewModalUrl}
                    className="creativetrafficking-preview-modal-media-asset"
                    controls
                    playsInline
                    poster={previewModalPosterUrl || undefined}
                    preload="auto"
                    onLoadStart={() => {
                      if (!renderedAdvertiserScopeIsCurrent()) return;
                      traceCreativePreview("video_load_started", {
                        scope: "modal",
                        assetId: previewModalAssetId,
                        versionId: previewModalVersion?.id || null,
                      })
                    }}
                    onLoadedMetadata={(event) => {
                      if (!renderedAdvertiserScopeIsCurrent()) return;
                      traceCreativePreview("video_metadata_loaded", {
                        scope: "modal",
                        assetId: previewModalAssetId,
                        readyState: event.currentTarget.readyState,
                      });
                      if (event.currentTarget.readyState >= 2) {
                        setPreviewModalMediaLoading(false);
                        traceCreativePreview("preview_spinner_hidden", {
                          scope: "modal",
                          reason: "metadata_ready",
                        });
                      }
                    }}
                    onLoadedData={() => {
                      if (!renderedAdvertiserScopeIsCurrent()) return;
                      setPreviewModalMediaLoading(false);
                      traceCreativePreview("video_first_frame_ready", {
                        scope: "modal",
                        assetId: previewModalAssetId,
                      });
                      traceCreativePreview("preview_spinner_hidden", {
                        scope: "modal",
                        reason: "loadeddata",
                      });
                    }}
                    onCanPlay={() => {
                      if (!renderedAdvertiserScopeIsCurrent()) return;
                      setPreviewModalMediaLoading(false);
                      traceCreativePreview("preview_spinner_hidden", {
                        scope: "modal",
                        reason: "canplay",
                      });
                    }}
                    onError={() => {
                      if (!renderedAdvertiserScopeIsCurrent()) return;
                      setPreviewModalMediaLoading(false);
                      setPreviewModalError(
                        "Video preview failed to render. The file may use an unsupported browser codec."
                      );
                      traceCreativePreview("video_preview_render_failed", {
                        scope: "modal",
                        assetId: previewModalAssetId,
                      });
                    }}
                  >
                    <source
                      src={previewModalUrl}
                      type={previewModalMediaMimeType || "video/mp4"}
                    />
                  </video>
                ) : previewModalMediaIsVideo ? (
                  <div className="creativetrafficking-preview-fallback creativetrafficking-preview-modal-fallback">
                    <span className="creativetrafficking-preview-fallback-title">
                      Video Preview Unavailable
                    </span>
                    <span className="creativetrafficking-preview-fallback-meta">
                      No playable video source was returned for this asset yet.
                    </span>
                  </div>
                ) : previewModalMediaIsDocument ? (
                  <iframe
                    className="creativestudio-preview-document creativetrafficking-preview-modal-media-asset"
                    src={previewModalUrl}
                    title={previewModalAssetName || "Creative document preview"}
                    onLoad={() => {
                      if (!renderedAdvertiserScopeIsCurrent()) return;
                      setPreviewModalMediaLoading(false);
                    }}
                  />
                ) : previewModalUrl ? (
                  <img
                    className="creativetrafficking-preview-modal-media-asset"
                    src={previewModalUrl}
                    alt={previewModalAssetName || "Creative preview"}
                    onLoad={() => {
                      if (!renderedAdvertiserScopeIsCurrent()) return;
                      setPreviewModalMediaLoading(false);
                    }}
                    onError={() => {
                      if (!renderedAdvertiserScopeIsCurrent()) return;
                      setPreviewModalMediaLoading(false);
                      setPreviewModalError("Image preview failed to render.");
                    }}
                  />
                ) : (
                  <div className="creativetrafficking-preview-fallback creativetrafficking-preview-modal-fallback">
                    <span className="creativetrafficking-preview-fallback-title">No Preview</span>
                    <span className="creativetrafficking-preview-fallback-meta">
                      A preview is not available for this asset yet.
                    </span>
                  </div>
                )}
                {previewModalShowLoadingOverlay ? <CreativeStudioPreviewLoader overlay /> : null}
                {nextPreviewModalAsset ? (
                  <button
                    type="button"
                    className="creativetrafficking-preview-modal-nav creativetrafficking-preview-modal-nav--right"
                    onClick={() => stepPreviewModal("next")}
                    aria-label="Next creative"
                    data-preview-swipe-block="true"
                  >
                    <span aria-hidden="true">›</span>
                  </button>
                ) : null}
              </div>

              <div className="creativetrafficking-preview-modal-meta">
                <span className={getStatusChipClass(previewModalStatus)}>
                  {formatStudioStatus(previewModalStatus)}
                </span>
                <div className="creativestudio-stage-chip">
                  File {getDisplayFileName(previewModalVersion, "No file")}
                </div>
                <div className="creativestudio-stage-chip">
                  Version {getDisplayVersionLabel(previewModalVersion, "v1")}
                </div>
                <CreativeStudioPlatformEligibilityCell
                  display={previewModalAsset?.platformEligibilityDisplay}
                  compact
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {isReviewModalOpen ? (
        <div
          // `modal-overlay` is the class the global swipe-navigation guard
          // already treats as an overlay, so gesture navigation stays inert
          // over the scrim as well as over the sheet itself.
          className="creativestudio-review-modal-backdrop modal-overlay"
          onClick={handleCloseReviewModal}
        >
          <div
            className="creativestudio-review-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="creativestudio-review-sheet-title"
            ref={reviewSheetRef}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="creativestudio-review-modal-header">
              <div
                className="creativestudio-review-eyebrow"
                id="creativestudio-review-sheet-title"
              >
                Creative Review
                {/* Queue position. A reviewer walking a batch needs to know how
                    much is left; nine unlabelled modal sessions gave them no
                    sense of progress at all. */}
                {reviewPosition.label ? (
                  <span className="creativestudio-review-queue-position">
                    {reviewPosition.label}
                    {reviewPosition.remaining > 0 ? ` · ${reviewPosition.remaining} remaining` : ""}
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                className="creativeoptimus-secondary-btn creativestudio-review-modal-close"
                onClick={handleCloseReviewModal}
                aria-label="Close creative review"
                ref={reviewCloseButtonRef}
              >
                ×
              </button>
            </div>

            <div className="creativestudio-review-modal-grid">
              <div className="creativestudio-review-modal-preview">
                <div className="creativestudio-review-card">
                  {/* The creative is the first thing in the sheet. The panel
                      that used to sit here explained what the reviewer can
                      already see, and on a phone it cost the creative the top
                      of the screen. */}
                  <div className="creativestudio-review-modal-stage">
                    {/* VIDEO REVIEW. Rendered from the generated poster, never
                        from the source master. Both non-ready states are
                        terminal and both say the original file is preserved,
                        so the reviewer is never left watching a spinner that
                        cannot resolve — and approval, which reads the metadata
                        beside this, is never gated on any of it. */}
                    {mediaIsVideo ? (
                      reviewMediaState === REVIEW_MEDIA_STATE.UNAVAILABLE ? (
                        <div className="creativestudio-empty creativestudio-review-media-notice">
                          <strong>{reviewMediaNotice?.headline}</strong>
                          <span>{reviewMediaNotice?.detail}</span>
                        </div>
                      ) : (
                        <>
                          <img
                            key={reviewMedia?.src || activeId}
                            className="creativestudio-preview-media creativestudio-review-modal-media"
                            src={reviewMedia?.src || previewPosterUrl}
                            alt={selectedAssetName || "Creative review poster"}
                            onLoad={() => setReviewPosterDecoding(false)}
                            onError={() => {
                              setReviewPosterDecoding(false);
                              setReviewPosterFailed(true);
                            }}
                          />
                          {reviewMediaState === REVIEW_MEDIA_STATE.LOADING ? (
                            <CreativeStudioPreviewLoader overlay />
                          ) : null}
                        </>
                      )
                    ) : previewShowLoaderOnly ? (
                      <CreativeStudioPreviewLoader />
                    ) : previewError ? (
                      <div className="creativestudio-empty">{previewError}</div>
                    ) : mediaIsVideo && previewUrl ? (
                      <video
                        key={previewUrl}
                        className="creativestudio-preview-media creativestudio-review-modal-media"
                        controls
                        playsInline
                        poster={previewPosterUrl || undefined}
                        preload="auto"
                        onLoadStart={() => {
                          if (!renderedAdvertiserScopeIsCurrent()) return;
                          traceCreativePreview("video_load_started", {
                            scope: "stage",
                            assetId: activeId,
                            versionId: selectedVersion?.id || null,
                          })
                        }}
                        onLoadedMetadata={(event) => {
                          if (!renderedAdvertiserScopeIsCurrent()) return;
                          traceCreativePreview("video_metadata_loaded", {
                            scope: "stage",
                            assetId: activeId,
                            readyState: event.currentTarget.readyState,
                          });
                          if (event.currentTarget.readyState >= 2) {
                            setPreviewMediaLoading(false);
                            traceCreativePreview("preview_spinner_hidden", {
                              scope: "stage",
                              reason: "metadata_ready",
                            });
                          }
                        }}
                        onLoadedData={() => {
                          if (!renderedAdvertiserScopeIsCurrent()) return;
                          setPreviewMediaLoading(false);
                          traceCreativePreview("video_first_frame_ready", {
                            scope: "stage",
                            assetId: activeId,
                          });
                          traceCreativePreview("preview_spinner_hidden", {
                            scope: "stage",
                            reason: "loadeddata",
                          });
                        }}
                        onCanPlay={() => {
                          if (!renderedAdvertiserScopeIsCurrent()) return;
                          setPreviewMediaLoading(false);
                          traceCreativePreview("preview_spinner_hidden", {
                            scope: "stage",
                            reason: "canplay",
                          });
                        }}
                        onError={() => {
                          if (!renderedAdvertiserScopeIsCurrent()) return;
                          setPreviewMediaLoading(false);
                          setPreviewError(
                            "Video preview failed to render. The file may use an unsupported browser codec."
                          );
                          traceCreativePreview("video_preview_render_failed", {
                            scope: "stage",
                            assetId: activeId,
                          });
                        }}
                      >
                        <source src={previewUrl} type={selectedMediaMimeType || "video/mp4"} />
                      </video>
                    ) : mediaIsVideo ? (
                      <div className="creativestudio-empty">
                        No playable video source was returned for this asset yet.
                      </div>
                    ) : mediaIsDocument ? (
                      <iframe
                        className="creativestudio-preview-document"
                        src={previewUrl}
                        title={selectedAssetName || "Creative document preview"}
                        onLoad={() => {
                          if (!renderedAdvertiserScopeIsCurrent()) return;
                          setPreviewMediaLoading(false);
                        }}
                      />
                    ) : previewUrl ? (
                      <img
                        className="creativestudio-preview-media creativestudio-review-modal-media"
                        src={previewUrl}
                        alt={selectedAssetName}
                        // A cached image can already be decoded by the time this
                        // handler attaches, so the `load` event never arrives.
                        // Resolve from the element instead of waiting forever.
                        ref={(node) => {
                          if (!node) return;
                          if (!renderedAdvertiserScopeIsCurrent()) return;
                          if (isImageAlreadyLoaded(node)) setPreviewMediaLoading(false);
                        }}
                        onLoad={() => {
                          if (!renderedAdvertiserScopeIsCurrent()) return;
                          setPreviewMediaLoading(false);
                        }}
                        onError={() => {
                          if (!renderedAdvertiserScopeIsCurrent()) return;
                          setPreviewMediaLoading(false);
                          setPreviewError("Image preview failed to render.");
                        }}
                      />
                    ) : (
                      <div className="creativestudio-empty">No preview available.</div>
                    )}
                    {previewShowLoadingOverlay ? <CreativeStudioPreviewLoader overlay /> : null}
                  </div>
                  {/* One control row under the creative: move through the
                      queue, and switch the version on stage. The queue arrows
                      used to be pinned inside the stage, which meant a band of
                      empty stage had to be reserved for them — on a 300x100
                      banner that band was larger than the creative. Same
                      handlers, same disabled rules, same accessible names. */}
                  <div className="creativestudio-review-version-row">
                    <div className="creativestudio-review-queue-nav">
                      <button
                        type="button"
                        className="creativeoptimus-secondary-btn creativestudio-review-stage-nav creativestudio-review-stage-nav-prev"
                        onClick={() => handleMoveReviewSelection(-1)}
                        disabled={!canGoPreviousAsset}
                        aria-label="Previous creative"
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        className="creativeoptimus-secondary-btn creativestudio-review-stage-nav creativestudio-review-stage-nav-next"
                        onClick={() => handleMoveReviewSelection(1)}
                        disabled={!canGoNextAsset}
                        aria-label="Next creative"
                      >
                        ›
                      </button>
                    </div>
                    <span className="creativestudio-review-version-label">Version</span>
                    <div className="creativestudio-preview-actions creativestudio-stage-actions">
                      {versions.map((version) => (
                        <button
                          key={version.id}
                          type="button"
                          className="creativeoptimus-secondary-btn"
                          onClick={() => setSelectedVersionId(version.id)}
                          disabled={String(version.id) === String(selectedVersionId)}
                        >
                          {getDisplayVersionLabel(version, `v${version.version_number}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="creativestudio-review-modal-side">
                <div className="creativestudio-review-card">
                  {/* SUMMARY. The five facts a reviewer decides from, as
                      badges — status, version, platform, format, dimensions —
                      and nothing that has no value to report. */}
                  <div className="creativetrafficking-detail-section-title">Review Summary</div>
                  <div className="creativestudio-review-chip-row">
                    <span
                      className={`${getStatusChipClass(selectedAssetStatus)} creativestudio-review-chip--status`}
                    >
                      {formatStudioStatus(selectedAssetStatus)}
                    </span>
                    <div className="creativestudio-stage-chip creativestudio-review-chip--version">
                      Version {selectedAssetVersionLabel}
                    </div>
                    {hasReviewStatValue(selectedAssetFormat) ? (
                      <div className="creativestudio-stage-chip creativestudio-review-chip--format">
                        {selectedAssetFormat}
                      </div>
                    ) : null}
                    {hasReviewStatValue(selectedAssetDimensions) ? (
                      <div className="creativestudio-stage-chip creativestudio-review-chip--dimensions">
                        {selectedAssetDimensions}
                      </div>
                    ) : null}
                  </div>
                  {activeAsset?.platformEligibilityDisplay ? (
                    <div className="creativestudio-review-platform-row">
                      <span className="creativestudio-review-field-label">Platform</span>
                      <CreativeStudioPlatformEligibilityCell
                        display={activeAsset?.platformEligibilityDisplay}
                        compact
                      />
                    </div>
                  ) : null}
                  {/* IDENTITY. The filename sits below the badges, never over
                      the creative: it is the one value here with no length
                      ceiling, and on a phone it is what pushes a row
                      off-screen. Two lines, then an ellipsis; the stored name
                      is unaltered and stays available in full. */}
                  <div className="creativestudio-review-identity">
                    <span className="creativestudio-review-chip-file-label">File</span>
                    <span
                      className="creativestudio-review-chip-file-value"
                      title={getDisplayFileName(selectedVersion, "No file")}
                    >
                      {getDisplayFileName(selectedVersion, "No file")}
                    </span>
                  </div>
                </div>

                {/* METADATA. One surface, one definition grid, divided by
                    rules rather than by another glowing box per field. A row is
                    rendered only where there is something to report: an empty
                    row costs a phone screen real height and tells the reviewer
                    nothing. Zero, however, is a value and stays. */}
                <div className="creativestudio-review-card creativestudio-review-meta-card">
                  <div className="creativetrafficking-detail-section-title">Asset Details</div>
                  <dl className="creativestudio-review-stats">
                    {hasReviewStatValue(activeAsset?.lifecycle_status) ? (
                      <div><dt>Lifecycle</dt><dd>{formatStudioStatus(activeAsset?.lifecycle_status)}</dd></div>
                    ) : null}
                    {hasReviewStatValue(openCommentCount) ? (
                      <div><dt>Open Threads</dt><dd>{openCommentCount}</dd></div>
                    ) : null}
                    {hasReviewStatValue(selectedAssetDimensions) ? (
                      <div><dt>Dimensions</dt><dd>{selectedAssetDimensions}</dd></div>
                    ) : null}
                    {hasReviewStatValue(selectedAssetRatio) ? (
                      <div><dt>Aspect Ratio</dt><dd>{selectedAssetRatio}</dd></div>
                    ) : null}
                    {hasReviewStatValue(selectedAssetFormat) ? (
                      <div><dt>Format</dt><dd>{selectedAssetFormat}</dd></div>
                    ) : null}
                    {approvedVersionForCopy ? (
                      <div><dt>Approved Version</dt><dd>{getDisplayVersionLabel(approvedVersionForCopy, "None")}</dd></div>
                    ) : null}
                    {hasReviewStatValue(reviewCreatedLabel) ? (
                      <div><dt>Created</dt><dd>{reviewCreatedLabel}</dd></div>
                    ) : null}
                    {hasReviewStatValue(reviewUpdatedLabel) ? (
                      <div><dt>Updated</dt><dd>{reviewUpdatedLabel}</dd></div>
                    ) : null}
                  </dl>
                </div>

                <CreativePlatformEligibilityDetail display={activeAsset?.platformEligibilityDisplay} />

                {(() => {
                  const intake = activeAsset?.intake || null;
                  const presentation = intakePresentation(intake?.status);
                  const media = intake?.media || null;
                  const thumbnail = intake?.derivatives?.thumbnail || null;
                  const poster = intake?.derivatives?.poster || null;
                  const canRetry = ["processing_failed", "needs_metadata"].includes(String(intake?.status || ""));
                  return (
                    <div className="creativetrafficking-surface-card creativestudio-intake-card">
                      <div className="creativetrafficking-history-column-title">Server-Verified File Details</div>
                      <div className="creativestudio-compact-meta">
                        <div className="creativestudio-stage-chip">{presentation.label}</div>
                        {intake?.detectedMimeType ? (
                          <div className="creativestudio-stage-chip">Detected {intake.detectedMimeType}</div>
                        ) : null}
                      </div>
                      <div className="creativeoptimus-metric-hint">{presentation.note}</div>
                      {intake?.mimeMismatch ? (
                        <div className="creativeoptimus-metric-hint">
                          The file's content is {intake.mimeMismatch.detectedMimeType}, not the {intake.mimeMismatch.claimedMimeType} the browser reported. The server-detected type is used.
                        </div>
                      ) : null}
                      {media ? (
                        <dl className="creativestudio-review-facts">
                          {media.width && media.height ? (
                            <div><dt>Measured Size</dt><dd>{media.width}×{media.height}{media.aspectRatio || media.displayAspectRatio ? ` (${media.aspectRatio || media.displayAspectRatio})` : ""}</dd></div>
                          ) : null}
                          {media.durationSeconds != null ? (
                            <div><dt>Duration</dt><dd>{media.durationSeconds}s</dd></div>
                          ) : null}
                          {media.videoCodec ? <div><dt>Video</dt><dd>{media.videoCodec}{media.frameRate ? ` · ${media.frameRate} fps` : ""}</dd></div> : null}
                          {media.audioCodec ? <div><dt>Audio</dt><dd>{media.audioCodec}{media.audioChannels ? ` · ${media.audioChannels}ch` : ""}{media.audioSampleRate ? ` · ${media.audioSampleRate}Hz` : ""}</dd></div> : null}
                          {media.hasAudioTrack === false ? <div><dt>Audio</dt><dd>No audio track</dd></div> : null}
                        </dl>
                      ) : intake && ["stored", "processing"].includes(intake.status) ? (
                        <div className="creativeoptimus-metric-hint">Server measurements will appear after inspection completes.</div>
                      ) : null}
                      {thumbnail || poster ? (
                        <div className="creativeoptimus-metric-hint">
                          Preview: {(thumbnail || poster).status === "generated"
                            ? `${thumbnail ? "thumbnail" : "poster frame"} generated`
                            : "generation failed — the original file is unaffected"}
                        </div>
                      ) : intake?.status === "processed" ? (
                        <div className="creativeoptimus-metric-hint">No thumbnail or poster derivative is generated for this file type. The full preview above is unaffected.</div>
                      ) : null}
                      {(intakeEvidence?.duplicates || []).length ? (
                        <div className="creativeoptimus-metric-hint">
                          Exact duplicate of{" "}
                          {intakeEvidence.duplicates.map((dup, index) => (
                            <button
                              key={dup.assetId}
                              className="creativestudio-link-btn creativeoptimus-secondary-btn"
                              type="button"
                              onClick={() => handleSelectAsset(dup.assetId)}
                            >
                              {index > 0 ? ", " : ""}{dup.title || `asset ${dup.assetId}`}
                            </button>
                          ))}
                        </div>
                      ) : null}
                      {(intakeEvidence?.similar || []).length ? (
                        <div className="creativeoptimus-metric-hint">
                          Possibly similar (advisory only — nothing happens automatically):{" "}
                          {intakeEvidence.similar
                            .map((sim) => sim.title || `asset ${sim.assetId}`)
                            .join(", ")}
                        </div>
                      ) : null}
                      {canRetry ? (
                        <button
                          className="creativeoptimus-secondary-btn"
                          type="button"
                          onClick={() => handleIntakeRetry(activeAsset.id)}
                        >
                          Retry inspection
                        </button>
                      ) : null}
                    </div>
                  );
                })()}

                <div className="creativestudio-review-card">
                  <div className="creativetrafficking-detail-section-title">Approval State</div>
                  <div className="creativestudio-preview-actions creativestudio-stage-actions creativestudio-review-actions">
                    <ApprovalChip label="CLIENT" state={approvalSummary.client} />
                    <ApprovalChip label="SUPERADMIN" state={approvalSummary.superadmin} />
                    <ApprovalChip label="FINAL" state={approvalSummary.fullyApproved ? "APPROVED" : "PENDING"} />
                  </div>
                  <div className="creativestudio-preview-actions creativestudio-stage-actions creativestudio-review-actions creativestudio-review-modal-action-row">
                    {/* Approval is enabled by the VERSION record alone. It must
                        never wait on a preview: the decision is made from
                        inspected metadata, which is present whether or not a
                        poster has painted. */}
                    <button
                      className="creativestudio-inline-status-pill creativestudio-review-modal-action status-approved"
                      type="button"
                      onClick={() => handleApprovalActionAndAdvance("approve")}
                      disabled={!selectedVersionId}
                    >
                      {reviewActionLabel("Approve", reviewAdvance)}
                    </button>
                    <button
                      className="creativestudio-inline-status-pill creativestudio-review-modal-action status-revisions"
                      type="button"
                      onClick={() => handleApprovalActionAndAdvance("reject")}
                      disabled={!selectedVersionId}
                    >
                      {reviewActionLabel("Request Revisions", reviewAdvance)}
                    </button>
                  </div>
                  <div className="creativestudio-comment-composer creativestudio-review-modal-composer">
                    <textarea
                      className="creativeoptimus-input creativestudio-comment-input"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add review feedback, requested edits, or placement-specific notes..."
                    />
                    <button
                      className="creativeoptimus-primary-btn"
                      type="button"
                      onClick={handleAddComment}
                      disabled={!selectedVersionId || !newComment.trim()}
                    >
                      Add Comment
                    </button>
                  </div>
                  <div className="creativestudio-review-footer-note">{reviewThreadLabel}</div>
                </div>

                <div className="creativestudio-review-card creativestudio-review-modal-comments">
                  <div className="creativetrafficking-detail-section-title">Comments</div>
                  <div className="creativestudio-thread-list">
                    {comments.length === 0 ? (
                      <div className="creativeoptimus-empty">No comments yet.</div>
                    ) : (
                      comments.map((comment) => (
                        <CommentItem key={comment.id} comment={comment} />
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CreativeStudio;
